import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import webSocketService from '../services/WebSocketService';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Alert,
  Tabs,
  Tab,
  Divider,
  Grid,
  Avatar,
  Tooltip,
  FormControlLabel,
  Checkbox,
  Switch
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Work as WorkIcon,
  Home as HomeIcon,
  Event as EventIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  ContentCopy as TemplateIcon,
  ContentCopy as ContentCopyIcon,
  PlayArrow as ApplyIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  Lock as LockIcon,
  LockOpen as UnlockIcon,
  Highlight as HighlightIcon,
  PushPin as PushPinIcon,
  PushPinOutlined as PushPinOutlinedIcon,
  PersonRemove as PersonRemoveIcon,
  PersonAdd as PersonAddIcon,
  ChevronLeft,
  ChevronRight,
  Flight as FlightIcon,
  Print as PrintIcon,
  MilitaryTech as MilitaryTechIcon,
  School as SchoolIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { lt } from 'date-fns/locale';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl, API_ENDPOINTS } from '../config/api';
import { useTranslation } from 'react-i18next';
import MonthLockManager from './MonthLockManager';
import MobileSchedule from './MobileSchedule';
import ScheduleMonthPrint from './schedule/ScheduleMonthPrint';
import './schedule/schedulePrint.css';
import {
  addDaysLocal,
  eachLocalDay,
  formatDateLocal,
  monthGridRange,
  startOfWeekMonday,
} from '../utils/scheduleWeek';

// Add CSS animation for optimistic updates
const pulseAnimation = `
  @keyframes pulse {
    0% { opacity: 1; }
    50% { opacity: 0.5; }
    100% { opacity: 1; }
  }
`;

// Rainbow text easter egg (injected globally; only applied when isJustina)
const rainbowAnimation = `
  @keyframes rainbow {
    0% { color: #e91e63; }
    16% { color: #ff9800; }
    33% { color: #ffeb3b; }
    50% { color: #4caf50; }
    66% { color: #2196f3; }
    83% { color: #9c27b0; }
    100% { color: #e91e63; }
  }
`;

// Inject the CSS
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = pulseAnimation + rainbowAnimation;
  document.head.appendChild(style);
}

interface WorkScheduleEntry {
  id: string;
  userId: string;
  date: string;
  status: 'working' | 'day_off' | 'mothers_day' | 'remote' | 'sick_leave' | 'rest_day' | 'state_holiday' | 'business_trip' | 'military_service' | 'praktika';
  startTime?: string;
  endTime?: string;
  breakStartTime?: string; // Break start time
  breakEndTime?: string;   // Break end time
  notes?: string;
  isApproved: boolean;
  user?: {
    firstName: string;
    lastName: string;
    email: string;
  };
}

interface WeeklyTemplate {
  dayOfWeek: number; // 0 = Sunday, 1 = Monday, etc.
  status: 'working' | 'day_off' | 'mothers_day' | 'remote' | 'sick_leave' | 'rest_day' | 'state_holiday' | 'business_trip' | 'military_service' | 'praktika';
  startTime?: string;
  endTime?: string;
  breakStartTime?: string; // Break start time
  breakEndTime?: string;   // Break end time
  notes?: string;
}

interface DepartmentWeekDay {
  dayOfWeek: number;
  status: WeeklyTemplate['status'];
  startTime?: string | null;
  endTime?: string | null;
  breakStartTime?: string | null;
  breakEndTime?: string | null;
  notes?: string;
}

interface DepartmentScheduleTemplate {
  department: string;
  enabled: boolean;
  week: DepartmentWeekDay[];
}

function buildDefaultDepartmentWeek(): DepartmentWeekDay[] {
  const working: Omit<DepartmentWeekDay, 'dayOfWeek'> = {
    status: 'working',
    startTime: '08:00',
    endTime: '17:00',
    breakStartTime: '12:00',
    breakEndTime: '12:30'
  };
  return [
    { dayOfWeek: 1, ...working },
    { dayOfWeek: 2, ...working },
    { dayOfWeek: 3, ...working },
    { dayOfWeek: 4, ...working },
    { dayOfWeek: 5, ...working },
    { dayOfWeek: 6, status: 'day_off' },
    { dayOfWeek: 0, status: 'day_off' }
  ];
}

interface CompanyEmployee {
  userId: string;
  name: string;
  department: string;
  position?: string;
  schedules: WorkScheduleEntry[];
  etatas?: number; // Optional etatas
  workAccountingType?: 'normal' | 'summary'; // Work accounting type
  isPinned?: boolean; // Whether user is pinned to top
  excludeFromWorkersCount?: boolean; // Whether user should be excluded from workers count
}

function getStateHolidaysInMonth(year: number, month: number, allEmployees: CompanyEmployee[]): string[] {
  const stateHolidayDates = new Set<string>();

  allEmployees.forEach(employee => {
    employee.schedules.forEach((schedule: WorkScheduleEntry) => {
      if (schedule.status === 'state_holiday') {
        const scheduleDate = new Date(schedule.date + 'T00:00:00');
        if (scheduleDate.getFullYear() === year && scheduleDate.getMonth() === month) {
          stateHolidayDates.add(schedule.date);
        }
      }
    });
  });

  return Array.from(stateHolidayDates);
}

// Single-day view model for the current user's mobile schedule
interface MobileScheduleDay {
  date: Date;
  dateString: string;
  dateLabel: string;
  weekdayLabel: string;
  status: WorkScheduleEntry['status'] | 'none';
  startTime?: string;
  endTime?: string;
  breakStartTime?: string;
  breakEndTime?: string;
  totalHours?: number;
  notes?: string;
  praktikaStartTime?: string;
  praktikaEndTime?: string;
  praktikaTotalHours?: number;
  excursions?: Array<{ title: string; startTime?: string; endTime?: string }>;
  edukacijos?: Array<{ title: string; startTime?: string; endTime?: string }>;
  planetarium?: Array<{ title: string; startTime?: string; endTime?: string }>;
}

interface ScheduleChangeRequest {
  id: string;
  userId: string;
  date: string;
  status: 'working' | 'day_off' | 'mothers_day' | 'remote' | 'sick_leave' | 'rest_day' | 'state_holiday' | 'business_trip' | 'military_service' | 'praktika';
  startTime?: string;
  endTime?: string;
  breakStartTime?: string; // Break start time
  breakEndTime?: string;   // Break end time
  notes?: string;
  reason?: string;
  isApproved: boolean;
  approvedBy?: string;
  approvedAt?: string;
  adminNotes?: string;
  /** Last approved baseline while a same-status pending edit waits */
  previousScheduleBeforeRequest?: {
    status?: ScheduleChangeRequest['status'];
    startTime?: string | null;
    endTime?: string | null;
    breakStartTime?: string | null;
    breakEndTime?: string | null;
    notes?: string | null;
  } | null;
}

/** Normalize schedule date to YYYY-MM-DD for comparisons. */
function normalizeScheduleDate(d: unknown): string | undefined {
  if (d == null) return undefined;
  if (typeof d === 'string') return d.length >= 10 ? d.slice(0, 10) : d;
  try {
    return new Date(d as string | number).toISOString().slice(0, 10);
  } catch {
    return undefined;
  }
}

const PAIRABLE_PRIMARY_STATUSES: WorkScheduleEntry['status'][] = ['working', 'remote'];

function scheduleDateMatches(
  scheduleDate: string | undefined,
  cellDate: string,
  year?: number,
  month?: number,
  day?: number
): boolean {
  if (!scheduleDate) return false;
  const normalized = normalizeScheduleDate(scheduleDate);
  if (normalized === cellDate || scheduleDate === cellDate) return true;
  if (year == null || month == null || day == null) return false;
  const altFormats = [
    `${year}-${(month + 1).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`,
    `${year}/${(month + 1).toString().padStart(2, '0')}/${day.toString().padStart(2, '0')}`,
    `${day.toString().padStart(2, '0')}-${(month + 1).toString().padStart(2, '0')}-${year}`,
    `${day.toString().padStart(2, '0')}/${(month + 1).toString().padStart(2, '0')}/${year}`
  ];
  return altFormats.includes(scheduleDate);
}

function getSchedulesForDate(
  schedules: WorkScheduleEntry[] | undefined,
  cellDate: string,
  year?: number,
  month?: number,
  day?: number
): WorkScheduleEntry[] {
  if (!schedules?.length) return [];
  const matched = schedules.filter((s) => scheduleDateMatches(s.date, cellDate, year, month, day));
  return matched.sort((a, b) => {
    const aStart = a.startTime || '';
    const bStart = b.startTime || '';
    if (aStart !== bStart) return aStart.localeCompare(bStart);
    if (a.status === 'praktika') return 1;
    if (b.status === 'praktika') return -1;
    return a.status.localeCompare(b.status);
  });
}

function getPrimaryScheduleForDate(
  schedules: WorkScheduleEntry[] | undefined,
  cellDate: string,
  year?: number,
  month?: number,
  day?: number
): WorkScheduleEntry | undefined {
  const daySchedules = getSchedulesForDate(schedules, cellDate, year, month, day);
  // Never treat Praktika as primary — a praktika-only day uses the secondary segment only
  // (avoids the same row becoming both primary and praktika → Pr+Pr / duplicate times).
  return (
    daySchedules.find((s) => PAIRABLE_PRIMARY_STATUSES.includes(s.status)) ||
    daySchedules.find((s) => s.status !== 'praktika')
  );
}

function getPraktikaScheduleForDate(
  schedules: WorkScheduleEntry[] | undefined,
  cellDate: string,
  year?: number,
  month?: number,
  day?: number
): WorkScheduleEntry | undefined {
  return getSchedulesForDate(schedules, cellDate, year, month, day).find((s) => s.status === 'praktika');
}

/**
 * Upsert/delete one day segment without wiping a same-day counterpart
 * (e.g. keep Dirba when Praktika arrives via WebSocket).
 */
function mergeScheduleSegmentIntoList(
  schedules: WorkScheduleEntry[],
  incoming: WorkScheduleEntry,
  operation: 'upsert' | 'delete' = 'upsert'
): WorkScheduleEntry[] {
  const date = normalizeScheduleDate(incoming.date) || incoming.date;
  const status = incoming.status;
  const sameDay = (s: WorkScheduleEntry) =>
    (normalizeScheduleDate(s.date) || s.date) === date;

  if (operation === 'delete') {
    return schedules.filter((s) => {
      if (!sameDay(s)) return true;
      if (incoming.id && s.id === incoming.id) return false;
      return s.status !== status;
    });
  }

  let next = schedules.filter((s) => {
    if (!sameDay(s)) return true;
    if (incoming.id && s.id === incoming.id) return false;
    if (s.status === status) return false;
    if (status === 'praktika') {
      return PAIRABLE_PRIMARY_STATUSES.includes(s.status);
    }
    if (PAIRABLE_PRIMARY_STATUSES.includes(status)) {
      return s.status === 'praktika';
    }
    // Exclusive status replaces the whole day
    return false;
  });

  next.push({ ...incoming, date });
  return next;
}

/**
 * department-view returns only isApproved:true rows. Pending stays in pendingApprovals (LAUKIA).
 * While a request waits, approved hours remain authoritative: keep existing approved rows,
 * and rehydrate from previousScheduleBeforeRequest when same-status pending overwrote the DB row.
 * Do not inject proposal times into schedules (hour totals / grid stay on approved).
 */
function mergePendingIntoCompanyView(
  employees: CompanyEmployee[],
  pendings: ScheduleChangeRequest[]
): CompanyEmployee[] {
  if (!pendings.length) return employees;
  return employees.map((emp) => {
    const empPendings = pendings.filter((p) => p.userId === emp.userId);
    if (empPendings.length === 0) return emp;
    let schedules = [...(emp.schedules || [])];
    for (const pending of empPendings) {
      const date = normalizeScheduleDate(pending.date);
      if (!date) continue;
      const prev = pending.previousScheduleBeforeRequest;
      if (!prev || typeof prev !== 'object') {
        // Net-new pending (no approved baseline): leave schedules unchanged; LAUKIA comes from pendingApprovals
        continue;
      }
      const approvedStatus = (prev.status || pending.status) as WorkScheduleEntry['status'];
      const hasApprovedForStatus = schedules.some(
        (s) =>
          normalizeScheduleDate(s.date) === date &&
          s.status === approvedStatus &&
          s.isApproved !== false
      );
      if (hasApprovedForStatus) continue;
      // Same-status pending replaced the approved row in DB — restore approved display from snapshot
      schedules = schedules.filter(
        (s) => !(normalizeScheduleDate(s.date) === date && s.status === approvedStatus)
      );
      schedules.push({
        id: `approved-baseline-${pending.id}`,
        userId: pending.userId,
        date,
        status: approvedStatus,
        startTime: prev.startTime ?? undefined,
        endTime: prev.endTime ?? undefined,
        breakStartTime: prev.breakStartTime ?? undefined,
        breakEndTime: prev.breakEndTime ?? undefined,
        notes: prev.notes ?? undefined,
        isApproved: true
      } as WorkScheduleEntry);
    }
    return { ...emp, schedules };
  });
}

interface WorkScheduleAuditLogEntry {
  id: string;
  actionType: string;
  targetUserId?: string | null;
  targetDate?: string | null;
  targetMonth: string;
  actorUserId: string;
  department?: string | null;
  beforeState?: any;
  afterState?: any;
  meta?: any;
  createdAt: string;
  actor?: {
    id: string;
    firstName: string;
    lastName: string;
    email?: string;
    department?: string;
  } | null;
  targetUser?: {
    id: string;
    firstName: string;
    lastName: string;
    email?: string;
    department?: string;
  } | null;
}

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  department: string;
  position?: string;
  role: string;
  isActive?: boolean;
}

/** True if break interval is fully inside work interval (same reference day, HH:MM). */
function isBreakInsideWorkHours(
  workStartHHMM: string,
  workEndHHMM: string,
  breakStartHHMM: string,
  breakEndHHMM: string
): boolean {
  const ws = new Date(`2000-01-01T${workStartHHMM}`).getTime();
  const we = new Date(`2000-01-01T${workEndHHMM}`).getTime();
  const bs = new Date(`2000-01-01T${breakStartHHMM}`).getTime();
  const be = new Date(`2000-01-01T${breakEndHHMM}`).getTime();
  if (be <= bs) return false;
  if (we <= ws) return false;
  return bs >= ws && be <= we;
}

/** Komandiruotė cell fill (department grid + legend chip). */
const BUSINESS_TRIP_GRADIENT =
  'linear-gradient(120deg, rgba(72, 181, 163, 0.92) 0%, rgba(100, 149, 237, 0.88) 50%, rgba(130, 210, 170, 0.88) 100%)';
/** Solid fallback for Excel / getStatusColor. */
const BUSINESS_TRIP_SOLID_FALLBACK = 'rgb(88, 165, 175)';

/** Karo tarnyba — camouflage olive. */
const MILITARY_SERVICE_GRADIENT =
  'linear-gradient(135deg, #4a5d23 0%, #6b7c32 35%, #556b2f 55%, #3d4a1f 100%)';
const MILITARY_SERVICE_SOLID_FALLBACK = 'rgba(85, 107, 47, 0.9)';

/** Praktika — violet / lilac. */
const PRAKTIKA_GRADIENT =
  'linear-gradient(135deg, rgba(138, 98, 210, 0.95) 0%, rgba(167, 130, 230, 0.9) 45%, rgba(186, 148, 240, 0.88) 100%)';
const PRAKTIKA_SOLID_FALLBACK = 'rgba(147, 112, 219, 0.88)';

function isHoursOnlyStatus(status: string): boolean {
  return (
    status === 'day_off' ||
    status === 'sick_leave' ||
    status === 'mothers_day' ||
    status === 'business_trip' ||
    status === 'military_service'
  );
}

/** Soft max for auto-filled leave/sick/family/business-trip hours (manual entry may exceed). */
const HOURS_ONLY_SOFT_CAP = 8;

/**
 * Net hours from start/end (minus break overlap when provided), soft-capped at 8 for autofill.
 * Returns '' when result is not positive.
 */
function formatHoursOnlyAutofill(
  startHHMM: string,
  endHHMM: string,
  breakStartHHMM?: string | null,
  breakEndHHMM?: string | null
): string {
  const start = new Date(`2000-01-01T${startHHMM}`).getTime();
  const end = new Date(`2000-01-01T${endHHMM}`).getTime();
  let hours = (end - start) / (1000 * 60 * 60);
  if (
    breakStartHHMM &&
    breakEndHHMM &&
    String(breakStartHHMM).trim() !== '' &&
    String(breakEndHHMM).trim() !== ''
  ) {
    const breakStart = new Date(`2000-01-01T${breakStartHHMM}`).getTime();
    const breakEnd = new Date(`2000-01-01T${breakEndHHMM}`).getTime();
    if (breakEnd > breakStart) {
      const overlapStart = Math.max(start, breakStart);
      const overlapEnd = Math.min(end, breakEnd);
      hours -= Math.max(0, overlapEnd - overlapStart) / (1000 * 60 * 60);
    }
  }
  if (hours <= 0) return '';
  return Math.min(hours, HOURS_ONLY_SOFT_CAP).toFixed(1);
}

function dateToHHMM(value: Date | null | undefined): string | null {
  if (!value) return null;
  return value.toTimeString().slice(0, 5);
}

const WorkSchedule: React.FC<{ initialView?: 'department' | 'locks' | 'mobile' }> = ({ initialView }) => {
  const {
    user,
    token,
    hasPermission,
    scheduleViewDepartments: authScheduleViewDepartments,
    canViewAllScheduleDepartments: authCanViewAllScheduleDepartments
  } = useAuth();
  const isJustina = Boolean(user && [user.firstName, user.lastName].filter(Boolean).join(' ').trim() === 'Justina Skrupskytė');
  const DEPARTMENT_FILTER_KEY = 'workSchedule_selectedDepartments';
  const SHOW_DETAILS_COLUMNS_KEY = 'workSchedule_showDetailsColumns';
  
  // Helper function to check if user can approve schedules
  const canApproveSchedules = () => {
    return hasPermission('schedule.approve') || 
           hasPermission('schedule.manage') ||
           user?.canApproveSchedules === true;
  };
  const isSuperAdmin = hasPermission('*.*');
  /** Boss-level: see every department. Partial allowlists use template schedule_view_departments. */
  const canViewAllDepartments =
    authCanViewAllScheduleDepartments ||
    hasPermission('schedule.view_all_departments') ||
    hasPermission('*.*');
  const canSeeMonthLocksTab = canApproveSchedules();
  const canSeeAuditLogsTab = isSuperAdmin;
  const monthLocksTabIndex = canSeeMonthLocksTab ? 1 : null;
  const auditLogsTabIndex = canSeeAuditLogsTab ? (canSeeMonthLocksTab ? 2 : 1) : null;

  // Permission-only: can edit other users' cells (used for clickability and opening edit dialog)
  const canEditOtherUsersCells = () =>
    hasPermission('schedule.approve') || hasPermission('schedule.manage');

  /** Remote-work address lives in `notes`; only the row owner or schedule admins/approvers may see others' addresses — not plain schedule viewers (`schedule.view`). */
  const canViewRemoteWorkAddressForEmployee = (employeeUserId: string | undefined): boolean => {
    if (employeeUserId != null && user?.id != null && String(employeeUserId) === String(user.id)) {
      return true;
    }
    return (
      hasPermission('*.*') ||
      hasPermission('schedule.manage') ||
      hasPermission('schedule.approve') ||
      user?.canApproveSchedules === true
    );
  };
  
  // Helper function to create default time (00:00)
  const createDefaultTime = () => new Date('2000-01-01T00:00:00');

  // Parse hours input (accepts both comma and period as decimal separator; 8.5 and 8,5 are equivalent)
  const parseHoursInput = (str: string): number | null => {
    if (!str || str.trim() === '') return null;
    const normalized = str.replace(/,/g, '.');
    const num = parseFloat(normalized);
    if (isNaN(num) || num < 0 || num > 24) return null;
    return num;
  };

  const [viewableDepartments, setViewableDepartments] = useState<string[] | null>(
    authScheduleViewDepartments
  );
  /** Multi-select chips when view-all or more than one viewable department */
  const canToggleDepartments =
    canViewAllDepartments ||
    (Array.isArray(viewableDepartments) && viewableDepartments.length > 1);

  const [schedules, setSchedules] = useState<WorkScheduleEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [scheduleToDelete, setScheduleToDelete] = useState<string | null>(null);
  const [editingSchedule, setEditingSchedule] = useState<WorkScheduleEntry | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [selectedStatus, setSelectedStatus] = useState<WorkScheduleEntry['status']>('working');
  const [startTime, setStartTime] = useState<Date | null>(createDefaultTime());
  const [endTime, setEndTime] = useState<Date | null>(createDefaultTime());
  const [addDialogHoursStr, setAddDialogHoursStr] = useState<string>('');
  const [breakStartTime, setBreakStartTime] = useState<Date | null>(createDefaultTime());
  const [breakEndTime, setBreakEndTime] = useState<Date | null>(createDefaultTime());
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [companyView, setCompanyView] = useState<CompanyEmployee[]>([]);
  /** When user is missing from department-view merge (edge cases), load their month via GET /work-schedule for Mobilus grafikas */
  const [mobileScheduleFallbackEmployee, setMobileScheduleFallbackEmployee] = useState<CompanyEmployee | null>(null);
  const [departmentViewLoading, setDepartmentViewLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState<Date>(new Date());
  const [mobileFocusDate, setMobileFocusDate] = useState<Date>(() => startOfWeekMonday(new Date()));

  /** Monday–Sunday padded range covering selectedMonth (for fetch + Mobilus grafikas). */
  const getMonthFetchRange = () => {
    const { rangeStart, rangeEnd } = monthGridRange(selectedMonth);
    return {
      startDate: formatDateLocal(rangeStart),
      endDate: formatDateLocal(rangeEnd),
    };
  };

  const [pinnedUsers, setPinnedUsers] = useState<Set<string>>(new Set());
  const [excludedFromWorkersCountUsers, setExcludedFromWorkersCountUsers] = useState<Set<string>>(new Set());
  /** userId -> weekly remote day max when different from default 2 */
  const [remoteWeeklyLimits, setRemoteWeeklyLimits] = useState<Record<string, number>>({});
  const [remoteLimitDialogOpen, setRemoteLimitDialogOpen] = useState(false);
  const [remoteLimitDialogUser, setRemoteLimitDialogUser] = useState<{ userId: string; name: string } | null>(null);
  const [remoteLimitDialogValue, setRemoteLimitDialogValue] = useState<number>(2);
  const [remoteLimitSaving, setRemoteLimitSaving] = useState(false);

  // Cell highlighting states
  const [cellHighlights, setCellHighlights] = useState<Map<string, boolean>>(new Map());
  const [cellBlueMarks, setCellBlueMarks] = useState<Map<string, boolean>>(new Map());
  const [excursionsByUserDate, setExcursionsByUserDate] = useState<Map<string, any[]>>(new Map());
  const [edukacijosByUserDate, setEdukacijosByUserDate] = useState<Map<string, any[]>>(new Map());
  const [planetariumByUserDate, setPlanetariumByUserDate] = useState<Map<string, any[]>>(new Map());

  // Weekly template states
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [weeklyTemplate, setWeeklyTemplate] = useState<WeeklyTemplate[]>([
    { dayOfWeek: 1, status: 'working', startTime: '09:00', endTime: '17:00', breakStartTime: '12:00', breakEndTime: '13:00' }, // Monday
    { dayOfWeek: 2, status: 'working', startTime: '09:00', endTime: '17:00', breakStartTime: '12:00', breakEndTime: '13:00' }, // Tuesday
    { dayOfWeek: 3, status: 'working', startTime: '09:00', endTime: '17:00', breakStartTime: '12:00', breakEndTime: '13:00' }, // Wednesday
    { dayOfWeek: 4, status: 'working', startTime: '09:00', endTime: '17:00', breakStartTime: '12:00', breakEndTime: '13:00' }, // Thursday
    { dayOfWeek: 5, status: 'working', startTime: '09:00', endTime: '17:00', breakStartTime: '12:00', breakEndTime: '13:00' }, // Friday
    { dayOfWeek: 6, status: 'rest_day' }, // Saturday
    { dayOfWeek: 0, status: 'rest_day' }, // Sunday
  ]);
  const [templateStartDate, setTemplateStartDate] = useState<Date | null>(new Date());
  const [templateEndDate, setTemplateEndDate] = useState<Date | null>(null);
  const [templateApplyMode, setTemplateApplyMode] = useState<'single' | 'multiple'>('single');
  const [templateWeeksCount, setTemplateWeeksCount] = useState<number>(1);
  const [templateSelectedUser, setTemplateSelectedUser] = useState<string>('');
  const [templateReason, setTemplateReason] = useState<string>('');
  const [templateDialogNotice, setTemplateDialogNotice] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [weeklyTemplateHoursStr, setWeeklyTemplateHoursStr] = useState<Record<number, string>>({});

  // Department schedule templates / autofill
  const [deptTemplates, setDeptTemplates] = useState<DepartmentScheduleTemplate[]>([]);
  const [deptAutofillDialogOpen, setDeptAutofillDialogOpen] = useState(false);
  const [deptTemplatesDialogOpen, setDeptTemplatesDialogOpen] = useState(false);
  const [deptAutofillSelected, setDeptAutofillSelected] = useState<string[]>([]);
  const [deptAutofillLoading, setDeptAutofillLoading] = useState(false);
  const [deptTemplatesSaving, setDeptTemplatesSaving] = useState(false);
  const [deptTemplatesLoading, setDeptTemplatesLoading] = useState(false);
  const [newDeptTemplateName, setNewDeptTemplateName] = useState('');
  const [editingDeptTemplateIndex, setEditingDeptTemplateIndex] = useState(0);
  const [remoteAddressTemplate, setRemoteAddressTemplate] = useState<string>('');
  const [remoteTemplateLoading, setRemoteTemplateLoading] = useState<boolean>(false);
  const [remoteTemplateSaving, setRemoteTemplateSaving] = useState<boolean>(false);

  // Cell editing states
  const [cellEditDialogOpen, setCellEditDialogOpen] = useState(false);
  const [cellEditMonthLocked, setCellEditMonthLocked] = useState(false);
  const [editingCellData, setEditingCellData] = useState<{
    userId: string;
    userName: string;
    date: string;
    currentStatus: string;
    currentStartTime?: string;
    currentEndTime?: string;
    currentBreakStartTime?: string;
    currentBreakEndTime?: string;
    currentNotes?: string;
  } | null>(null);
  const [cellEditStatus, setCellEditStatus] = useState<WorkScheduleEntry['status']>('working');
  const [cellEditPraktikaEnabled, setCellEditPraktikaEnabled] = useState(false);
  const [cellEditPraktikaId, setCellEditPraktikaId] = useState<string | null>(null);
  const [cellEditPraktikaStartTime, setCellEditPraktikaStartTime] = useState<Date | null>(null);
  const [cellEditPraktikaEndTime, setCellEditPraktikaEndTime] = useState<Date | null>(null);
  const [cellEditPraktikaBreakStartTime, setCellEditPraktikaBreakStartTime] = useState<Date | null>(null);
  const [cellEditPraktikaBreakEndTime, setCellEditPraktikaBreakEndTime] = useState<Date | null>(null);
  const [cellEditStartTime, setCellEditStartTime] = useState<Date | null>(createDefaultTime());
  const [cellEditEndTime, setCellEditEndTime] = useState<Date | null>(createDefaultTime());
  const [cellEditHoursStr, setCellEditHoursStr] = useState<string>('');
  const [cellEditBreakStartTime, setCellEditBreakStartTime] = useState<Date | null>(createDefaultTime());
  const [cellEditBreakEndTime, setCellEditBreakEndTime] = useState<Date | null>(createDefaultTime());
  const [cellEditNotes, setCellEditNotes] = useState('');
  const [cellEditDialogNotice, setCellEditDialogNotice] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Helper to clear break times for vacation-like statuses
  const clearBreakTimesForStatus = (status: WorkScheduleEntry['status']) => {
    if (isHoursOnlyStatus(status)) {
      setBreakStartTime(null);
      setBreakEndTime(null);
    }
  };

  const clearCellEditBreakTimesForStatus = (status: WorkScheduleEntry['status']) => {
    if (isHoursOnlyStatus(status)) {
      setCellEditBreakStartTime(null);
      setCellEditBreakEndTime(null);
    }
  };


  // Department filtering for admins
  const [availableDepartments, setAvailableDepartments] = useState<string[]>([]);
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem(DEPARTMENT_FILTER_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.error('Failed to load department filter:', error);
      return [];
    }
  });

  // Bulk approval for user requests
  const [selectedUserForBulkApproval, setSelectedUserForBulkApproval] = useState<{
    userId: string;
    userName: string;
    pendingRequests: any[];
  } | null>(null);
  const [userBulkApprovalDialogOpen, setUserBulkApprovalDialogOpen] = useState(false);
  const [selectedRequestsForBulk, setSelectedRequestsForBulk] = useState<Set<string>>(new Set());

  // Schedule change request states
  const [changeRequests, setChangeRequests] = useState<ScheduleChangeRequest[]>([]);
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [requestedStatus, setRequestedStatus] = useState<
    Exclude<WorkScheduleEntry['status'], 'state_holiday'>
  >('working');
  const [requestedStartTime, setRequestedStartTime] = useState<Date | null>(createDefaultTime());
  const [requestedEndTime, setRequestedEndTime] = useState<Date | null>(createDefaultTime());
  const [requestedHoursStr, setRequestedHoursStr] = useState<string>('');
  const [requestedBreakStartTime, setRequestedBreakStartTime] = useState<Date | null>(null);
  const [requestedBreakEndTime, setRequestedBreakEndTime] = useState<Date | null>(null);
  const [requestedNotes, setRequestedNotes] = useState('');
  const [requestedReason, setRequestedReason] = useState('');
  const [requestDialogNotice, setRequestDialogNotice] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [requestingForDate, setRequestingForDate] = useState<string>('');
  const [requestingForUser, setRequestingForUser] = useState<string>('');
  const [isCurrentMonthLocked, setIsCurrentMonthLocked] = useState(false);
  const [stateHolidayDate, setStateHolidayDate] = useState<Date | null>(new Date());

  // Approval system states
  const [pendingApprovals, setPendingApprovals] = useState<ScheduleChangeRequest[]>([]);
  const pendingApprovalsRef = useRef<ScheduleChangeRequest[]>([]);
  useEffect(() => {
    pendingApprovalsRef.current = pendingApprovals;
  }, [pendingApprovals]);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [selectedApproval, setSelectedApproval] = useState<ScheduleChangeRequest | null>(null);
  const [approvalReason, setApprovalReason] = useState('');

  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [auditLogs, setAuditLogs] = useState<WorkScheduleAuditLogEntry[]>([]);
  const [auditLogsLoading, setAuditLogsLoading] = useState(false);
  const [auditLogPage, setAuditLogPage] = useState(1);
  const [auditLogLimit, setAuditLogLimit] = useState(50);
  const [auditLogTotal, setAuditLogTotal] = useState(0);
  const [auditMonthFilter, setAuditMonthFilter] = useState<Date>(new Date());
  const [auditDepartmentFilter, setAuditDepartmentFilter] = useState<string>('');
  const [auditActorFilter, setAuditActorFilter] = useState<string>('');
  const [auditEmployeeFilter, setAuditEmployeeFilter] = useState<string>('');
  const [auditActionFilter, setAuditActionFilter] = useState<string>('');
  const [auditDetailsOpen, setAuditDetailsOpen] = useState(false);
  const [selectedAuditLog, setSelectedAuditLog] = useState<WorkScheduleAuditLogEntry | null>(null);

  const { t } = useTranslation();

  const getBreakOutOfShiftMessage = (
    status: string,
    workStart: Date | null | undefined,
    workEnd: Date | null | undefined,
    breakStart: Date | null | undefined,
    breakEnd: Date | null | undefined
  ): string | null => {
    if (status !== 'working' && status !== 'remote' && status !== 'praktika') return null;
    if (!workStart || !workEnd || !breakStart || !breakEnd) return null;
    const ws = workStart.toTimeString().slice(0, 5);
    const we = workEnd.toTimeString().slice(0, 5);
    const bs = breakStart.toTimeString().slice(0, 5);
    const be = breakEnd.toTimeString().slice(0, 5);
    if (!isBreakInsideWorkHours(ws, we, bs, be)) {
      return t(
        'schedule.breakOutsideWorkHours',
        'Pertrauka turi būti visiškai tarp darbo pradžios ir pabaigos laikų.'
      );
    }
    return null;
  };

  // Add optimistic update state
  const [optimisticUpdates, setOptimisticUpdates] = useState<Set<string>>(new Set());

  // Helper functions for optimistic updates (keyed by date + status for dual segments)
  const updateCompanyViewOptimistically = (
    userId: string, 
    date: string, 
    updatedSchedule: Partial<WorkScheduleEntry>,
    operation: 'create' | 'update' | 'delete' = 'update'
  ) => {
    const statusKey = updatedSchedule.status || 'working';
    const cellKey = `${userId}-${date}`;
    const normalizedDate = normalizeScheduleDate(date) || date;
    
    // Mark as optimistic update
    setOptimisticUpdates(prev => new Set(prev).add(cellKey));

    // Pending proposals must not replace approved hours in the grid (LAUKIA via pendingApprovals)
    if (
      operation !== 'delete' &&
      updatedSchedule.isApproved === false
    ) {
      return;
    }
    
    setCompanyView(prev => prev.map(employee => {
      if (employee.userId !== userId) return employee;

      if (operation === 'delete' && !updatedSchedule.status) {
        return {
          ...employee,
          schedules: employee.schedules.filter(
            (s) => (normalizeScheduleDate(s.date) || s.date) !== normalizedDate
          )
        };
      }

      const segment = {
        id: updatedSchedule.id || `temp-${Date.now()}-${statusKey}`,
        userId,
        isApproved: false,
        ...updatedSchedule,
        date: normalizedDate,
        status: statusKey as WorkScheduleEntry['status']
      } as WorkScheduleEntry;

      return {
        ...employee,
        schedules: mergeScheduleSegmentIntoList(
          employee.schedules,
          segment,
          operation === 'delete' ? 'delete' : 'upsert'
        )
      };
    }));
  };

  const rollbackCompanyViewUpdate = (
    userId: string, 
    date: string, 
    originalSchedule?: WorkScheduleEntry
  ) => {
    const cellKey = `${userId}-${date}`;
    
    // Remove from optimistic updates
    setOptimisticUpdates(prev => {
      const newSet = new Set(prev);
      newSet.delete(cellKey);
      return newSet;
    });
    
    setCompanyView(prev => prev.map(employee => {
      if (employee.userId === userId) {
        const updatedSchedules = [...employee.schedules];
        
        if (originalSchedule) {
          // Restore original schedule
          const index = updatedSchedules.findIndex(s => s.date === date);
          if (index >= 0) {
            updatedSchedules[index] = originalSchedule;
          } else {
            updatedSchedules.push(originalSchedule);
          }
        } else {
          // Remove temporary schedule
          const index = updatedSchedules.findIndex(s => s.date === date);
          if (index >= 0) {
            updatedSchedules.splice(index, 1);
          }
        }
        
        return {
          ...employee,
          schedules: updatedSchedules
        };
      }
      return employee;
    }));
  };

  const confirmOptimisticUpdate = (userId: string, date: string) => {
    const cellKey = `${userId}-${date}`;
    setOptimisticUpdates(prev => {
      const newSet = new Set(prev);
      newSet.delete(cellKey);
      return newSet;
    });
  };

  // Required monthly hours from backend API
  const [requiredHours, setRequiredHours] = useState<string>('');
  const [monthlyRequirement, setMonthlyRequirement] = useState<{
    id: string;
    requiredHours: number;
    description?: string;
    setter: { firstName: string; lastName: string };
    setAt: string;
  } | null>(null);
  const [monthlyRequirementLoading, setMonthlyRequirementLoading] = useState(false);
  const [showDetailsColumns, setShowDetailsColumns] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(SHOW_DETAILS_COLUMNS_KEY);
      if (stored !== null) return stored === 'true';
    } catch {
      // ignore
    }
    return true;
  });

  // Save showDetailsColumns to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(SHOW_DETAILS_COLUMNS_KEY, String(showDetailsColumns));
    } catch (error) {
      console.error('Failed to save showDetailsColumns:', error);
    }
  }, [showDetailsColumns]);

  // Sticky employee column is always present; details columns are toggleable.
  const fixedDetailsColumnCount = showDetailsColumns ? 5 : 0;

  // Save department selection to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(DEPARTMENT_FILTER_KEY, JSON.stringify(selectedDepartments));
    } catch (error) {
      console.error('Failed to save department filter:', error);
    }
  }, [selectedDepartments]);

  useEffect(() => {
    if (canViewAllDepartments) {
      setViewableDepartments(null);
    } else if (authScheduleViewDepartments) {
      setViewableDepartments(authScheduleViewDepartments);
    }
  }, [canViewAllDepartments, authScheduleViewDepartments]);

  useEffect(() => {
    fetchRemoteAddressTemplate();
  }, [token]);

  // Fetch monthly requirement when month changes
  useEffect(() => {
    const fetchMonthlyRequirement = async () => {
      if (!user) return;
      
      setMonthlyRequirementLoading(true);
      try {
        const year = selectedMonth.getFullYear();
        const month = selectedMonth.getMonth() + 1;
        
        const response = await fetch(`${getApiUrl(API_ENDPOINTS.WORK_SCHEDULE)}/monthly-requirement/${year}/${month}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            setMonthlyRequirement(data.data);
            setRequiredHours(data.data.requiredHours.toString());
          } else {
            setMonthlyRequirement(null);
            setRequiredHours('');
          }
        } else {
          setMonthlyRequirement(null);
          setRequiredHours('');
        }
      } catch (error) {
        console.error('Error fetching monthly requirement:', error);
        setMonthlyRequirement(null);
        setRequiredHours('');
      } finally {
        setMonthlyRequirementLoading(false);
      }
    };

    fetchMonthlyRequirement();
  }, [selectedMonth, user]);

  const handlePreviousMonth = () => {
    // Always day 1 — setMonth on day 31 overflows (Jan 31 → March)
    setSelectedMonth(new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setSelectedMonth(new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 1));
  };

  /** Snap to Monday of current focus week, then ±7 days (always land on a Monday). */
  const shiftMobileFocusWeek = (weekDelta: number) => {
    setMobileFocusDate((prev) => {
      const monday = startOfWeekMonday(prev);
      const nextMonday = addDaysLocal(monday, weekDelta * 7);
      setSelectedMonth(new Date(nextMonday.getFullYear(), nextMonday.getMonth(), 1));
      return nextMonday;
    });
  };

  const handleMobilePrevWeek = () => {
    shiftMobileFocusWeek(-1);
  };

  const handleMobileNextWeek = () => {
    shiftMobileFocusWeek(1);
  };

  const handleMobileToday = () => {
    const todayMonday = startOfWeekMonday(new Date());
    setMobileFocusDate(todayMonday);
    setSelectedMonth(new Date(todayMonday.getFullYear(), todayMonday.getMonth(), 1));
  };

  const handleRequiredHoursChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setRequiredHours(value);
    
    // If user is admin, save to backend
    if (hasPermission('schedule.manage')) {
      try {
        const year = selectedMonth.getFullYear();
        const month = selectedMonth.getMonth() + 1;
        
        const response = await fetch(`${getApiUrl(API_ENDPOINTS.WORK_SCHEDULE)}/monthly-requirement`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            year,
            month,
            requiredHours: parseFloat(value) || 0,
            description: monthlyRequirement?.description || `Monthly requirement for ${year}-${month.toString().padStart(2, '0')}`
          })
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            // Refresh the monthly requirement data
            const refreshResponse = await fetch(`${getApiUrl(API_ENDPOINTS.WORK_SCHEDULE)}/monthly-requirement/${year}/${month}`, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json'
              }
            });
            
            if (refreshResponse.ok) {
              const refreshData = await refreshResponse.json();
              if (refreshData.success && refreshData.data) {
                setMonthlyRequirement(refreshData.data);
              }
            }
          }
        }
      } catch (error) {
        console.error('Error saving monthly requirement:', error);
      }
    }
  };

  // Debug user information
  useEffect(() => {
    console.log('=== User Debug Info ===');
    console.log('User object:', user);
    // Role removed - using permissions instead
    console.log('User ID:', user?.id);
  }, [user]);

  // Sync addDialogHoursStr when dialog opens (status changes set hours in the Select onChange)
  useEffect(() => {
    if (!dialogOpen) return;
    if (isHoursOnlyStatus(selectedStatus) && startTime && endTime) {
      setAddDialogHoursStr(
        formatHoursOnlyAutofill(
          startTime.toTimeString().slice(0, 5),
          endTime.toTimeString().slice(0, 5),
          dateToHHMM(breakStartTime),
          dateToHHMM(breakEndTime)
        )
      );
    }
  }, [dialogOpen]);

  // Sync cellEditHoursStr when cell edit dialog opens (status changes set hours in the Select onChange)
  useEffect(() => {
    if (!cellEditDialogOpen) return;
    if (isHoursOnlyStatus(cellEditStatus) && cellEditStartTime && cellEditEndTime) {
      setCellEditHoursStr(
        formatHoursOnlyAutofill(
          cellEditStartTime.toTimeString().slice(0, 5),
          cellEditEndTime.toTimeString().slice(0, 5),
          dateToHHMM(cellEditBreakStartTime),
          dateToHHMM(cellEditBreakEndTime)
        )
      );
    }
  }, [cellEditDialogOpen]);

  // Sync requestedHoursStr when request dialog opens (status changes set hours in the Select onChange)
  useEffect(() => {
    if (!requestDialogOpen) return;
    if (isHoursOnlyStatus(requestedStatus) && requestedStartTime && requestedEndTime) {
      setRequestedHoursStr(
        formatHoursOnlyAutofill(
          requestedStartTime.toTimeString().slice(0, 5),
          requestedEndTime.toTimeString().slice(0, 5),
          dateToHHMM(requestedBreakStartTime),
          dateToHHMM(requestedBreakEndTime)
        )
      );
    }
  }, [requestDialogOpen]);

  // Sync weeklyTemplateHoursStr when template dialog opens
  useEffect(() => {
    if (templateDialogOpen) {
      const next: Record<number, string> = {};
      weeklyTemplate.forEach(day => {
        if (isHoursOnlyStatus(day.status)) {
          if (day.startTime && day.endTime) {
            next[day.dayOfWeek] = formatHoursOnlyAutofill(
              day.startTime,
              day.endTime,
              day.breakStartTime,
              day.breakEndTime
            );
          } else {
            next[day.dayOfWeek] = '';
          }
        }
      });
      setWeeklyTemplateHoursStr(prev => {
        const merged = { ...prev };
        Object.keys(next).forEach(k => { merged[Number(k)] = next[Number(k)]; });
        return merged;
      });
    }
  }, [templateDialogOpen]);

  const statusOptions = [
    { value: 'working', label: t('schedule.working'), color: 'success', icon: <WorkIcon /> },
    { value: 'day_off', label: t('schedule.dayOff'), color: 'warning', icon: <EventIcon /> },
    { value: 'mothers_day', label: t('schedule.mothersDay'), color: 'info', icon: <PersonIcon /> },
    { value: 'remote', label: t('schedule.remote'), color: 'primary', icon: <HomeIcon /> },
    {
      value: 'business_trip',
      label: t('schedule.businessTrip'),
      color: 'info',
      icon: <FlightIcon />
    },
    {
      value: 'military_service',
      label: t('schedule.militaryService'),
      color: 'success',
      icon: <MilitaryTechIcon />
    },
    {
      value: 'praktika',
      label: t('schedule.praktika'),
      color: 'secondary',
      icon: <SchoolIcon />
    },
    { value: 'sick_leave', label: t('schedule.sickLeave'), color: 'error', icon: <PersonIcon /> },
    { value: 'rest_day', label: t('schedule.restDay'), color: 'secondary', icon: <EventIcon /> },
    { value: 'state_holiday', label: t('schedule.stateHoliday'), color: 'error', icon: <EventIcon /> }
  ];

  // Filtered status options for change requests (exclude state_holiday)
  const changeRequestStatusOptions = statusOptions.filter(option => option.value !== 'state_holiday');
  // Filtered status options for weekly template (exclude state_holiday for users)
  const weeklyTemplateStatusOptions = statusOptions.filter(option => option.value !== 'state_holiday');
  // Filtered status options for cell edit (exclude state_holiday; manage via dedicated approval)
  const cellEditStatusOptions = statusOptions.filter(option => option.value !== 'state_holiday');

  const dayNames = [
    t('schedule.sunday'),
    t('schedule.monday'),
    t('schedule.tuesday'),
    t('schedule.wednesday'),
    t('schedule.thursday'),
    t('schedule.friday'),
    t('schedule.saturday')
  ];

  const fetchSchedules = async () => {
    setLoading(true);
    try {
      console.log('Fetching schedules from:', `${getApiUrl(API_ENDPOINTS.WORK_SCHEDULE)}`);
      const response = await fetch(`${getApiUrl(API_ENDPOINTS.WORK_SCHEDULE)}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      console.log('Response status:', response.status);
      if (response.ok) {
        const data = await response.json();
        console.log('Schedules data:', data);
        // Filter for only unapproved schedules (active requests)
        const unapprovedSchedules = (data.data || []).filter((schedule: WorkScheduleEntry) => !schedule.isApproved);
        setSchedules(unapprovedSchedules);
      } else {
        console.error('Failed to fetch schedules, status:', response.status);
        setError('Failed to fetch schedules');
      }
    } catch (error) {
      console.error('Error fetching schedules:', error);
      setError('Failed to fetch schedules');
    } finally {
      setLoading(false);
    }
  };


  const fetchCompanyView = async () => {
    try {
      setDepartmentViewLoading(true);

      // Include adjacent-month days in Mon–Sun weeks (Mobilus grafikas + padded fetch)
      const { startDate, endDate } = getMonthFetchRange();
      const viewMonth = `${selectedMonth.getFullYear()}-${String(selectedMonth.getMonth() + 1).padStart(2, '0')}`;
      
      const response = await fetch(`${getApiUrl(API_ENDPOINTS.WORK_SCHEDULE_DEPARTMENT_VIEW)}?startDate=${startDate}&endDate=${endDate}&viewMonth=${encodeURIComponent(viewMonth)}&_t=${Date.now()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Company view data fetched:', data.data);
        if (data.canViewAllDepartments) {
          setViewableDepartments(null);
        } else if (Array.isArray(data.scheduleViewDepartments)) {
          setViewableDepartments(data.scheduleViewDepartments);
        }
        
        // Debug: Log each employee's schedules to see the actual data
        data.data.forEach((employee: CompanyEmployee) => {
          console.log(`Employee ${employee.name} schedules:`, employee.schedules);
        });

        setCompanyView(mergePendingIntoCompanyView(data.data || [], pendingApprovalsRef.current));
      } else {
        setError('Failed to fetch company view');
      }
    } catch (error) {
      setError('Failed to fetch company view');
    } finally {
      setDepartmentViewLoading(false);
    }
  };

  const fetchAllUsers = async () => {
    // Only fetch all users if user has both schedule.manage and users.view (backend requires users.view)
    if (!hasPermission('schedule.manage') || !hasPermission('users.view')) {
      console.log('👤 User does not have schedule.manage and users.view, skipping fetchAllUsers');
      setUsersLoading(false);
      return;
    }

    console.log('🔄 Fetching all users...');
    setUsersLoading(true);
    try {
      const response = await fetch(`${getApiUrl(API_ENDPOINTS.USERS)}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      console.log('📡 Users API response status:', response.status);
      if (response.ok) {
        const data = await response.json();
        console.log('📊 Users API response data:', data);
        
        // Filter out archived users (isActive: false) for schedule display
        const activeUsers = (data.users || []).filter((u: any) => u.isActive !== false);
        console.log(`✅ Loaded ${data.users ? data.users.length : 0} total users, ${activeUsers.length} active users`);
        
        setAllUsers(activeUsers);  // Only set active users
      } else {
        const errorText = await response.text();
        console.error('❌ Users API failed:', response.status, errorText);
        setError('Failed to fetch users');
      }
    } catch (error) {
      console.error('❌ Error fetching users:', error);
      setError('Failed to fetch users');
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchCellHighlights = async () => {
    try {
      const { startDate, endDate } = getMonthFetchRange();
      
      const response = await fetch(`${getApiUrl(API_ENDPOINTS.WORK_SCHEDULE_HIGHLIGHTS)}?startDate=${startDate}&endDate=${endDate}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const highlightsMap = new Map<string, boolean>();
        
        data.data.forEach((highlight: any) => {
          const cellKey = `${highlight.userId}-${highlight.date}`;
          highlightsMap.set(cellKey, true);
        });
        
        setCellHighlights(highlightsMap);
      } else {
        console.error('Failed to fetch cell highlights');
      }
    } catch (error) {
      console.error('Error fetching cell highlights:', error);
    }
  };

  const fetchCellBlueMarks = async () => {
    try {
      const { startDate, endDate } = getMonthFetchRange();
      
      const response = await fetch(`${getApiUrl(API_ENDPOINTS.WORK_SCHEDULE_BLUE_MARKS)}?startDate=${startDate}&endDate=${endDate}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const blueMarksMap = new Map<string, boolean>();
        
        data.data.forEach((blueMark: any) => {
          const cellKey = `${blueMark.userId}-${blueMark.date}`;
          blueMarksMap.set(cellKey, true);
        });
        
        setCellBlueMarks(blueMarksMap);
      } else {
        console.error('Failed to fetch cell blue marks');
      }
    } catch (error) {
      console.error('Error fetching cell blue marks:', error);
    }
  };

  const fetchExcursionsForMonth = async () => {
    try {
      const { startDate, endDate } = getMonthFetchRange();
      
      const response = await fetch(`${getApiUrl(API_ENDPOINTS.EXCURSIONS_BY_DATE_RANGE)}?startDate=${startDate}&endDate=${endDate}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const excursionsMap = new Map<string, any[]>();
        
        data.data.forEach((excursion: any) => {
          if (excursion?.status === 'cancelled') return;
          if (excursion.assignedColleague) {
            const excursionDate = excursion.eventDate.split('T')[0]; // Get date part only
            const cellKey = `${excursion.assignedColleague.id}-${excursionDate}`;
            
            if (!excursionsMap.has(cellKey)) {
              excursionsMap.set(cellKey, []);
            }
            excursionsMap.get(cellKey)!.push(excursion);
          }
        });
        
        setExcursionsByUserDate(excursionsMap);
      } else {
        console.error('Failed to fetch excursions');
      }
    } catch (error) {
      console.error('Error fetching excursions:', error);
    }
  };

  const fetchEdukacijosForMonth = async () => {
    try {
      const { startDate, endDate } = getMonthFetchRange();

      const response = await fetch(`${getApiUrl(API_ENDPOINTS.EDUKATIONS_BY_DATE_RANGE)}?startDate=${startDate}&endDate=${endDate}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const edukacijosMap = new Map<string, any[]>();

        data.data.forEach((edukation: any) => {
          if (edukation?.status === 'cancelled') return;
          const edukationDate = edukation.eventDate.split('T')[0];
          const runners: { id: string }[] =
            edukation.assignedColleagues && edukation.assignedColleagues.length > 0
              ? edukation.assignedColleagues
              : edukation.assignedColleague
                ? [edukation.assignedColleague]
                : [];
          const watchers: { id: string }[] = edukation.watcherColleagues || [];
          const schedulePeople = [...runners, ...watchers];
          schedulePeople.forEach((person: { id?: string }) => {
            if (!person?.id) return;
            const cellKey = `${person.id}-${edukationDate}`;
            if (!edukacijosMap.has(cellKey)) {
              edukacijosMap.set(cellKey, []);
            }
            edukacijosMap.get(cellKey)!.push(edukation);
          });
        });

        setEdukacijosByUserDate(edukacijosMap);
      } else {
        console.error('Failed to fetch edukacijos');
      }
    } catch (error) {
      console.error('Error fetching edukacijos:', error);
    }
  };

  const fetchPlanetariumForMonth = async () => {
    try {
      const { startDate, endDate } = getMonthFetchRange();

      const response = await fetch(
        `${getApiUrl(API_ENDPOINTS.PLANETARIUM_BY_DATE_RANGE)}?startDate=${startDate}&endDate=${endDate}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        const planetariumMap = new Map<string, any[]>();

        (data.data || []).forEach((session: any) => {
          if (session?.status === 'not_working') return;
          const sessionDate = String(session.sessionDate).split('T')[0];
          const assignees: { id: string }[] = session.assignedColleagues || [];
          assignees.forEach((person) => {
            if (!person?.id) return;
            const cellKey = `${person.id}-${sessionDate}`;
            if (!planetariumMap.has(cellKey)) {
              planetariumMap.set(cellKey, []);
            }
            planetariumMap.get(cellKey)!.push(session);
          });
        });

        setPlanetariumByUserDate(planetariumMap);
      } else {
        console.error('Failed to fetch planetarium sessions');
      }
    } catch (error) {
      console.error('Error fetching planetarium sessions:', error);
    }
  };

  useEffect(() => {
    fetchAllUsers();
  }, [hasPermission]);

  useEffect(() => {
    if (user) {
      fetchPendingApprovals();
    }
  }, [user, hasPermission]);

  useEffect(() => {
    fetchSchedules();
  }, []);

  useEffect(() => {
    fetchAllUsers();
  }, [hasPermission]);

  useEffect(() => {
    // Load company (department) view data when the department view tab is active
    if (activeTab === 0) {
      fetchCompanyView();
      fetchCellHighlights();
      fetchCellBlueMarks();
      fetchExcursionsForMonth();
      fetchEdukacijosForMonth();
      fetchPlanetariumForMonth();
      fetchPinnedUsers();
      fetchExcludedFromWorkersCountUsers();
      fetchRemoteWeeklyLimits();
    }
  }, [selectedMonth, activeTab]);

  const formatRemoteWeeklyLimitError = (maxAllowed?: number) => {
    const max = typeof maxAllowed === 'number' && maxAllowed > 0 ? maxAllowed : 2;
    return t(
      'schedule.remoteWeeklyLimitExceeded',
      'Viršytas darbo nuotoliu dienų limitas: per savaitę leidžiamos daugiausia {{max}} dienos.',
      { max }
    );
  };

  const fetchPinnedUsers = async () => {
    if (!hasPermission('schedule.manage') || !hasPermission('users.view') || !token) {
      return;
    }

    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.USERS_PINNED), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setPinnedUsers(new Set(data.pinnedUserIds || []));
        }
      }
    } catch (error) {
      console.error('Error fetching pinned users:', error);
    }
  };

  const fetchExcludedFromWorkersCountUsers = async () => {
    if (!hasPermission('schedule.manage') || !hasPermission('users.view') || !token) {
      return;
    }

    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.USERS_EXCLUDED_FROM_WORKERS_COUNT), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setExcludedFromWorkersCountUsers(new Set(data.excludedUserIds || []));
        }
      }
    } catch (error) {
      console.error('Error fetching excluded users:', error);
    }
  };

  const fetchRemoteWeeklyLimits = async () => {
    if (!hasPermission('schedule.view') || !token) {
      return;
    }
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.USERS_REMOTE_WEEKLY_LIMITS), {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setRemoteWeeklyLimits(data.limits || {});
        }
      }
    } catch (error) {
      console.error('Error fetching remote weekly limits:', error);
    }
  };

  const openRemoteLimitDialog = (userId: string, name: string) => {
    setRemoteLimitDialogUser({ userId, name });
    setRemoteLimitDialogValue(remoteWeeklyLimits[userId] || 2);
    setRemoteLimitDialogOpen(true);
  };

  const saveRemoteWeeklyLimit = async () => {
    if (!token || !remoteLimitDialogUser) return;
    try {
      setRemoteLimitSaving(true);
      const limit = remoteLimitDialogValue;
      const response = await fetch(
        getApiUrl(API_ENDPOINTS.USER_REMOTE_WEEKLY_LIMIT(remoteLimitDialogUser.userId)),
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            limit: limit === 2 ? null : limit
          })
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || 'Failed to save remote weekly limit');
      }
      setRemoteWeeklyLimits((prev) => {
        const next = { ...prev };
        if (!data.limit || data.limit === 2 || data.isDefault) {
          delete next[remoteLimitDialogUser.userId];
        } else {
          next[remoteLimitDialogUser.userId] = data.limit;
        }
        return next;
      });
      setRemoteLimitDialogOpen(false);
      setRemoteLimitDialogUser(null);
      setSuccess(
        t('schedule.remoteWeeklyLimitSaved', 'Nuotolio dienų limitas išsaugotas ({{max}} / savaitę)', {
          max: data.limit ?? 2
        })
      );
    } catch (err) {
      console.error('Error saving remote weekly limit:', err);
      setError(err instanceof Error ? err.message : 'Nepavyko išsaugoti limito');
    } finally {
      setRemoteLimitSaving(false);
    }
  };

  const formatMonthKey = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  };

  const fetchAuditLogs = async () => {
    if (!token || !isSuperAdmin) {
      return;
    }

    setAuditLogsLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('month', formatMonthKey(auditMonthFilter));
      params.set('page', String(auditLogPage));
      params.set('limit', String(auditLogLimit));
      params.set('sort', 'desc');
      if (auditDepartmentFilter) params.set('department', auditDepartmentFilter);
      if (auditActorFilter) params.set('actorUserId', auditActorFilter);
      if (auditEmployeeFilter) params.set('targetUserId', auditEmployeeFilter);
      if (auditActionFilter) params.set('actionType', auditActionFilter);

      const response = await fetch(`${getApiUrl(API_ENDPOINTS.WORK_SCHEDULE_LOGS)}?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch audit logs');
      }

      const result = await response.json();
      if (result.success) {
        setAuditLogs(result.data || []);
        setAuditLogTotal(result.pagination?.total || 0);
      }
    } catch (err) {
      console.error('Error fetching work schedule audit logs:', err);
      setError(t('schedule.logs.fetchFailed', 'Nepavyko įkelti audito įrašų'));
    } finally {
      setAuditLogsLoading(false);
    }
  };

  useEffect(() => {
    setAuditLogPage(1);
  }, [auditMonthFilter, auditDepartmentFilter, auditActorFilter, auditEmployeeFilter, auditActionFilter]);

  useEffect(() => {
    if (auditLogsTabIndex !== null && activeTab === auditLogsTabIndex) {
      fetchAuditLogs();
    }
  }, [activeTab, auditLogsTabIndex, auditMonthFilter, auditDepartmentFilter, auditActorFilter, auditEmployeeFilter, auditActionFilter, auditLogPage, auditLogLimit, token, isSuperAdmin]);

  // WebSocket setup and real-time updates
  useEffect(() => {
    if (!user) return;

    const token = localStorage.getItem('token');
    if (!token) return;

    console.log('🔌 Setting up WebSocket connection for user:', user.id, user.firstName, user.lastName);

    // Connect to WebSocket
    webSocketService.connect(token);

    // Add connection status listeners
    const handleConnected = () => {
      console.log('🔌 WebSocket connected successfully in WorkSchedule component');
    };

    const handleDisconnected = (reason: any) => {
      console.log('🔌 WebSocket disconnected in WorkSchedule component:', reason);
    };

    const handleConnectionError = (error: any) => {
      console.error('🔌 WebSocket connection error in WorkSchedule component:', error);
    };

    webSocketService.on('connected', handleConnected);
    webSocketService.on('disconnected', handleDisconnected);
    webSocketService.on('connection-error', handleConnectionError);

    // Set up event listeners
    const handleScheduleUpdate = (data: any) => {
      console.log('📅 Real-time schedule update received:', data);
      console.log('📅 Current user:', user?.id, user?.firstName, user?.lastName);
      console.log('📅 Update type:', data.type);
      console.log('📅 Schedule user ID:', data.schedule?.userId);
      console.log('📅 Schedule date:', data.schedule?.date);
      
      const schedule = data.schedule;
      if (!schedule?.userId) return;

      const normalizeDate = (d: unknown): string | undefined => {
        if (d == null) return undefined;
        if (typeof d === 'string') return d.length >= 10 ? d.slice(0, 10) : d;
        try {
          return new Date(d as string | number).toISOString().slice(0, 10);
        } catch {
          return undefined;
        }
      };
      const normalizedDate = normalizeDate(schedule.date);

      // Approved rows update the grid; pending proposals only go to pendingApprovals (LAUKIA)
      const isPendingSchedule =
        data.type !== 'deleted' && schedule.isApproved === false;
      if (!isPendingSchedule) {
        setCompanyView(prev => {
          console.log('📅 Previous companyView employees:', prev.length);
          const updated = prev.map(employee => {
            if (employee.userId === schedule.userId) {
              console.log('📅 Found matching employee:', employee.name);
              const scheduleToMerge = normalizedDate
                ? { ...schedule, date: normalizedDate }
                : schedule;
              const updatedSchedules = mergeScheduleSegmentIntoList(
                employee.schedules,
                scheduleToMerge as WorkScheduleEntry,
                data.type === 'deleted' ? 'delete' : 'upsert'
              );
              console.log('📅 Merged schedules count:', updatedSchedules.length);
              return {
                ...employee,
                schedules: updatedSchedules
              };
            }
            return employee;
          });
          console.log('📅 Updated companyView employees:', updated.length);
          return updated;
        });
      } else if (normalizedDate && schedule.previousScheduleBeforeRequest) {
        // Rehydrate approved baseline if same-status pending overwrote the DB row
        setCompanyView((prev) =>
          mergePendingIntoCompanyView(prev, [
            { ...schedule, date: normalizedDate } as ScheduleChangeRequest
          ])
        );
      }

      // When month is closed, inline edit creates pending schedule (isApproved: false).
      // Add to pendingApprovals so LAUKIA badge shows for admin. Remove when deleted.
      setPendingApprovals(prev => {
        if (!normalizedDate) return prev;
        const filtered = prev.filter(
          (req) =>
            !(
              req.userId === schedule.userId &&
              (req.date === schedule.date || req.date === normalizedDate) &&
              req.status === schedule.status
            )
        );
        if (data.type !== 'deleted' && schedule.isApproved === false) {
          filtered.push({ ...schedule, date: normalizedDate });
        }
        return filtered;
      });

      // Show success message
      setSuccess(`${data.user?.name ?? 'Vartotojas'} ${data.type} a schedule entry`);
    };

    const handleChangeRequestUpdate = (data: any) => {
      const cr = data?.changeRequest;
      if (!cr || cr.userId === undefined || cr.date === undefined) return;

      const normalizeDate = (d: unknown): string | undefined => {
        if (d == null) return undefined;
        if (typeof d === 'string') return d.length >= 10 ? d.slice(0, 10) : d;
        try {
          return new Date(d as string | number).toISOString().slice(0, 10);
        } catch {
          return undefined;
        }
      };
      const normalizedDate = normalizeDate(cr.date);
      if (!normalizedDate) return;
      const normalizedCr = { ...cr, date: normalizedDate };

      console.log('📝 Real-time change request update received:', data);
      console.log('📝 Current pendingApprovals count:', pendingApprovals.length);
      console.log('📝 Change request data:', {
        userId: cr?.userId,
        date: cr?.date,
        type: data.type,
        isApproved: cr?.isApproved
      });

      // Update pending approvals (per status segment)
      setPendingApprovals(prev => {
        console.log('📝 Previous pendingApprovals:', prev.length);
        const filtered = prev.filter(
          (req) =>
            !(
              req.userId === normalizedCr.userId &&
              req.date === normalizedCr.date &&
              req.status === normalizedCr.status
            )
        );
        console.log('📝 Filtered pendingApprovals:', filtered.length);
        if (data.type === 'submitted' && normalizedCr) {
          filtered.push(normalizedCr);
          console.log('📝 Added new change request, new count:', filtered.length);
        }
        return filtered;
      });

      // Pending submission: keep approved hours; rehydrate from snapshot if needed (do not show proposal times)
      if (data.type === 'submitted' && normalizedCr) {
        setCompanyView((prev) => mergePendingIntoCompanyView(prev, [normalizedCr as ScheduleChangeRequest]));
      }

      // When cancelled or rejected: restore original segment if server sent it, otherwise remove that status
      if ((data.type === 'cancelled' || data.type === 'rejected') && normalizedCr) {
        const restored = data.restoredSchedule;
        setCompanyView(prev => prev.map(employee => {
          if (employee.userId !== normalizedCr.userId) return employee;
          if (restored && restored.userId && restored.date) {
            const restoredDate = normalizeScheduleDate(restored.date) || restored.date;
            return {
              ...employee,
              schedules: mergeScheduleSegmentIntoList(
                employee.schedules,
                { ...restored, date: restoredDate } as WorkScheduleEntry,
                'upsert'
              )
            };
          }
          return {
            ...employee,
            schedules: mergeScheduleSegmentIntoList(
              employee.schedules,
              normalizedCr as WorkScheduleEntry,
              'delete'
            )
          };
        }));
      }

      // Show notification
      if (data.type === 'cancelled') {
        setSuccess(t('schedule.changeRequestCancelled', 'Change request was cancelled'));
      } else if (data.type === 'rejected') {
        setSuccess(t('schedule.changeRequestRejected', 'Change request was rejected'));
      } else if (data.type === 'submitted') {
        setSuccess(`${data.user?.name ?? 'Vartotojas'} submitted a change request`);
      }
    };

    const handleApprovalUpdate = (data: any) => {
      console.log('✅ Real-time approval update received:', data);
      
      // Update pending approvals
      setPendingApprovals(prev => prev.filter(req => req.id !== data.changeRequest.id));
      
      // Update company view if approved (keep same-day counterpart segments)
      if (data.type === 'approved') {
        setCompanyView(prev => prev.map(employee => {
          if (employee.userId === data.changeRequest.userId) {
            const crDate =
              normalizeScheduleDate(data.changeRequest.date) || data.changeRequest.date;
            return {
              ...employee,
              schedules: mergeScheduleSegmentIntoList(
                employee.schedules,
                { ...data.changeRequest, date: crDate } as WorkScheduleEntry,
                'upsert'
              )
            };
          }
          return employee;
        }));
      }

      // Show notification
      setSuccess(`Change request ${data.type} by ${data.user?.name ?? 'Vartotojas'}`);
    };

    const handleUserEditing = (data: any) => {
      console.log('✏️ User editing:', data);
      // TODO: Show visual indicator that someone is editing
    };

    const handleUserStoppedEditing = (data: any) => {
      console.log('✏️ User stopped editing:', data);
      // TODO: Remove visual indicator
    };

    // Register event listeners
    webSocketService.on('schedule-updated', handleScheduleUpdate);
    webSocketService.on('schedule-deleted', handleScheduleUpdate);
    webSocketService.on('change-request-updated', handleChangeRequestUpdate);
    webSocketService.on('approval-updated', handleApprovalUpdate);
    webSocketService.on('user-editing', handleUserEditing);
    webSocketService.on('user-stopped-editing', handleUserStoppedEditing);

    // Join current month view
    const year = selectedMonth.getFullYear();
    const month = selectedMonth.getMonth() + 1;
    webSocketService.joinMonth(year, month);

    // Visibility fallback: refetch pending approvals when tab becomes visible (handles missed WebSocket events)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchPendingApprovals();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup on unmount
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      webSocketService.off('connected', handleConnected);
      webSocketService.off('disconnected', handleDisconnected);
      webSocketService.off('connection-error', handleConnectionError);
      webSocketService.off('schedule-updated', handleScheduleUpdate);
      webSocketService.off('schedule-deleted', handleScheduleUpdate);
      webSocketService.off('change-request-updated', handleChangeRequestUpdate);
      webSocketService.off('approval-updated', handleApprovalUpdate);
      webSocketService.off('user-editing', handleUserEditing);
      webSocketService.off('user-stopped-editing', handleUserStoppedEditing);
      
      webSocketService.leaveMonth(year, month);
    };
  }, [user, selectedMonth]);

  // Merge all users into company view so we show rows even without schedules.
  // Limit /users merge to viewable departments unless view-all.
  const mergedCompanyView = React.useMemo(() => {
    if (!allUsers || allUsers.length === 0) {
      return companyView;
    }
    const viewSet = canViewAllDepartments
      ? null
      : new Set(
          (viewableDepartments || [])
            .map((d) => d.trim().toLowerCase())
            .filter(Boolean)
        );
    const ownDeptNorm = (user?.department || '').trim().toLowerCase();
    if (viewSet && ownDeptNorm) viewSet.add(ownDeptNorm);

    const usersForMerge = canViewAllDepartments
      ? allUsers
      : allUsers.filter((u) => {
          if (viewSet && viewSet.size > 0) {
            return viewSet.has((u.department || '').trim().toLowerCase());
          }
          if (ownDeptNorm) {
            return (u.department || '').trim().toLowerCase() === ownDeptNorm;
          }
          return String(u.id) === String(user?.id);
        });
    const byId = new Map(companyView.map(emp => [emp.userId, emp]));
    const merged: CompanyEmployee[] = usersForMerge.map(u => {
      const existing = byId.get(u.id);
      return {
        userId: u.id,
        name: `${u.firstName} ${u.lastName}`,
        department: u.department,
        position: u.position,
        schedules: existing?.schedules || [],
        etatas: existing?.etatas
      };
    });
    // Include any employees returned by department view that are not in allUsers (safety)
    companyView.forEach(emp => {
      if (!merged.find(m => m.userId === emp.userId)) {
        merged.push(emp);
      }
    });
    return merged;
  }, [allUsers, companyView, canViewAllDepartments, viewableDepartments, user?.department, user?.id]);

  useEffect(() => {
    if (mergedCompanyView.length > 0) {
      const departmentsFromData = Array.from(
        new Set(mergedCompanyView.map(emp => emp.department).filter(Boolean))
      ) as string[];
      // Chips only within viewable set (or all when view-all)
      const departments = canViewAllDepartments || !viewableDepartments
        ? departmentsFromData
        : departmentsFromData.filter((d) =>
            viewableDepartments.some(
              (v) => v.trim().toLowerCase() === (d || '').trim().toLowerCase()
            )
          );
      setAvailableDepartments(departments);

      if (!canToggleDepartments) {
        const ownDept = (user?.department || '').trim();
        const locked = ownDept && departments.includes(ownDept)
          ? [ownDept]
          : departments.length > 0
            ? [departments[0]]
            : [];
        setSelectedDepartments(locked);
        return;
      }
      
      // Validate saved selection within viewable departments
      const validSavedDepartments = selectedDepartments.filter(d => departments.includes(d));
      
      if (validSavedDepartments.length > 0) {
        setSelectedDepartments(validSavedDepartments);
      } else if (selectedDepartments.length === 0) {
        setSelectedDepartments(departments);
      } else {
        // Drop selections outside allowlist
        setSelectedDepartments(validSavedDepartments.length > 0 ? validSavedDepartments : departments);
      }
    }
  }, [mergedCompanyView, canViewAllDepartments, canToggleDepartments, viewableDepartments, user?.department]); // Remove selectedDepartments from deps to avoid infinite loop

  // Filter company view based on selected departments and sort with current user first,
  // then by pinned status, etatas, and name.
  const filteredCompanyView = mergedCompanyView
    .filter(employee => selectedDepartments.includes(employee.department))
    .sort((a, b) => {
      // Highest priority: logged-in user always first (if present in this view)
      const currentUserId = user?.id;
      if (currentUserId) {
        const aIsCurrent = a.userId === currentUserId;
        const bIsCurrent = b.userId === currentUserId;

        if (aIsCurrent && !bIsCurrent) return -1;
        if (bIsCurrent && !aIsCurrent) return 1;
      }

      // Primary sort (after current user): pinned users first
      const aPinned = a.isPinned || pinnedUsers.has(a.userId);
      const bPinned = b.isPinned || pinnedUsers.has(b.userId);
      
      if (aPinned !== bPinned) {
        return aPinned ? -1 : 1; // Pinned users come first
      }
      
      // Secondary sort: by etatas (descending - highest first)
      const etatasA = a.etatas ?? 0;
      const etatasB = b.etatas ?? 0;
      
      if (etatasB !== etatasA) {
        return etatasB - etatasA; // Descending order
      }
      
      // Tertiary sort: alphabetically by name (ascending)
      return a.name.localeCompare(b.name, 'lt'); // Lithuanian locale for proper alphabet sorting
    });

  const stateHolidayDateSet = React.useMemo(() => {
    const year = selectedMonth.getFullYear();
    const month = selectedMonth.getMonth();
    return new Set(getStateHolidaysInMonth(year, month, mergedCompanyView));
  }, [mergedCompanyView, selectedMonth]);

  // Month title reused for both desktop and mobile views
  const mobileMonthTitle = selectedMonth.toLocaleDateString('lt-LT', {
    month: 'long',
    year: 'numeric',
  });

  // Current logged-in employee record; always visible in mobile view (ignore department filter).
  // Do NOT use filteredCompanyView here — when the user deselects their own department chip, filtered rows exclude them.
  const currentUserEmployeeFromMerged = React.useMemo<CompanyEmployee | null>(() => {
    if (!user?.id) return null;
    const uid = String(user.id);
    return mergedCompanyView.find((emp) => String(emp.userId) === uid) ?? null;
  }, [mergedCompanyView, user?.id]);

  const resolveScheduleOwnerDepartment = React.useCallback(
    (ownerUserId?: string | null, fallbackDepartment?: string | null) => {
      if (fallbackDepartment) return fallbackDepartment;
      if (ownerUserId) {
        const uid = String(ownerUserId);
        const fromCompany = mergedCompanyView.find((emp) => String(emp.userId) === uid)?.department;
        if (fromCompany) return fromCompany;
        const fromUsers = allUsers.find((u) => String(u.id) === uid)?.department;
        if (fromUsers) return fromUsers;
      }
      return user?.department || null;
    },
    [mergedCompanyView, allUsers, user?.department]
  );

  useEffect(() => {
    if (currentUserEmployeeFromMerged) {
      setMobileScheduleFallbackEmployee(null);
      return;
    }
    if (!user?.id) {
      setMobileScheduleFallbackEmployee(null);
      return;
    }

    let cancelled = false;
    const { startDate, endDate } = getMonthFetchRange();

    void (async () => {
      try {
        const response = await fetch(
          `${getApiUrl(API_ENDPOINTS.WORK_SCHEDULE)}?startDate=${startDate}&endDate=${endDate}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        if (!response.ok || cancelled) return;
        const data = await response.json();
        const rows = (data.data || []) as WorkScheduleEntry[];
        const schedules = rows.filter((s) => s.isApproved !== false);
        if (cancelled) return;
        setMobileScheduleFallbackEmployee({
          userId: String(user.id),
          name: `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.email || 'User',
          department: user.department || 'No Department',
          position: user.position,
          schedules
        });
      } catch {
        if (!cancelled) setMobileScheduleFallbackEmployee(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [currentUserEmployeeFromMerged, user, selectedMonth]);

  const currentUserEmployee = currentUserEmployeeFromMerged ?? mobileScheduleFallbackEmployee;

  // Per-day view for Mobilus grafikas: full Mon–Sun weeks covering selectedMonth (padded edges)
  const currentUserDays: MobileScheduleDay[] = React.useMemo(() => {
    if (!currentUserEmployee) return [];

    const { rangeStart, rangeEnd } = monthGridRange(selectedMonth);
    const gridDays = eachLocalDay(rangeStart, rangeEnd);

    return gridDays.map((dateObj) => {
      const dateString = formatDateLocal(dateObj);
      const schedule = getPrimaryScheduleForDate(currentUserEmployee.schedules, dateString);
      const praktikaSeg = getPraktikaScheduleForDate(currentUserEmployee.schedules, dateString);

      const weekdayLabel = dateObj.toLocaleDateString('lt-LT', { weekday: 'long' });
      const dateLabel = dateObj.toLocaleDateString('lt-LT', { day: '2-digit', month: '2-digit' });

      const cellKey = user?.id ? `${user.id}-${dateString}` : '';
      const dayExcursions = cellKey ? excursionsByUserDate.get(cellKey) : undefined;
      const dayEdukacijos = cellKey ? edukacijosByUserDate.get(cellKey) : undefined;
      const dayPlanetarium = cellKey ? planetariumByUserDate.get(cellKey) : undefined;

      return {
        date: dateObj,
        dateString,
        dateLabel,
        weekdayLabel,
        status: schedule?.status ?? (praktikaSeg ? 'praktika' : 'none'),
        startTime: schedule?.startTime ?? (praktikaSeg && !schedule ? praktikaSeg.startTime : undefined),
        endTime: schedule?.endTime ?? (praktikaSeg && !schedule ? praktikaSeg.endTime : undefined),
        breakStartTime: schedule?.breakStartTime,
        breakEndTime: schedule?.breakEndTime,
        totalHours:
          schedule && schedule.status !== 'rest_day' && schedule.status !== 'praktika'
            ? getScheduleHours(schedule)
            : !schedule && praktikaSeg
              ? getScheduleHours(praktikaSeg)
              : 0,
        notes: schedule?.notes ?? (!schedule && praktikaSeg ? praktikaSeg.notes : undefined),
        praktikaStartTime:
          schedule && praktikaSeg ? praktikaSeg.startTime : undefined,
        praktikaEndTime: schedule && praktikaSeg ? praktikaSeg.endTime : undefined,
        praktikaTotalHours:
          schedule && praktikaSeg ? getScheduleHours(praktikaSeg) : undefined,
        excursions:
          dayExcursions && dayExcursions.length > 0
            ? dayExcursions.map((e: { title: string; startTime?: string; endTime?: string }) => ({
                title: e.title,
                startTime: e.startTime,
                endTime: e.endTime
              }))
            : undefined,
        edukacijos:
          dayEdukacijos && dayEdukacijos.length > 0
            ? dayEdukacijos.map((e: { title: string; startTime?: string; endTime?: string }) => ({
                title: e.title,
                startTime: e.startTime,
                endTime: e.endTime
              }))
            : undefined,
        planetarium:
          dayPlanetarium && dayPlanetarium.length > 0
            ? dayPlanetarium.map(
                (s: { program?: string; startTime?: string; endTime?: string; status?: string }) => ({
                  title:
                    s.status === 'technical_day'
                      ? 'Techninė diena'
                      : s.status === 'planetarium_closed'
                        ? 'Planetariumas uždarytas'
                        : s.program || 'Planetariumas',
                  startTime: s.startTime,
                  endTime: s.endTime
                })
              )
            : undefined
      };
    });
  }, [currentUserEmployee, selectedMonth, user?.id, excursionsByUserDate, edukacijosByUserDate, planetariumByUserDate]);

  // Monthly planned hours: calendar month only (exclude padded adjacent-month days)
  const currentUserMonthPlannedHours = React.useMemo(() => {
    const year = selectedMonth.getFullYear();
    const month = selectedMonth.getMonth();
    return currentUserDays.reduce((sum, d) => {
      if (d.date.getFullYear() !== year || d.date.getMonth() !== month) return sum;
      if (
        d.status === 'praktika' ||
        d.status === 'rest_day' ||
        d.status === 'state_holiday' ||
        d.status === 'none'
      ) {
        return sum;
      }
      return sum + (d.totalHours || 0);
    }, 0);
  }, [currentUserDays, selectedMonth]);

  /** Calendar-month slice only (print + any month-grid consumers). */
  const currentUserMonthDays = React.useMemo(() => {
    const year = selectedMonth.getFullYear();
    const month = selectedMonth.getMonth();
    return currentUserDays.filter(
      (d) => d.date.getFullYear() === year && d.date.getMonth() === month
    );
  }, [currentUserDays, selectedMonth]);

  // Monthly hours balance for the current user (mobile view), aligned with desktop logic
  const currentUserMonthHoursBalance = React.useMemo(() => {
    if (!currentUserEmployee) return null;

    const year = selectedMonth.getFullYear();
    const month = selectedMonth.getMonth();
    let required = 0;

    if (currentUserEmployee.workAccountingType === 'summary') {
      required = calculateSummaryAccountingHours(
        currentUserEmployee,
        year,
        month,
        filteredCompanyView
      );
    } else {
      const etatas = currentUserEmployee.etatas || 1;
      required = Number(requiredHours) > 0 ? Number(requiredHours) * etatas : 0;
    }

    if (required === 0) return null;
    return Math.round((currentUserMonthPlannedHours - required) * 100) / 100;
  }, [currentUserEmployee, currentUserMonthPlannedHours, filteredCompanyView, requiredHours, selectedMonth]);

  const handleSubmit = async () => {
    if (!selectedDate || !user?.id) return;

    // Sync hours string to times on save (day_off/sick_leave/mothers_day)
    let effectiveStartTime = startTime;
    let effectiveEndTime = endTime;
    if (isHoursOnlyStatus(selectedStatus)) {
      const hours = parseHoursInput(addDialogHoursStr);
      if (hours !== null && hours > 0) {
        effectiveStartTime = new Date('2000-01-01T09:00:00');
        effectiveEndTime = new Date(effectiveStartTime.getTime() + (hours * 60 * 60 * 1000));
      } else if (addDialogHoursStr.trim() !== '') {
        setError(t('schedule.invalidHours', 'Įveskite tinkamą valandų skaičių (pvz. 7,5 arba 8)'));
        return;
      } else {
        effectiveStartTime = createDefaultTime();
        effectiveEndTime = createDefaultTime();
      }
    }

    const scheduleDate = formatDateLocal(selectedDate);

    const breakValidationError = getBreakOutOfShiftMessage(
      selectedStatus,
      effectiveStartTime,
      effectiveEndTime,
      breakStartTime,
      breakEndTime
    );
    if (breakValidationError) {
      setError(breakValidationError);
      return;
    }
    if (selectedStatus === 'remote' && !notes.trim()) {
      setError(t('schedule.remoteAddressRequired', 'Reikia įvesti darbo nuotoliu adresą.'));
      return;
    }
    
    // Check if user has schedule management permission
    if (!hasPermission('schedule.manage')) {
      const requestDate = new Date(scheduleDate);
      const year = requestDate.getFullYear();
      const month = requestDate.getMonth() + 1;
      
      let isMonthLocked = false;
      try {
        const monthStatusResponse = await fetch(`${getApiUrl(API_ENDPOINTS.MONTH_STATUS(year, month, user?.department))}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (monthStatusResponse.ok) {
          const monthStatusData = await monthStatusResponse.json();
          isMonthLocked = monthStatusData.data?.isLocked || false;
        } else {
          isMonthLocked = true; // Default to locked if we can't determine
        }
      } catch (error) {
        console.error('Error checking month lock status:', error);
        isMonthLocked = true; // Default to locked for safety
      }

      // If month is locked and user is not admin, they should not be able to edit directly
      if (isMonthLocked) {
        setError(t('schedule.monthLockedNoChanges'));
        setDialogOpen(false);
        resetForm();
        return;
      }
    }
    
    // Store original schedule for rollback
    const originalSchedule = companyView
      .find(emp => emp.userId === user.id)
      ?.schedules.find(s => s.date === scheduleDate);

    // Clear break times for vacation statuses (they don't have breaks)
    const shouldHaveBreak = selectedStatus === 'working' || selectedStatus === 'remote' || selectedStatus === 'praktika';
    // Prepare optimistic update data
    const optimisticSchedule = {
      status: selectedStatus,
      startTime: effectiveStartTime?.toTimeString().slice(0, 5),
      endTime: effectiveEndTime?.toTimeString().slice(0, 5),
      breakStartTime: shouldHaveBreak ? (breakStartTime?.toTimeString().slice(0, 5)) : undefined,
      breakEndTime: shouldHaveBreak ? (breakEndTime?.toTimeString().slice(0, 5)) : undefined,
      notes,
      isApproved: canApproveSchedules() // Only users with approval permission auto-approve
    };

    // ✅ OPTIMISTIC UPDATE: Update UI immediately (only after lock check passes)
    updateCompanyViewOptimistically(
      user.id,
      scheduleDate,
      optimisticSchedule,
      originalSchedule ? 'update' : 'create'
    );

    try {
      // Clear break times for vacation statuses (they don't have breaks)
      const shouldHaveBreak = selectedStatus === 'working' || selectedStatus === 'remote' || selectedStatus === 'praktika';
      const scheduleData = {
        userId: user.id,
        date: scheduleDate,
        status: selectedStatus,
        startTime: effectiveStartTime?.toTimeString().slice(0, 5),
        endTime: effectiveEndTime?.toTimeString().slice(0, 5),
        breakStartTime: shouldHaveBreak ? (breakStartTime?.toTimeString().slice(0, 5)) : undefined,
        breakEndTime: shouldHaveBreak ? (breakEndTime?.toTimeString().slice(0, 5)) : undefined,
        notes
      };

      const response = await fetch(`${getApiUrl(API_ENDPOINTS.WORK_SCHEDULE)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(scheduleData)
      });

      if (response.ok) {
        const result = await response.json();
        
        // ✅ Update with real server data (replace temp ID with real ID)
        if (result.data) {
          updateCompanyViewOptimistically(
            user.id,
            scheduleDate,
            {
              ...result.data,
              id: result.data.id // Use real server ID
            },
            'update'
          );
        }
        
        setSuccess('Schedule updated successfully');
        setDialogOpen(false);
        resetForm();
        
        // ✅ NO MORE FULL REFRESH - only update schedules list if needed
        fetchSchedules();
      } else {
        // ❌ ROLLBACK: Revert optimistic update on error
        rollbackCompanyViewUpdate(user.id, scheduleDate, originalSchedule);
        
        const errorText = await response.text();
        console.error('Error response:', errorText);
        
        // Try to parse error message from server and translate it
        let errorMessage = t('schedule.failedToUpdate');
        try {
          const errorData = JSON.parse(errorText);
          const backendMessage = errorData.message || '';
          const backendDetail =
            typeof errorData.detail === 'string' && errorData.detail.trim()
              ? ` (${errorData.detail.trim()})`
              : '';
          const combinedBackendText = `${backendMessage} ${backendDetail} ${errorText}`.toLowerCase();

          // Map backend error messages to translation keys
          if (backendMessage.includes('This month is locked. No changes are allowed')) {
            errorMessage = t('schedule.monthLockedNoChanges');
          } else if (combinedBackendText.includes('remote work limit exceeded')) {
            errorMessage = formatRemoteWeeklyLimitError(
              typeof errorData.maxAllowed === 'number' ? errorData.maxAllowed : undefined
            );
          } else if (backendMessage.includes('This month is closed. No changes or requests are allowed') || 
                     backendMessage.includes('This month is locked. No changes or requests are allowed')) {
            errorMessage = t('schedule.monthLockedNoRequests');
          } else if (backendMessage) {
            errorMessage = `${t('schedule.failedToUpdate')}: ${backendMessage}${backendDetail}`;
          }
        } catch {
          // If not JSON, use default message
          errorMessage = t('schedule.failedToUpdate');
        }
        
        setError(errorMessage);
      }
    } catch (error) {
      // ❌ ROLLBACK: Revert optimistic update on error
      rollbackCompanyViewUpdate(user.id, scheduleDate, originalSchedule);
      setError(t('schedule.failedToUpdate'));
    }
  };

  const handleDelete = async (scheduleId: string) => {
    setScheduleToDelete(scheduleId);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!scheduleToDelete) return;

    // Find the schedule to delete for rollback
    const scheduleToDeleteObj = schedules.find(s => s.id === scheduleToDelete);
    if (!scheduleToDeleteObj) {
      setError('Schedule entry not found');
      setDeleteConfirmOpen(false);
      setScheduleToDelete(null);
      return;
    }

    // ✅ OPTIMISTIC UPDATE: Remove from UI immediately
    setSchedules(prev => prev.filter(s => s.id !== scheduleToDelete));
    
    // Also update company view if this schedule exists there
    updateCompanyViewOptimistically(
      scheduleToDeleteObj.userId,
      scheduleToDeleteObj.date,
      {},
      'delete'
    );

    try {
      const response = await fetch(`${getApiUrl(API_ENDPOINTS.WORK_SCHEDULE)}/${scheduleToDelete}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        setSuccess('Schedule entry deleted successfully');
        
        // ✅ NO MORE FULL REFRESH - only update change requests if needed
        if (canApproveSchedules()) {
          fetchChangeRequests();
        }
      } else {
        // ❌ ROLLBACK: Revert optimistic update on error
        setSchedules(prev => [...prev, scheduleToDeleteObj]);
        rollbackCompanyViewUpdate(scheduleToDeleteObj.userId, scheduleToDeleteObj.date, scheduleToDeleteObj);
        setError('Failed to delete schedule entry');
      }
    } catch (error) {
      // ❌ ROLLBACK: Revert optimistic update on error
      setSchedules(prev => [...prev, scheduleToDeleteObj]);
      rollbackCompanyViewUpdate(scheduleToDeleteObj.userId, scheduleToDeleteObj.date, scheduleToDeleteObj);
      setError('Failed to delete schedule entry');
    } finally {
      setDeleteConfirmOpen(false);
      setScheduleToDelete(null);
    }
  };

  const handleApprove = async (scheduleIds: string[]) => {
    // Store original schedules for rollback
    const schedulesToApprove = schedules.filter(s => scheduleIds.includes(s.id));
    
    // ✅ OPTIMISTIC UPDATE: Mark as approved immediately
    setSchedules(prev => prev.map(schedule => 
      scheduleIds.includes(schedule.id) 
        ? { ...schedule, isApproved: true }
        : schedule
    ));
    
    // Update company view optimistically
    schedulesToApprove.forEach(schedule => {
      updateCompanyViewOptimistically(
        schedule.userId,
        schedule.date,
        { isApproved: true },
        'update'
      );
    });

    try {
      const response = await fetch(`${getApiUrl(API_ENDPOINTS.WORK_SCHEDULE)}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ scheduleIds })
      });

      if (response.ok) {
        setSuccess('Schedule entries approved successfully');
        
        // ✅ NO MORE FULL REFRESH - only update pending approvals
        fetchPendingApprovals();
      } else {
        // ❌ ROLLBACK: Revert optimistic update on error
        setSchedules(prev => prev.map(schedule => 
          scheduleIds.includes(schedule.id) 
            ? schedulesToApprove.find(s => s.id === schedule.id) || schedule
            : schedule
        ));
        
        schedulesToApprove.forEach(schedule => {
          rollbackCompanyViewUpdate(schedule.userId, schedule.date, schedule);
        });
        
        setError('Failed to approve schedule entries');
      }
    } catch (error) {
      // ❌ ROLLBACK: Revert optimistic update on error
      setSchedules(prev => prev.map(schedule => 
        scheduleIds.includes(schedule.id) 
          ? schedulesToApprove.find(s => s.id === schedule.id) || schedule
          : schedule
      ));
      
      schedulesToApprove.forEach(schedule => {
        rollbackCompanyViewUpdate(schedule.userId, schedule.date, schedule);
      });
      
      setError('Failed to approve schedule entries');
    }
  };

  const handleApproveIndividual = async (scheduleId: string) => {
    // Find the schedule to approve
    const scheduleToApprove = schedules.find(s => s.id === scheduleId);
    if (!scheduleToApprove) {
      setError('Schedule not found');
      return;
    }

    // Store original schedule for rollback
    const originalSchedule = companyView
      .find(emp => emp.userId === scheduleToApprove.userId)
      ?.schedules.find(s => s.date === scheduleToApprove.date);

    // ✅ OPTIMISTIC UPDATE: Mark as approved immediately
    updateCompanyViewOptimistically(
      scheduleToApprove.userId,
      scheduleToApprove.date,
      { isApproved: true },
      'update'
    );

    try {
      const response = await fetch(`${getApiUrl(API_ENDPOINTS.WORK_SCHEDULE_APPROVE_CHANGE_REQUEST(scheduleId))}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        // ✅ Confirm optimistic update
        confirmOptimisticUpdate(scheduleToApprove.userId, scheduleToApprove.date);
        
        setSuccess('Change request approved successfully');
        
        // Refresh company view so admin sees the approved schedule immediately
        await fetchCompanyView();
        fetchPendingApprovals();
      } else {
        // ❌ ROLLBACK: Revert optimistic update on error
        rollbackCompanyViewUpdate(scheduleToApprove.userId, scheduleToApprove.date, originalSchedule);
        setError('Failed to approve change request');
      }
    } catch (error) {
      // ❌ ROLLBACK: Revert optimistic update on error
      rollbackCompanyViewUpdate(scheduleToApprove.userId, scheduleToApprove.date, originalSchedule);
      setError('Failed to approve change request');
    }
  };

  const handlePinUser = async (userId: string) => {
    if (!token) return;
    
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.USER_PIN(userId)), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to pin user');
      }

      const data = await response.json();
      if (data.success) {
        // Update pinned users set
        setPinnedUsers(prev => new Set([...prev, userId]));
        // Update company view to reflect pinned status
        setCompanyView(prev => prev.map(emp => 
          emp.userId === userId ? { ...emp, isPinned: true } : emp
        ));
        setSuccess('Vartotojas užkabintas');
      }
    } catch (err) {
      console.error('Error pinning user:', err);
      setError(err instanceof Error ? err.message : 'Nepavyko užkabinti vartotojo');
    }
  };

  const handleUnpinUser = async (userId: string) => {
    if (!token) return;
    
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.USER_UNPIN(userId)), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to unpin user');
      }

      const data = await response.json();
      if (data.success) {
        // Update pinned users set
        setPinnedUsers(prev => {
          const newSet = new Set(prev);
          newSet.delete(userId);
          return newSet;
        });
        // Update company view to reflect unpinned status
        setCompanyView(prev => prev.map(emp => 
          emp.userId === userId ? { ...emp, isPinned: false } : emp
        ));
        setSuccess('Vartotojas atkabintas');
      }
    } catch (err) {
      console.error('Error unpinning user:', err);
      setError(err instanceof Error ? err.message : 'Nepavyko atkabinti vartotojo');
    }
  };

  const handleExcludeFromWorkersCount = async (userId: string) => {
    if (!token) return;

    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.USER_EXCLUDE_FROM_WORKERS_COUNT(userId)), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to exclude user from workers count');
      }

      const data = await response.json();
      if (data.success) {
        setExcludedFromWorkersCountUsers(prev => new Set([...prev, userId]));
        setCompanyView(prev => prev.map(emp =>
          emp.userId === userId ? { ...emp, excludeFromWorkersCount: true } : emp
        ));
        setSuccess('Vartotojas neįtrauktas į darbuotojų skaičių');
      }
    } catch (err) {
      console.error('Error excluding user from workers count:', err);
      setError(err instanceof Error ? err.message : 'Nepavyko neįtraukti vartotojo į darbuotojų skaičių');
    }
  };

  const handleIncludeInWorkersCount = async (userId: string) => {
    if (!token) return;

    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.USER_INCLUDE_IN_WORKERS_COUNT(userId)), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to include user in workers count');
      }

      const data = await response.json();
      if (data.success) {
        setExcludedFromWorkersCountUsers(prev => {
          const newSet = new Set(prev);
          newSet.delete(userId);
          return newSet;
        });
        setCompanyView(prev => prev.map(emp =>
          emp.userId === userId ? { ...emp, excludeFromWorkersCount: false } : emp
        ));
        setSuccess('Vartotojas įtrauktas į darbuotojų skaičių');
      }
    } catch (err) {
      console.error('Error including user in workers count:', err);
      setError(err instanceof Error ? err.message : 'Nepavyko įtraukti vartotojo į darbuotojų skaičių');
    }
  };

  const findEmployeeScheduleForDate = (
    employee: CompanyEmployee,
    cellDate: string,
    year: number,
    month: number,
    day: number
  ) => getPrimaryScheduleForDate(employee.schedules, cellDate, year, month, day);

  const isEmployeeExcludedFromWorkersCount = (employee: CompanyEmployee) =>
    Boolean(employee.excludeFromWorkersCount) || excludedFromWorkersCountUsers.has(employee.userId);

  const shouldCountEmployeeForWorkersCount = (
    employee: CompanyEmployee,
    cellDate: string,
    year: number,
    month: number,
    day: number
  ) => {
    if (isEmployeeExcludedFromWorkersCount(employee)) return false;
    const schedule = findEmployeeScheduleForDate(employee, cellDate, year, month, day);
    return Boolean(schedule && (schedule.status === 'working' || schedule.status === 'remote'));
  };

  const handleRejectIndividual = async (scheduleId: string) => {
    // Find the schedule to reject
    const scheduleToReject = schedules.find(s => s.id === scheduleId);
    if (!scheduleToReject) {
      setError('Schedule not found');
      return;
    }

    // Store original schedule for rollback
    const originalSchedule = companyView
      .find(emp => emp.userId === scheduleToReject.userId)
      ?.schedules.find(s => s.date === scheduleToReject.date);

    // ✅ OPTIMISTIC UPDATE: Remove the pending change request immediately
    updateCompanyViewOptimistically(
      scheduleToReject.userId,
      scheduleToReject.date,
      {},
      'delete'
    );

    try {
      console.log('Rejecting change request for ID:', scheduleId);
      const url = `${getApiUrl(API_ENDPOINTS.WORK_SCHEDULE_REJECT_CHANGE_REQUEST(scheduleId))}`;
      console.log('Reject URL:', url);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      console.log('Reject response status:', response.status);
      
      if (response.ok) {
        const responseData = await response.json();
        console.log('Reject response data:', responseData);
        
        // ✅ Confirm optimistic update
        confirmOptimisticUpdate(scheduleToReject.userId, scheduleToReject.date);
        
        setSuccess('Change request rejected successfully');
        
        // Refresh company view so admin sees the updated schedule immediately
        await fetchCompanyView();
        fetchPendingApprovals();
      } else {
        // ❌ ROLLBACK: Revert optimistic update on error
        rollbackCompanyViewUpdate(scheduleToReject.userId, scheduleToReject.date, originalSchedule);
        
        const errorData = await response.json().catch(() => ({}));
        console.error('Reject error response:', errorData);
        setError(`Failed to reject change request: ${errorData.message || 'Unknown error'}`);
      }
    } catch (error) {
      // ❌ ROLLBACK: Revert optimistic update on error
      rollbackCompanyViewUpdate(scheduleToReject.userId, scheduleToReject.date, originalSchedule);
      
      console.error('Reject error:', error);
      setError('Failed to reject change request');
    }
  };

  const handleCancelOwnRequest = async (requestId: string) => {
    try {
      // Find the pending request to cancel
      const pendingRequest = pendingApprovals.find(pa => pa.id === requestId);
      if (!pendingRequest) return;
      
      // ✅ OPTIMISTIC UPDATE: Remove from pending approvals immediately
      setPendingApprovals((prev) => {
        const next = prev.filter((pa) => pa.id !== requestId);
        pendingApprovalsRef.current = next;
        return next;
      });
      
      const response = await fetch(`${getApiUrl(API_ENDPOINTS.WORK_SCHEDULE)}/cancel-change-request/${requestId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        setSuccess('Change request cancelled successfully');
        // Sync remaining pendings from server, then refetch approved rows and merge pendings
        // so other pending remote days keep times when the user re-opens those cells.
        await fetchPendingApprovals();
        await fetchCompanyView();
      } else {
        // ❌ ERROR: Revert optimistic update
        const data = await response.json();
        setError(data.message || 'Failed to cancel change request');
        fetchPendingApprovals(); // Refetch to restore correct state
      }
    } catch (error) {
      console.error('Error cancelling change request:', error);
      setError('Failed to cancel change request');
      fetchPendingApprovals(); // Refetch on error
    }
  };

  const resetForm = () => {
    setSelectedDate(new Date());
    setSelectedStatus('working');
    setStartTime(null);
    setEndTime(null);
    setBreakStartTime(null);
    setBreakEndTime(null);
    setNotes('');
    setEditingSchedule(null);
  };

  const fetchRemoteAddressTemplate = React.useCallback(async () => {
    if (!token) return;
    try {
      setRemoteTemplateLoading(true);
      const response = await fetch(getApiUrl(API_ENDPOINTS.WORK_SCHEDULE_REMOTE_TEMPLATE), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) return;
      const data = await response.json();
      if (data.success) {
        setRemoteAddressTemplate(typeof data.template === 'string' ? data.template : '');
      }
    } catch (err) {
      console.error('Failed to fetch remote address template:', err);
    } finally {
      setRemoteTemplateLoading(false);
    }
  }, [token]);

  const saveRemoteAddressTemplate = React.useCallback(async (template: string) => {
    if (!token) return false;
    const trimmed = template.trim();
    if (!trimmed) return false;
    try {
      setRemoteTemplateSaving(true);
      const response = await fetch(getApiUrl(API_ENDPOINTS.WORK_SCHEDULE_REMOTE_TEMPLATE), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ template: trimmed })
      });
      if (!response.ok) return false;
      const data = await response.json();
      if (!data.success) return false;
      const saved = typeof data.template === 'string' ? data.template : trimmed;
      setRemoteAddressTemplate(saved);
      return true;
    } catch (err) {
      console.error('Failed to save remote address template:', err);
      return false;
    } finally {
      setRemoteTemplateSaving(false);
    }
  }, [token]);

  const canManageDepartmentTemplates = () => hasPermission('schedule.manage');

  const loadDepartmentTemplates = React.useCallback(async (): Promise<DepartmentScheduleTemplate[]> => {
    if (!token) return [];
    setDeptTemplatesLoading(true);
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.WORK_SCHEDULE_DEPARTMENT_TEMPLATES), {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to load');
      const data = await response.json();
      if (!data.success || !Array.isArray(data.templates)) {
        throw new Error(data.message || 'Invalid response');
      }
      const templates: DepartmentScheduleTemplate[] = data.templates.map((t: DepartmentScheduleTemplate) => ({
        department: t.department,
        enabled: t.enabled !== false,
        week: Array.isArray(t.week) && t.week.length > 0 ? t.week : buildDefaultDepartmentWeek()
      }));
      setDeptTemplates(templates);
      return templates;
    } catch (err) {
      console.error('Failed to load department templates:', err);
      setError(t('schedule.departmentTemplatesLoadFailed'));
      return [];
    } finally {
      setDeptTemplatesLoading(false);
    }
  }, [token, t]);

  const openDeptAutofillDialog = async () => {
    if (isCurrentMonthLocked) {
      setError(t('schedule.departmentAutofillLocked'));
      return;
    }
    const templates = await loadDepartmentTemplates();
    const enabled = templates.filter((tpl) => tpl.enabled).map((tpl) => tpl.department);
    setDeptAutofillSelected(enabled);
    setDeptAutofillDialogOpen(true);
  };

  const openDeptTemplatesDialog = async () => {
    const templates = await loadDepartmentTemplates();
    setEditingDeptTemplateIndex(templates.length > 0 ? 0 : -1);
    setNewDeptTemplateName('');
    setDeptTemplatesDialogOpen(true);
  };

  const saveDepartmentTemplates = async () => {
    if (!token) return;
    setDeptTemplatesSaving(true);
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.WORK_SCHEDULE_DEPARTMENT_TEMPLATES), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ templates: deptTemplates })
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Save failed');
      }
      setDeptTemplates(Array.isArray(data.templates) ? data.templates : deptTemplates);
      setSuccess(t('schedule.departmentTemplatesSaved'));
      setDeptTemplatesDialogOpen(false);
    } catch (err) {
      console.error('Failed to save department templates:', err);
      setError(t('schedule.departmentTemplatesSaveFailed'));
    } finally {
      setDeptTemplatesSaving(false);
    }
  };

  const runDepartmentAutofill = async () => {
    if (!token || deptAutofillSelected.length === 0) return;
    if (isCurrentMonthLocked) {
      setError(t('schedule.departmentAutofillLocked'));
      return;
    }
    setDeptAutofillLoading(true);
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.WORK_SCHEDULE_DEPARTMENT_AUTOFILL), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          year: selectedMonth.getFullYear(),
          month: selectedMonth.getMonth() + 1,
          departments: deptAutofillSelected
        })
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Autofill failed');
      }
      const summary = data.data || {};
      setSuccess(t('schedule.departmentAutofillSuccess', {
        created: summary.created ?? 0,
        skippedExisting: summary.skippedExisting ?? 0,
        usersProcessed: summary.usersProcessed ?? 0
      }));
      setDeptAutofillDialogOpen(false);
      await fetchCompanyView();
    } catch (err) {
      console.error('Department autofill failed:', err);
      setError(err instanceof Error ? err.message : t('schedule.departmentAutofillFailed'));
    } finally {
      setDeptAutofillLoading(false);
    }
  };

  const updateDeptTemplateWeekDay = (
    templateIndex: number,
    dayOfWeek: number,
    field: keyof DepartmentWeekDay,
    value: string | null
  ) => {
    setDeptTemplates((prev) =>
      prev.map((tpl, idx) => {
        if (idx !== templateIndex) return tpl;
        return {
          ...tpl,
          week: tpl.week.map((day) =>
            day.dayOfWeek === dayOfWeek ? { ...day, [field]: value } : day
          )
        };
      })
    );
  };

  const addDepartmentTemplate = () => {
    const name = newDeptTemplateName.trim();
    if (!name) return;
    if (deptTemplates.some((t) => t.department.toLowerCase() === name.toLowerCase())) {
      setError(t('schedule.departmentTemplatesSaveFailed'));
      return;
    }
    const next: DepartmentScheduleTemplate = {
      department: name,
      enabled: true,
      week: buildDefaultDepartmentWeek()
    };
    setDeptTemplates((prev) => [...prev, next]);
    setEditingDeptTemplateIndex(deptTemplates.length);
    setNewDeptTemplateName('');
  };

  const removeDepartmentTemplate = (index: number) => {
    setDeptTemplates((prev) => prev.filter((_, i) => i !== index));
    setEditingDeptTemplateIndex((prev) => {
      if (prev === index) return Math.max(0, index - 1);
      if (prev > index) return prev - 1;
      return prev;
    });
  };

  const getStatusIcon = (status: string) => {
    const option = statusOptions.find(s => s.value === status);
    return option?.icon || <PersonIcon />;
  };

  const updateWeeklyTemplate = (dayOfWeek: number, field: keyof WeeklyTemplate, value: any) => {
    setWeeklyTemplate(prev => 
      prev.map(day => {
        if (day.dayOfWeek === dayOfWeek) {
          const updated = { ...day, [field]: value };
          
          // When status changes, adjust times appropriately
          if (field === 'status') {
            // For rest days and state holidays, clear all time fields
            if (value === 'rest_day' || value === 'state_holiday') {
              updated.startTime = undefined;
              updated.endTime = undefined;
              updated.breakStartTime = undefined;
              updated.breakEndTime = undefined;
            }

            // For Atostogos / Ligos atostogos / Mamadienis, clear break times (no breaks)
            if (isHoursOnlyStatus(value)) {
              updated.breakStartTime = undefined;
              updated.breakEndTime = undefined;
            }
          }
          
          return updated;
        }
        return day;
      })
    );
  };

  const applyWeeklyTemplate = async () => {
    if (!templateStartDate) return;

    // Validate hours for day_off/sick_leave/mothers_day before applying
    for (const day of weeklyTemplate) {
      if (isHoursOnlyStatus(day.status)) {
        const str = weeklyTemplateHoursStr[day.dayOfWeek] ?? '';
        if (str.trim() !== '') {
          const hours = parseHoursInput(str);
          if (hours === null) {
            setTemplateDialogNotice({ type: 'error', text: t('schedule.invalidHours', 'Įveskite tinkamą valandų skaičių (pvz. 7,5 arba 8)') + ` - ${dayNames[day.dayOfWeek]}` });
            return;
          }
        }
      }
    }

    for (const day of weeklyTemplate) {
      if (day.status === 'remote' && !(day.notes || '').trim()) {
        setTemplateDialogNotice({
          type: 'error',
          text: `${t('schedule.remoteAddressRequired', 'Reikia įvesti darbo nuotoliu adresą.')} - ${dayNames[day.dayOfWeek]}`
        });
        return;
      }
    }

    // Check if user has schedule management permission
    if (!hasPermission('schedule.manage')) {
      const startDate = new Date(templateStartDate);
      const year = startDate.getFullYear();
      const month = startDate.getMonth() + 1;
      
      let isMonthLocked = false;
      try {
        const monthStatusResponse = await fetch(`${getApiUrl(API_ENDPOINTS.MONTH_STATUS(year, month, user?.department))}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (monthStatusResponse.ok) {
          const monthStatusData = await monthStatusResponse.json();
          isMonthLocked = monthStatusData.data?.isLocked || false;
        } else {
          isMonthLocked = true; // Default to locked if we can't determine
        }
      } catch (error) {
        console.error('Error checking month lock status:', error);
        isMonthLocked = true; // Default to locked for safety
      }

      // If month is locked and user is not admin, submit per-day change requests instead of blocking
      if (isMonthLocked) {
        if (!templateReason.trim()) {
          setTemplateDialogNotice({ type: 'error', text: t('schedule.reasonRequiredWhenLocked', 'Prašome pateikti pakeitimo priežastį (reikalinga, kai mėnuo užblokuotas)') });
          return;
        }

        try {
          const currentUserId = user?.id;
          if (!currentUserId) {
            setError('Vartotojo sesija negaliojanti. Bandykite prisijungti iš naujo.');
            return;
          }
          let start = new Date(templateStartDate);
          let end: Date;
          if (templateApplyMode === 'single') {
            end = new Date(start);
            end.setDate(start.getDate() + 6);
          } else if (templateEndDate) {
            end = new Date(templateEndDate);
          } else {
            end = new Date(start);
            end.setDate(start.getDate() + (templateWeeksCount * 7) - 1);
          }

          const results: { date: string; ok: boolean; message?: string }[] = [];
          const current = new Date(start);
          while (current <= end) {
            const dow = current.getDay();
            const tpl = weeklyTemplate.find(t => t.dayOfWeek === dow);
            if (tpl) {
              let tplStartTime = tpl.startTime;
              let tplEndTime = tpl.endTime;
              if (isHoursOnlyStatus(tpl.status)) {
                const hours = parseHoursInput(weeklyTemplateHoursStr[tpl.dayOfWeek] || '');
                if (hours !== null && hours > 0) {
                  tplStartTime = '09:00';
                  tplEndTime = new Date(new Date('2000-01-01T09:00:00').getTime() + (hours * 60 * 60 * 1000)).toTimeString().slice(0, 5);
                } else {
                  tplStartTime = undefined;
                  tplEndTime = undefined;
                }
              }
              const reqDate = formatDateLocal(current);
              const requestData = {
                userId: currentUserId,
                date: reqDate,
                requestedStatus: tpl.status,
                requestedStartTime: tplStartTime,
                requestedEndTime: tplEndTime,
                requestedBreakStartTime: tpl.breakStartTime,
                requestedBreakEndTime: tpl.breakEndTime,
                requestedNotes: tpl.notes,
                reason: templateReason
              } as any;

              // Optimistic UI: mark cell as pending
              updateCompanyViewOptimistically(currentUserId, reqDate, {
                status: tpl.status,
                startTime: tplStartTime,
                endTime: tplEndTime,
                breakStartTime: tpl.breakStartTime,
                breakEndTime: tpl.breakEndTime,
                notes: tpl.notes,
                isApproved: false
              }, 'update');

              const resp = await fetch(`${getApiUrl(API_ENDPOINTS.WORK_SCHEDULE)}/change-request`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(requestData)
              });
              if (resp.ok) {
                results.push({ date: reqDate, ok: true });
              } else {
                const rawError = await resp.text();
                let message = rawError;
                try {
                  const parsed = JSON.parse(rawError);
                  message = parsed?.message || rawError;
                  if (String(message).includes('Remote work limit exceeded')) {
                    message = formatRemoteWeeklyLimitError(
                      typeof parsed?.maxAllowed === 'number' ? parsed.maxAllowed : undefined
                    );
                  } else if (String(message).includes('This month is closed. No changes or requests are allowed.')) {
                    message = t('schedule.monthLockedNoRequests');
                  }
                } catch (_) {
                  if (String(message).includes('Remote work limit exceeded')) {
                    message = formatRemoteWeeklyLimitError();
                  } else if (String(message).includes('This month is closed. No changes or requests are allowed.')) {
                    message = t('schedule.monthLockedNoRequests');
                  }
                }
                results.push({ date: reqDate, ok: false, message: String(message) });
              }
            }
            current.setDate(current.getDate() + 1);
          }

          const okCount = results.filter(r => r.ok).length;
          const total = results.length;
          const firstFailed = results.find(r => !r.ok);
          if (okCount === total) {
            setTemplateDialogNotice({ type: 'success', text: t('schedule.templateRequestsSubmitted', 'Pateikta savaitinio šablono prašymų: ') + total });
          } else if (okCount > 0) {
            const details = firstFailed?.message ? ` (${firstFailed.date}: ${firstFailed.message})` : '';
            setTemplateDialogNotice({ type: 'error', text: t('schedule.templateRequestsPartial', 'Dalies prašymų pateikti nepavyko. Sėkmingi: ') + okCount + '/' + total + details });
          } else {
            const details = firstFailed?.message ? ` (${firstFailed.date}: ${firstFailed.message})` : '';
            setTemplateDialogNotice({ type: 'error', text: t('schedule.templateRequestsFailed', 'Nepavyko pateikti prašymų') + details });
          }
          setTemplateDialogOpen(false);
          fetchChangeRequests();
          fetchCompanyView();
          return;
        } catch (e) {
          console.error('Failed to submit weekly template requests:', e);
          setTemplateDialogNotice({ type: 'error', text: t('schedule.templateRequestsFailed', 'Nepavyko pateikti prašymų') });
          return;
        }
      }
    }

    try {
      const scheduleData = [];
      let startDate = new Date(templateStartDate);
      let endDate: Date;

      if (templateApplyMode === 'single') {
        // Apply to single week
        endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + 6); // 7 days including start date
      } else {
        // Apply to multiple weeks
        if (templateEndDate) {
          endDate = new Date(templateEndDate);
        } else {
          // Calculate end date based on weeks count
          endDate = new Date(startDate);
          endDate.setDate(startDate.getDate() + (templateWeeksCount * 7) - 1);
        }
      }

      // Generate schedule entries for all weeks in the range
      const currentDate = new Date(startDate);
      while (currentDate <= endDate) {
        const dayOfWeek = currentDate.getDay();
        const template = weeklyTemplate.find(t => t.dayOfWeek === dayOfWeek);
        
        if (template) {
          let tplStartTime = template.startTime;
          let tplEndTime = template.endTime;
          if (isHoursOnlyStatus(template.status)) {
            const hours = parseHoursInput(weeklyTemplateHoursStr[template.dayOfWeek] || '');
            if (hours !== null && hours > 0) {
              tplStartTime = '09:00';
              tplEndTime = new Date(new Date('2000-01-01T09:00:00').getTime() + (hours * 60 * 60 * 1000)).toTimeString().slice(0, 5);
            } else {
              tplStartTime = undefined;
              tplEndTime = undefined;
            }
          }
          scheduleData.push({
            date: formatDateLocal(currentDate),
            status: template.status,
            startTime: tplStartTime,
            endTime: tplEndTime,
            breakStartTime: template.breakStartTime,
            breakEndTime: template.breakEndTime,
            notes: template.notes
          });
        }
        
        currentDate.setDate(currentDate.getDate() + 1);
      }

      // Send all schedule entries
      const requestBody = {
        schedules: scheduleData,
        userId: canApproveSchedules() && templateSelectedUser ? templateSelectedUser : undefined
      };

      const response = await fetch(`${getApiUrl(API_ENDPOINTS.WORK_SCHEDULE)}/bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        const weeksApplied = templateApplyMode === 'single' ? 1 : 
          templateEndDate ? 
            Math.ceil((new Date(templateEndDate).getTime() - new Date(templateStartDate).getTime()) / (7 * 24 * 60 * 60 * 1000)) :
            templateWeeksCount;
        setSuccess(`Weekly schedule template applied successfully to ${weeksApplied} week(s)`);
        setTemplateDialogOpen(false);
        fetchSchedules();
        fetchCompanyView();
      } else {
        setError('Failed to apply weekly template');
      }
    } catch (error) {
      setError('Failed to apply weekly template');
    }
  };

  // Helper function to check if user has pending requests for the selected month
  const getUserPendingRequests = (userId: string) => {
    const selectedYear = selectedMonth.getFullYear();
    const selectedMonthNumber = selectedMonth.getMonth() + 1;
    
    return pendingApprovals.filter(approval => {
      if (approval.userId !== userId) return false;
      
      // Parse the approval date and check if it matches the selected month
      const approvalDate = new Date(approval.date);
      const approvalYear = approvalDate.getFullYear();
      const approvalMonth = approvalDate.getMonth() + 1;
      
      return approvalYear === selectedYear && approvalMonth === selectedMonthNumber;
    });
  };

  const hasPendingRequests = (userId: string) => {
    return getUserPendingRequests(userId).length > 0;
  };

  const handleUserNameClick = (userId: string, userName: string) => {
    if (!hasPermission('schedule.manage')) return;
    
    const userPendingRequests = getUserPendingRequests(userId);
    console.log('🔍 DEBUG: User pending requests for', userName, ':', userPendingRequests);
    console.log('🔍 DEBUG: Selected month:', selectedMonth.getFullYear(), selectedMonth.getMonth() + 1);
    
    if (userPendingRequests.length > 0) {
      console.log('🔍 DEBUG: Opening bulk approval dialog with requests:', userPendingRequests.map(req => ({
        id: req.id,
        date: req.date,
        status: req.status
      })));
      
      setSelectedUserForBulkApproval({
        userId,
        userName,
        pendingRequests: userPendingRequests
      });
      // Initialize with all requests selected by default
      setSelectedRequestsForBulk(new Set(userPendingRequests.map(req => req.id)));
      setUserBulkApprovalDialogOpen(true);
    }
  };

  // Helper functions for managing selected requests
  const toggleRequestSelection = (requestId: string) => {
    setSelectedRequestsForBulk(prev => {
      const newSet = new Set(prev);
      if (newSet.has(requestId)) {
        newSet.delete(requestId);
      } else {
        newSet.add(requestId);
      }
      return newSet;
    });
  };

  const selectAllRequests = () => {
    if (selectedUserForBulkApproval) {
      setSelectedRequestsForBulk(new Set(selectedUserForBulkApproval.pendingRequests.map(req => req.id)));
    }
  };

  const selectNoRequests = () => {
    setSelectedRequestsForBulk(new Set());
  };

  const isRequestSelected = (requestId: string) => {
    return selectedRequestsForBulk.has(requestId);
  };

  const handleToggleHighlight = async (userId: string, date: string) => {
    try {
      // Check month lock status before allowing highlight toggle
      const requestDate = new Date(date);
      const year = requestDate.getFullYear();
      const month = requestDate.getMonth() + 1;
      const ownerDepartment = resolveScheduleOwnerDepartment(userId);
      
      let isMonthLocked = false;
      try {
        const monthStatusResponse = await fetch(`${getApiUrl(API_ENDPOINTS.MONTH_STATUS(year, month, ownerDepartment))}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (monthStatusResponse.ok) {
          const monthStatusData = await monthStatusResponse.json();
          isMonthLocked = monthStatusData.data?.isLocked || false;
        } else {
          isMonthLocked = true; // Default to locked if we can't determine
        }
      } catch (error) {
        console.error('Error checking month lock status:', error);
        isMonthLocked = true; // Default to locked for safety
      }

      if (isMonthLocked) {
        setError(t('schedule.monthLockedNoChanges'));
        return;
      }

      const cellKey = `${userId}-${date}`;
      const hasManualHighlight = cellHighlights.has(cellKey);
      
      if (hasManualHighlight) {
        // Remove highlight
        const response = await fetch(`${getApiUrl(API_ENDPOINTS.WORK_SCHEDULE_HIGHLIGHTS)}/${userId}/${date}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (response.ok) {
          await fetchCellHighlights();
          setSuccess('Cell highlight removed successfully');
        } else {
          setError('Failed to remove cell highlight');
        }
      } else {
        // Add highlight
        const response = await fetch(`${getApiUrl(API_ENDPOINTS.WORK_SCHEDULE_HIGHLIGHTS)}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            userId,
            date
          })
        });
        
        if (response.ok) {
          await fetchCellHighlights();
          setSuccess('Cell highlighted successfully');
        } else {
          setError('Failed to highlight cell');
        }
      }
    } catch (error) {
      console.error('Error toggling highlight:', error);
      setError('Failed to toggle cell highlight');
    }
  };

  const handleToggleBlueMark = async (userId: string, date: string) => {
    try {
      // Check month lock status before allowing blue mark toggle
      const requestDate = new Date(date);
      const year = requestDate.getFullYear();
      const month = requestDate.getMonth() + 1;
      const ownerDepartment = resolveScheduleOwnerDepartment(userId);
      
      let isMonthLocked = false;
      try {
        const monthStatusResponse = await fetch(`${getApiUrl(API_ENDPOINTS.MONTH_STATUS(year, month, ownerDepartment))}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (monthStatusResponse.ok) {
          const monthStatusData = await monthStatusResponse.json();
          isMonthLocked = monthStatusData.data?.isLocked || false;
        } else {
          isMonthLocked = true; // Default to locked if we can't determine
        }
      } catch (error) {
        console.error('Error checking month lock status:', error);
        isMonthLocked = true; // Default to locked for safety
      }

      if (isMonthLocked) {
        setError(t('schedule.monthLockedNoChanges'));
        return;
      }

      const cellKey = `${userId}-${date}`;
      const hasBlueMark = cellBlueMarks.has(cellKey);
      
      if (hasBlueMark) {
        // Remove blue mark
        const response = await fetch(`${getApiUrl(API_ENDPOINTS.WORK_SCHEDULE_BLUE_MARKS)}/${userId}/${date}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (response.ok) {
          await fetchCellBlueMarks();
          setSuccess('Mėlynas žymeklis pašalintas');
        } else {
          setError('Nepavyko pašalinti mėlyno žymeklio');
        }
      } else {
        // Add blue mark
        const response = await fetch(`${getApiUrl(API_ENDPOINTS.WORK_SCHEDULE_BLUE_MARKS)}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            userId,
            date
          })
        });
        
        if (response.ok) {
          await fetchCellBlueMarks();
          setSuccess('Ląstelė pažymėta mėlynai');
        } else {
          setError('Nepavyko pažymėti ląstelės');
        }
      }
    } catch (error) {
      console.error('Error toggling cell blue mark:', error);
      setError('Nepavyko perjungti mėlyno žymeklio');
    }
  };

  const populateCellEditForm = (
    employee: CompanyEmployee,
    date: string,
    isMonthLocked: boolean
  ) => {
    const cellDateNorm = normalizeScheduleDate(date) || date;
    const daySchedules = getSchedulesForDate(employee.schedules, cellDateNorm);
    const pendingForDate = pendingApprovalsRef.current.filter(
      (pa) => pa.userId === employee.userId && normalizeScheduleDate(pa.date) === cellDateNorm
    );
    const pendingPrimary =
      pendingForDate.find((p) => PAIRABLE_PRIMARY_STATUSES.includes(p.status)) ||
      pendingForDate.find((p) => p.status !== 'praktika');
    const pendingPraktika = pendingForDate.find((p) => p.status === 'praktika');

    const primary =
      (pendingPrimary
        ? ({
            ...pendingPrimary,
            date: cellDateNorm,
            isApproved: false
          } as WorkScheduleEntry)
        : undefined) ||
      getPrimaryScheduleForDate(daySchedules, cellDateNorm);
    const praktikaSeg =
      (pendingPraktika
        ? ({
            ...pendingPraktika,
            date: cellDateNorm,
            isApproved: false
          } as WorkScheduleEntry)
        : undefined) || getPraktikaScheduleForDate(daySchedules, cellDateNorm);

    // If only praktika exists, edit it as main status; otherwise prefer primary and optional praktika segment
    const source =
      primary ||
      praktikaSeg ||
      ({ status: 'working' } as Partial<WorkScheduleEntry>);
    const mainIsPraktikaOnly = !primary && !!praktikaSeg;

    setEditingCellData({
      userId: employee.userId,
      userName: employee.name,
      date: date,
      currentStatus: (source.status as WorkScheduleEntry['status']) || 'working',
      currentStartTime: source.startTime,
      currentEndTime: source.endTime,
      currentBreakStartTime: source.breakStartTime,
      currentBreakEndTime: source.breakEndTime,
      currentNotes: source.notes
    });

    setCellEditStatus((source.status as WorkScheduleEntry['status']) || 'working');
    setCellEditStartTime(source.startTime ? new Date(`2000-01-01T${source.startTime}`) : null);
    setCellEditEndTime(source.endTime ? new Date(`2000-01-01T${source.endTime}`) : null);
    setCellEditBreakStartTime(
      source.breakStartTime ? new Date(`2000-01-01T${source.breakStartTime}`) : null
    );
    setCellEditBreakEndTime(
      source.breakEndTime ? new Date(`2000-01-01T${source.breakEndTime}`) : null
    );
    setCellEditNotes(source.notes || '');
    setCellEditMonthLocked(isMonthLocked);
    setCellEditDialogNotice(null);

    if (!mainIsPraktikaOnly && praktikaSeg && primary) {
      setCellEditPraktikaEnabled(true);
      setCellEditPraktikaId(praktikaSeg.id || null);
      setCellEditPraktikaStartTime(
        praktikaSeg.startTime ? new Date(`2000-01-01T${praktikaSeg.startTime}`) : null
      );
      setCellEditPraktikaEndTime(
        praktikaSeg.endTime ? new Date(`2000-01-01T${praktikaSeg.endTime}`) : null
      );
      setCellEditPraktikaBreakStartTime(
        praktikaSeg.breakStartTime ? new Date(`2000-01-01T${praktikaSeg.breakStartTime}`) : null
      );
      setCellEditPraktikaBreakEndTime(
        praktikaSeg.breakEndTime ? new Date(`2000-01-01T${praktikaSeg.breakEndTime}`) : null
      );
    } else {
      setCellEditPraktikaEnabled(false);
      setCellEditPraktikaId(praktikaSeg && !primary ? praktikaSeg.id : null);
      setCellEditPraktikaStartTime(null);
      setCellEditPraktikaEndTime(null);
      setCellEditPraktikaBreakStartTime(null);
      setCellEditPraktikaBreakEndTime(null);
    }
  };

  // Cell editing functions
  const handleCellClick = async (employee: CompanyEmployee, date: string, schedule: WorkScheduleEntry | undefined) => {
    console.log('=== handleCellClick called ===');
    console.log('Cell clicked:', { employee, date, schedule });
    console.log('Date comparison - cell date:', date, 'schedule date:', schedule?.date, 'match:', date === schedule?.date);

    if (employee.userId !== user?.id && !canEditOtherUsersCells()) {
      return;
    }

    if (canApproveSchedules()) {
      // Users with approval permission can edit directly
      console.log('Opening cell edit dialog for:', employee.name, 'on', date);
      
      // Check month lock status
      const requestDate = new Date(date);
      const year = requestDate.getFullYear();
      const month = requestDate.getMonth() + 1;
      const ownerDepartment = resolveScheduleOwnerDepartment(employee.userId, employee.department);
      
      let isMonthLocked = false;
      try {
        const monthStatusResponse = await fetch(`${getApiUrl(API_ENDPOINTS.MONTH_STATUS(year, month, ownerDepartment))}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (monthStatusResponse.ok) {
          const monthStatusData = await monthStatusResponse.json();
          isMonthLocked = monthStatusData.data?.isLocked || false;
        }
      } catch (error) {
        console.error('Error checking month lock status:', error);
      }
      
      populateCellEditForm(employee, date, isMonthLocked);

      console.log('About to set cellEditDialogOpen to true');
      setCellEditDialogOpen(true);
      console.log('cellEditDialogOpen set to true');
    } else {
      // Regular users can only modify their own schedule
      if (employee.userId !== user?.id) {
        console.log('Non-admin user cannot modify other users\' schedules');
        return; // Don't open any dialog for other users' cells
      }
      
      // Check month lock status for the requested date
      const requestDate = new Date(date);
      const year = requestDate.getFullYear();
      const month = requestDate.getMonth() + 1;
      const ownerDepartment = resolveScheduleOwnerDepartment(employee.userId, employee.department);
      let isMonthLocked = false;
      try {
        const monthStatusResponse = await fetch(`${getApiUrl(API_ENDPOINTS.MONTH_STATUS(year, month, ownerDepartment))}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (monthStatusResponse.ok) {
          const monthStatusData = await monthStatusResponse.json();
          isMonthLocked = monthStatusData.data?.isLocked || false;
        } else {
          isMonthLocked = true; // Default to locked if we can't determine
        }
      } catch (error) {
        console.error('Error checking month lock status:', error);
        isMonthLocked = true; // Default to locked for safety
      }
      
      if (isMonthLocked) {
        // Month is locked - no changes allowed
        console.log('Month is locked - no changes allowed');
        setError(t('schedule.monthClosedNoChanges', 'This month is closed. No changes or requests are allowed.'));
        return;
      } else {
        // Month is unlocked - allow direct editing (including clearing)
        console.log('Month is unlocked - opening cell edit dialog for own schedule on:', date);
        populateCellEditForm(employee, date, false);

        console.log('About to set cellEditDialogOpen to true');
        setCellEditDialogOpen(true);
        console.log('cellEditDialogOpen set to true');
      }
    }
  };

  const handleCellEditSubmit = async (
    forcedStatus?: WorkScheduleEntry['status'],
    forcedNotes?: string,
    forcedAuditSource?: string
  ) => {
    if (!editingCellData) return;
    const statusToSave = forcedStatus ?? cellEditStatus;
    const notesToSave = forcedNotes ?? cellEditNotes;

    if (statusToSave === 'remote' && !notesToSave.trim()) {
      setCellEditDialogNotice({
        type: 'error',
        text: t('schedule.remoteAddressRequired', 'Reikia įvesti darbo nuotoliu adresą.')
      });
      return;
    }

    // Sync hours string to times on save (day_off/sick_leave/mothers_day)
    let effectiveStartTime = cellEditStartTime;
    let effectiveEndTime = cellEditEndTime;
    if (isHoursOnlyStatus(statusToSave)) {
      const hours = parseHoursInput(cellEditHoursStr);
      if (hours !== null && hours > 0) {
        effectiveStartTime = new Date('2000-01-01T09:00:00');
        effectiveEndTime = new Date(effectiveStartTime.getTime() + (hours * 60 * 60 * 1000));
      } else if (cellEditHoursStr.trim() !== '') {
        setError(t('schedule.invalidHours', 'Įveskite tinkamą valandų skaičių (pvz. 7,5 arba 8)'));
        return;
      } else {
        effectiveStartTime = createDefaultTime();
        effectiveEndTime = createDefaultTime();
      }
    }

    const cellBreakValidationError = getBreakOutOfShiftMessage(
      statusToSave,
      effectiveStartTime,
      effectiveEndTime,
      cellEditBreakStartTime,
      cellEditBreakEndTime
    );
    if (cellBreakValidationError) {
      setCellEditDialogNotice({ type: 'error', text: cellBreakValidationError });
      return;
    }

    const savePraktikaSegment =
      cellEditPraktikaEnabled &&
      PAIRABLE_PRIMARY_STATUSES.includes(statusToSave) &&
      !!cellEditPraktikaStartTime &&
      !!cellEditPraktikaEndTime;

    if (savePraktikaSegment) {
      const pStart = cellEditPraktikaStartTime!.toTimeString().slice(0, 5);
      const pEnd = cellEditPraktikaEndTime!.toTimeString().slice(0, 5);
      const primaryStart = effectiveStartTime?.toTimeString().slice(0, 5);
      const primaryEnd = effectiveEndTime?.toTimeString().slice(0, 5);
      if (primaryStart && primaryEnd) {
        const ps = primaryStart.slice(0, 5);
        const pe = primaryEnd.slice(0, 5);
        const toMin = (t: string) => {
          const [h, m] = t.split(':').map(Number);
          return h * 60 + m;
        };
        if (toMin(ps) < toMin(pEnd) && toMin(pStart) < toMin(pe)) {
          setCellEditDialogNotice({
            type: 'error',
            text: t(
              'schedule.praktikaOverlapError',
              'Praktika ir Dirba/Nuotoliu valandos negali persidengti.'
            )
          });
          return;
        }
      }
      const praktikaBreakErr = getBreakOutOfShiftMessage(
        'praktika',
        cellEditPraktikaStartTime,
        cellEditPraktikaEndTime,
        cellEditPraktikaBreakStartTime,
        cellEditPraktikaBreakEndTime
      );
      if (praktikaBreakErr) {
        setCellEditDialogNotice({ type: 'error', text: praktikaBreakErr });
        return;
      }
    }

    // Check if user has schedule management permission
    if (!hasPermission('schedule.manage')) {
      const requestDate = new Date(editingCellData.date);
      const year = requestDate.getFullYear();
      const month = requestDate.getMonth() + 1;
      const ownerDepartment = resolveScheduleOwnerDepartment(editingCellData.userId);
      
      let isMonthLocked = false;
      try {
        const monthStatusResponse = await fetch(`${getApiUrl(API_ENDPOINTS.MONTH_STATUS(year, month, ownerDepartment))}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (monthStatusResponse.ok) {
          const monthStatusData = await monthStatusResponse.json();
          isMonthLocked = monthStatusData.data?.isLocked || false;
        } else {
          isMonthLocked = true; // Default to locked if we can't determine
        }
      } catch (error) {
        console.error('Error checking month lock status:', error);
        isMonthLocked = true; // Default to locked for safety
      }

      // If month is locked and user is not admin, they should not be able to edit directly
      if (isMonthLocked) {
        setError(t('schedule.monthLockedNoChanges'));
        setCellEditDialogOpen(false);
        setEditingCellData(null);
        return;
      }
    }

    // Store original schedules for rollback
    const originalDaySchedules =
      companyView
        .find((emp) => emp.userId === editingCellData.userId)
        ?.schedules.filter(
          (s) => normalizeScheduleDate(s.date) === normalizeScheduleDate(editingCellData.date)
        ) || [];
    const originalSchedule =
      originalDaySchedules.find((s) => s.status === statusToSave) || originalDaySchedules[0];

    // Clear break times for vacation statuses (they don't have breaks)
    const shouldHaveBreak = statusToSave === 'working' || statusToSave === 'remote' || statusToSave === 'praktika';
    // Prepare optimistic update data
    const optimisticSchedule = {
      status: statusToSave,
      startTime: effectiveStartTime ? effectiveStartTime.toTimeString().slice(0, 5) : undefined,
      endTime: effectiveEndTime ? effectiveEndTime.toTimeString().slice(0, 5) : undefined,
      breakStartTime: shouldHaveBreak ? (cellEditBreakStartTime ? cellEditBreakStartTime.toTimeString().slice(0, 5) : undefined) : undefined,
      breakEndTime: shouldHaveBreak ? (cellEditBreakEndTime ? cellEditBreakEndTime.toTimeString().slice(0, 5) : undefined) : undefined,
      notes: notesToSave,
      isApproved: canApproveSchedules() // Only users with approval permission auto-approve
    };

    // ✅ OPTIMISTIC UPDATE: Update UI immediately
    updateCompanyViewOptimistically(
      editingCellData.userId,
      editingCellData.date,
      optimisticSchedule,
      originalSchedule ? 'update' : 'create'
    );

    if (savePraktikaSegment) {
      updateCompanyViewOptimistically(
        editingCellData.userId,
        editingCellData.date,
        {
          status: 'praktika',
          startTime: cellEditPraktikaStartTime!.toTimeString().slice(0, 5),
          endTime: cellEditPraktikaEndTime!.toTimeString().slice(0, 5),
          breakStartTime: cellEditPraktikaBreakStartTime
            ? cellEditPraktikaBreakStartTime.toTimeString().slice(0, 5)
            : undefined,
          breakEndTime: cellEditPraktikaBreakEndTime
            ? cellEditPraktikaBreakEndTime.toTimeString().slice(0, 5)
            : undefined,
          isApproved: canApproveSchedules()
        },
        'update'
      );
    } else if (
      cellEditPraktikaId &&
      PAIRABLE_PRIMARY_STATUSES.includes(statusToSave) &&
      !cellEditPraktikaEnabled
    ) {
      updateCompanyViewOptimistically(
        editingCellData.userId,
        editingCellData.date,
        { status: 'praktika' },
        'delete'
      );
    }

    try {
      console.log('=== handleCellEditSubmit called ===');
      console.log('Editing cell data:', editingCellData);

      // Clear break times for vacation statuses (they don't have breaks)
      const shouldHaveBreak = statusToSave === 'working' || statusToSave === 'remote' || statusToSave === 'praktika';
      const scheduleData = {
        userId: editingCellData.userId,
        date: editingCellData.date,
        status: statusToSave,
        startTime: effectiveStartTime ? effectiveStartTime.toTimeString().slice(0, 5) : undefined,
        endTime: effectiveEndTime ? effectiveEndTime.toTimeString().slice(0, 5) : undefined,
        breakStartTime: shouldHaveBreak ? (cellEditBreakStartTime ? cellEditBreakStartTime.toTimeString().slice(0, 5) : undefined) : undefined,
        breakEndTime: shouldHaveBreak ? (cellEditBreakEndTime ? cellEditBreakEndTime.toTimeString().slice(0, 5) : undefined) : undefined,
        notes: notesToSave,
        auditSource: forcedAuditSource
      };

      console.log('Submitting schedule data:', scheduleData);

      const response = await fetch(`${getApiUrl(API_ENDPOINTS.WORK_SCHEDULE)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(scheduleData)
      });

      console.log('Response status:', response.status);

      if (response.ok) {
        try {
          const result = await response.json();
          console.log('Schedule update response:', result);
          
          // ✅ Update with real server data (replace temp ID with real ID)
          if (result.data) {
            updateCompanyViewOptimistically(
              editingCellData.userId,
              editingCellData.date,
              {
                ...result.data,
                id: result.data.id // Use real server ID
              },
              'update'
            );
          }

          // Save or remove optional same-day Praktika segment
          if (savePraktikaSegment) {
            const praktikaPayload = {
              userId: editingCellData.userId,
              date: editingCellData.date,
              status: 'praktika' as const,
              startTime: cellEditPraktikaStartTime!.toTimeString().slice(0, 5),
              endTime: cellEditPraktikaEndTime!.toTimeString().slice(0, 5),
              breakStartTime: cellEditPraktikaBreakStartTime
                ? cellEditPraktikaBreakStartTime.toTimeString().slice(0, 5)
                : undefined,
              breakEndTime: cellEditPraktikaBreakEndTime
                ? cellEditPraktikaBreakEndTime.toTimeString().slice(0, 5)
                : undefined,
              notes: undefined,
              auditSource: forcedAuditSource
            };
            const praktikaRes = await fetch(`${getApiUrl(API_ENDPOINTS.WORK_SCHEDULE)}`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${localStorage.getItem('token')}`
              },
              body: JSON.stringify(praktikaPayload)
            });
            if (praktikaRes.ok) {
              const praktikaResult = await praktikaRes.json();
              if (praktikaResult.data) {
                updateCompanyViewOptimistically(
                  editingCellData.userId,
                  editingCellData.date,
                  { ...praktikaResult.data, id: praktikaResult.data.id },
                  'update'
                );
              }
            } else {
              const errText = await praktikaRes.text();
              let msg = t('schedule.failedToUpdate');
              try {
                msg = JSON.parse(errText).message || msg;
              } catch {
                /* ignore */
              }
              setCellEditDialogNotice({ type: 'error', text: msg });
              return;
            }
          } else if (
            cellEditPraktikaId &&
            !String(cellEditPraktikaId).startsWith('temp-') &&
            PAIRABLE_PRIMARY_STATUSES.includes(statusToSave) &&
            !cellEditPraktikaEnabled
          ) {
            await fetch(`${getApiUrl(API_ENDPOINTS.WORK_SCHEDULE)}/${cellEditPraktikaId}`, {
              method: 'DELETE',
              headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            updateCompanyViewOptimistically(
              editingCellData.userId,
              editingCellData.date,
              { status: 'praktika' },
              'delete'
            );
          }
          
          // ✅ Confirm optimistic update
          confirmOptimisticUpdate(editingCellData.userId, editingCellData.date);
          
          const remotePendingForApproval =
            result.data &&
            result.data.status === 'remote' &&
            result.data.isApproved === false;
          setSuccess(
            remotePendingForApproval
              ? t('schedule.remoteSubmittedForApproval', 'Darbo nuotoliu įrašas pateiktas patvirtinimui (Laukia).')
              : 'Schedule updated successfully'
          );
          // When save went to pending (Laukia), show it immediately: add to pendingApprovals then refetch to sync
          if (result.data && result.data.isApproved === false) {
            const pendingEntry: ScheduleChangeRequest = {
              id: result.data.id,
              userId: result.data.userId,
              date: result.data.date,
              status: result.data.status,
              startTime: result.data.startTime,
              endTime: result.data.endTime,
              breakStartTime: result.data.breakStartTime,
              breakEndTime: result.data.breakEndTime,
              notes: result.data.notes,
              isApproved: false,
              previousScheduleBeforeRequest:
                result.data.previousScheduleBeforeRequest ?? null
            };
            setPendingApprovals(prev => [
              ...prev.filter(
                (p) =>
                  !(
                    p.userId === pendingEntry.userId &&
                    p.date === pendingEntry.date &&
                    p.status === pendingEntry.status
                  )
              ),
              pendingEntry
            ]);
            setCompanyView((prev) => mergePendingIntoCompanyView(prev, [pendingEntry]));
            fetchPendingApprovals();
          }
        } catch (parseOrUpdateError) {
          console.error('Error parsing response or updating view:', parseOrUpdateError);
          setError(t('schedule.failedToUpdate'));
        } finally {
          // Always close the dialog when we got a successful HTTP response
          setCellEditDialogOpen(false);
          setEditingCellData(null);
          setCellEditPraktikaEnabled(false);
          setCellEditPraktikaId(null);
        }
        
        // ✅ NO MORE FULL REFRESH - only update change requests if needed
        if (canApproveSchedules()) {
          fetchChangeRequests();
        }
      } else {
        // ❌ ROLLBACK: Revert optimistic update on error
        rollbackCompanyViewUpdate(editingCellData.userId, editingCellData.date, originalSchedule);
        
        const errorText = await response.text();
        console.error('Error response:', errorText);
        
        // Try to parse error message and translate it
        let errorMessage = t('schedule.failedToUpdate');
        try {
          const errorJson = JSON.parse(errorText);
          const backendMessage = errorJson.message || '';
          const backendDetail =
            typeof errorJson.detail === 'string' && errorJson.detail.trim() ? ` (${errorJson.detail.trim()})` : '';
          const combinedBackendText = `${backendMessage} ${backendDetail} ${errorText}`.toLowerCase();

          // Map backend error messages to translation keys
          if (backendMessage.includes('This month is locked. No changes are allowed')) {
            errorMessage = t('schedule.monthLockedNoChanges');
          } else if (combinedBackendText.includes('remote work limit exceeded')) {
            errorMessage = formatRemoteWeeklyLimitError(
              typeof errorJson.maxAllowed === 'number' ? errorJson.maxAllowed : undefined
            );
          } else if (backendMessage.includes('This month is closed. No changes or requests are allowed') || 
                     backendMessage.includes('This month is locked. No changes or requests are allowed')) {
            errorMessage = t('schedule.monthLockedNoRequests');
          } else {
            errorMessage = `${t('schedule.failedToUpdate')}: ${backendMessage}${backendDetail}`;
          }
        } catch (e) {
          // If parsing fails, use default message
          errorMessage = t('schedule.failedToUpdate');
        }
        
        setError(errorMessage);
        setCellEditDialogOpen(false);
        setEditingCellData(null);
      }
    } catch (error) {
      // ❌ ROLLBACK: Revert optimistic update on error
      rollbackCompanyViewUpdate(editingCellData.userId, editingCellData.date, originalSchedule);
      
      console.error('Exception in handleCellEditSubmit:', error);
      setError(t('schedule.failedToUpdate'));
      setCellEditDialogOpen(false);
      setEditingCellData(null);
    }
  };

  const handleCancelRemoteWork = async () => {
    if (!editingCellData || cellEditStatus !== 'remote' || !hasPermission('schedule.manage')) {
      return;
    }

    const confirmed = window.confirm(t('schedule.confirmCancelRemoteWork'));
    if (!confirmed) {
      return;
    }

    const cancellationNote = 'Atšauktas darbas nuotoliu';
    const currentNotes = (cellEditNotes || '').trim();
    const nextNotes = currentNotes.includes(cancellationNote)
      ? currentNotes
      : (currentNotes ? `${currentNotes}\n${cancellationNote}` : cancellationNote);

    setCellEditStatus('working');
    setCellEditNotes(nextNotes);
    await handleCellEditSubmit('working', nextNotes, 'admin_shortcut_cancel_remote_work');
  };

  const handleCellEditCancel = () => {
    setCellEditDialogOpen(false);
    setEditingCellData(null);
    setCellEditStatus('working');
    setCellEditStartTime(createDefaultTime());
    setCellEditEndTime(createDefaultTime());
    setCellEditBreakStartTime(createDefaultTime());
    setCellEditBreakEndTime(createDefaultTime());
    setCellEditNotes('');
    setCellEditDialogNotice(null);
    setCellEditPraktikaEnabled(false);
    setCellEditPraktikaId(null);
    setCellEditPraktikaStartTime(null);
    setCellEditPraktikaEndTime(null);
    setCellEditPraktikaBreakStartTime(null);
    setCellEditPraktikaBreakEndTime(null);
  };

  const handleCellClear = async () => {
    if (!editingCellData) return;

    // Check if user has schedule management permission
    if (!hasPermission('schedule.manage')) {
      const requestDate = new Date(editingCellData.date);
      const year = requestDate.getFullYear();
      const month = requestDate.getMonth() + 1;
      const ownerDepartment = resolveScheduleOwnerDepartment(editingCellData.userId);
      
      let isMonthLocked = false;
      try {
        const monthStatusResponse = await fetch(`${getApiUrl(API_ENDPOINTS.MONTH_STATUS(year, month, ownerDepartment))}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (monthStatusResponse.ok) {
          const monthStatusData = await monthStatusResponse.json();
          isMonthLocked = monthStatusData.data?.isLocked || false;
        } else {
          isMonthLocked = true; // Default to locked if we can't determine
        }
      } catch (error) {
        console.error('Error checking month lock status:', error);
        isMonthLocked = true; // Default to locked for safety
      }

      // If month is locked and user is not admin, they should not be able to clear directly
      if (isMonthLocked) {
        setError(t('schedule.monthLockedNoChanges'));
        setCellEditDialogOpen(false);
        setEditingCellData(null);
        return;
      }
    }

    // Store original schedule for rollback
    const originalSchedule = companyView
      .find(emp => emp.userId === editingCellData.userId)
      ?.schedules.find(s => s.date === editingCellData.date);

    // ✅ OPTIMISTIC UPDATE: Remove from UI immediately
    updateCompanyViewOptimistically(
      editingCellData.userId,
      editingCellData.date,
      {},
      'delete'
    );

    try {
      console.log('=== Clearing cell ===');
      console.log('Clearing cell for:', editingCellData.userName, 'on', editingCellData.date);

      // First, find the schedule entry to get its ID
      const scheduleResponse = await fetch(`${getApiUrl(API_ENDPOINTS.WORK_SCHEDULE)}?userId=${editingCellData.userId}&startDate=${editingCellData.date}&endDate=${editingCellData.date}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!scheduleResponse.ok) {
        // ❌ ROLLBACK: Revert optimistic update on error
        rollbackCompanyViewUpdate(editingCellData.userId, editingCellData.date, originalSchedule);
        setError('Failed to find schedule entry');
        return;
      }

      const scheduleData = await scheduleResponse.json();
      const scheduleEntries: { id: string; status?: string }[] = Array.isArray(scheduleData.data)
        ? scheduleData.data
        : [];

      if (scheduleEntries.length === 0) {
        // No schedule entry exists, so nothing to clear
        setSuccess('Cell is already empty');
        setCellEditDialogOpen(false);
        setEditingCellData(null);
        return;
      }

      // Delete all segments for this date (e.g. Dirba + Praktika)
      let allDeleted = true;
      for (const scheduleEntry of scheduleEntries) {
        const deleteResponse = await fetch(
          `${getApiUrl(API_ENDPOINTS.WORK_SCHEDULE)}/${scheduleEntry.id}`,
          {
            method: 'DELETE',
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        if (!deleteResponse.ok) {
          allDeleted = false;
          const errorData = await deleteResponse.json().catch(() => ({}));
          rollbackCompanyViewUpdate(editingCellData.userId, editingCellData.date, originalSchedule);
          setError(errorData.message || 'Failed to clear cell');
          break;
        }
      }

      if (allDeleted) {
        confirmOptimisticUpdate(editingCellData.userId, editingCellData.date);
        setSuccess('Cell cleared successfully');
        setCellEditDialogOpen(false);
        setEditingCellData(null);
        if (canApproveSchedules()) {
          fetchChangeRequests();
        }
      }
    } catch (error) {
      // ❌ ROLLBACK: Revert optimistic update on error
      rollbackCompanyViewUpdate(editingCellData.userId, editingCellData.date, originalSchedule);
      
      console.error('Error clearing cell:', error);
      setError('Failed to clear cell');
    }
  };

  const handleChangeRequestSubmit = async () => {
    if (!requestingForUser || !requestingForDate) return;

    // Sync hours string to times on save (day_off/sick_leave/mothers_day)
    let effectiveStartTime = requestedStartTime;
    let effectiveEndTime = requestedEndTime;
    if (isHoursOnlyStatus(requestedStatus)) {
      const hours = parseHoursInput(requestedHoursStr);
      if (hours !== null && hours > 0) {
        effectiveStartTime = new Date('2000-01-01T09:00:00');
        effectiveEndTime = new Date(effectiveStartTime.getTime() + (hours * 60 * 60 * 1000));
      } else if (requestedHoursStr.trim() !== '') {
        setRequestDialogNotice({
          type: 'error',
          text: t('schedule.invalidHours', 'Įveskite tinkamą valandų skaičių (pvz. 7,5 arba 8)')
        });
        return;
      } else {
        effectiveStartTime = createDefaultTime();
        effectiveEndTime = createDefaultTime();
      }
    }

    const requestBreakValidationError = getBreakOutOfShiftMessage(
      requestedStatus,
      effectiveStartTime,
      effectiveEndTime,
      requestedBreakStartTime,
      requestedBreakEndTime
    );
    if (requestBreakValidationError) {
      setRequestDialogNotice({ type: 'error', text: requestBreakValidationError });
      return;
    }

    // Store original schedule for rollback
    const originalSchedule = companyView
      .find(emp => emp.userId === requestingForUser)
      ?.schedules.find(s => s.date === requestingForDate);

    // Generate temporary ID for optimistic update
    const tempId = `temp-${Date.now()}`;

    try {
      // No need to check month lock here - locked months are blocked in handleCellClick
      // and the backend will also reject any attempts to change locked months

      // ✅ OPTIMISTIC UPDATE: Add to pending approvals (LAUKIA); keep approved hours in grid
      const optimisticChangeRequest: ScheduleChangeRequest = {
        id: tempId, // Temporary ID
        userId: requestingForUser,
        date: requestingForDate,
        status: requestedStatus,
        startTime: effectiveStartTime ? effectiveStartTime.toTimeString().slice(0, 5) : undefined,
        endTime: effectiveEndTime ? effectiveEndTime.toTimeString().slice(0, 5) : undefined,
        breakStartTime: requestedBreakStartTime ? requestedBreakStartTime.toTimeString().slice(0, 5) : undefined,
        breakEndTime: requestedBreakEndTime ? requestedBreakEndTime.toTimeString().slice(0, 5) : undefined,
        notes: requestedNotes,
        reason: requestedReason,
        isApproved: false,
        approvedBy: undefined,
        approvedAt: undefined,
        adminNotes: undefined,
        previousScheduleBeforeRequest: originalSchedule
          ? {
              status: originalSchedule.status,
              startTime: originalSchedule.startTime ?? null,
              endTime: originalSchedule.endTime ?? null,
              breakStartTime: originalSchedule.breakStartTime ?? null,
              breakEndTime: originalSchedule.breakEndTime ?? null,
              notes: originalSchedule.notes ?? null
            }
          : null
      };
      
      setPendingApprovals(prev => [...prev, optimisticChangeRequest]);
      setCompanyView((prev) => mergePendingIntoCompanyView(prev, [optimisticChangeRequest]));

      const requestData = {
        userId: requestingForUser,
        date: requestingForDate,
        requestedStatus: requestedStatus,
        requestedStartTime: effectiveStartTime ? effectiveStartTime.toTimeString().slice(0, 5) : undefined,
        requestedEndTime: effectiveEndTime ? effectiveEndTime.toTimeString().slice(0, 5) : undefined,
        requestedBreakStartTime: requestedBreakStartTime ? requestedBreakStartTime.toTimeString().slice(0, 5) : undefined,
        requestedBreakEndTime: requestedBreakEndTime ? requestedBreakEndTime.toTimeString().slice(0, 5) : undefined,
        requestedNotes: requestedNotes,
        reason: requestedReason || undefined // Make reason optional if month is unlocked
      };

      // Debug: Log what we're sending
      console.log('=== Change Request Debug ===');
      console.log('Request data being sent:', requestData);
      console.log('requestingForUser:', requestingForUser);
      console.log('requestingForDate:', requestingForDate);
      console.log('requestedStatus:', requestedStatus);
      console.log('requestReason:', requestedReason);

      // Prevent requesting the same status as current when month is locked
      try {
        const employee = companyView.find(e => e.userId === requestingForUser);
        const existing = employee?.schedules.find(s => s.date === requestingForDate);
        if (existing && existing.status === requestedStatus) {
          setRequestDialogNotice({ type: 'error', text: t('schedule.sameStatusRequestNotAllowed', 'Negalite pateikti prašymo su tuo pačiu statusu.') });
          return;
        }
      } catch (_) {}

      const response = await fetch(`${getApiUrl(API_ENDPOINTS.WORK_SCHEDULE)}/change-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(requestData)
      });

      if (response.ok) {
        const result = await response.json();
        
        // ✅ Update with real server data if available (keep approved hours; LAUKIA from pending)
        if (result.data) {
          const serverPending: ScheduleChangeRequest = {
            ...result.data,
            id: result.data.id,
            isApproved: false
          };
          setPendingApprovals(prev => prev.map(req => 
            req.id === tempId && req.userId === requestingForUser && req.date === requestingForDate
              ? serverPending
              : req
          ));
          setCompanyView((prev) => mergePendingIntoCompanyView(prev, [serverPending]));
        }
        
        // ✅ Confirm optimistic update
        confirmOptimisticUpdate(requestingForUser, requestingForDate);
        
        setSuccess('Change request submitted successfully');
        setRequestDialogOpen(false);
        setRequestedReason('');
        setRequestedStatus('working');
        setRequestedStartTime(null);
        setRequestedEndTime(null);
        setRequestedBreakStartTime(null);
        setRequestedBreakEndTime(null);
        setRequestedNotes('');
        
        // ✅ NO MORE FULL REFRESH - only update change requests list
        fetchChangeRequests();
      } else {
        // ❌ ROLLBACK: Revert optimistic update on error
        rollbackCompanyViewUpdate(requestingForUser, requestingForDate, originalSchedule);
        
        // ❌ ROLLBACK: Remove temporary change request from pending approvals
        setPendingApprovals(prev => prev.filter(req => req.id !== tempId));
        
        // Debug: Log the error response
        const errorText = await response.text();
        console.error('Change request failed:', response.status, errorText);

        let parsedMessage = '';
        let maxAllowed: number | undefined;
        try {
          const errorJson = JSON.parse(errorText);
          parsedMessage = String(errorJson?.message || '');
          if (typeof errorJson?.maxAllowed === 'number') {
            maxAllowed = errorJson.maxAllowed;
          }
        } catch (_) {
          parsedMessage = errorText;
        }

        let dialogMessage = parsedMessage || t('schedule.changeRequestSubmitFailed', 'Nepavyko pateikti pakeitimo prašymo');
        if (parsedMessage.includes('Remote work limit exceeded')) {
          dialogMessage = formatRemoteWeeklyLimitError(maxAllowed);
        } else if (parsedMessage.includes('This month is closed. No changes or requests are allowed.')) {
          dialogMessage = t('schedule.monthLockedNoRequests');
        }

        setRequestDialogNotice({ type: 'error', text: dialogMessage });
      }
    } catch (error) {
      // ❌ ROLLBACK: Revert optimistic update on error
      rollbackCompanyViewUpdate(requestingForUser, requestingForDate, originalSchedule);
      
      // ❌ ROLLBACK: Remove temporary change request from pending approvals
      setPendingApprovals(prev => prev.filter(req => req.id !== tempId));
      
      console.error('Change request error:', error);
      setRequestDialogNotice({
        type: 'error',
        text: t('schedule.changeRequestSubmitFailed', 'Nepavyko pateikti pakeitimo prašymo')
      });
    }
  };

  const handleChangeRequestCancel = () => {
    setRequestDialogOpen(false);
    setRequestedReason('');
    setRequestedStatus('working');
    setRequestedStartTime(null);
    setRequestedEndTime(null);
    setRequestedBreakStartTime(null);
    setRequestedBreakEndTime(null);
    setRequestedNotes('');
    setRequestDialogNotice(null);
  };

  // Clipboard helpers for copy/paste across dialogs
  const SCHEDULE_CLIPBOARD_KEY = 'workScheduleClipboard';

  const timeToString = (value: Date | null): string | null => {
    if (!value) return null;
    return value.toTimeString().slice(0, 5);
  };

  const stringToTime = (value?: string | null): Date | null => {
    if (!value) return null;
    try {
      return new Date(`2000-01-01T${value}`);
    } catch (_) {
      return null;
    }
  };

  const setScheduleClipboard = (data: {
    status: WorkScheduleEntry['status'];
    startTime?: string | null;
    endTime?: string | null;
    breakStartTime?: string | null;
    breakEndTime?: string | null;
    notes?: string | null;
  }) => {
    try {
      localStorage.setItem(SCHEDULE_CLIPBOARD_KEY, JSON.stringify(data));
      setSuccess('Nukopijuota');
    } catch (error) {
      console.error('Failed to copy schedule data:', error);
      setError('Nepavyko nukopijuoti duomenų');
    }
  };

  const getScheduleClipboard = (): {
    status: WorkScheduleEntry['status'];
    startTime?: string | null;
    endTime?: string | null;
    breakStartTime?: string | null;
    breakEndTime?: string | null;
    notes?: string | null;
  } | null => {
    try {
      const raw = localStorage.getItem(SCHEDULE_CLIPBOARD_KEY);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (error) {
      console.error('Failed to read schedule clipboard:', error);
      return null;
    }
  };

  // Copy/Paste for Cell Edit dialog
  const handleCopyCellEdit = () => {
    setScheduleClipboard({
      status: cellEditStatus,
      startTime: timeToString(cellEditStartTime),
      endTime: timeToString(cellEditEndTime),
      breakStartTime: timeToString(cellEditBreakStartTime),
      breakEndTime: timeToString(cellEditBreakEndTime),
      notes: cellEditNotes || ''
    });
    setCellEditDialogNotice({ type: 'success', text: t('messages.copied', 'Schedule updated successfully') });
    setTimeout(() => setCellEditDialogNotice(null), 2000);
  };

  const handlePasteCellEdit = () => {
    const data = getScheduleClipboard();
    if (!data) {
      setCellEditDialogNotice({ type: 'error', text: t('messages.noClipboard', 'No copied data found') });
      setTimeout(() => setCellEditDialogNotice(null), 2000);
      return;
    }
    setCellEditStatus(data.status as any);
    setCellEditStartTime(stringToTime(data.startTime));
    setCellEditEndTime(stringToTime(data.endTime));
    setCellEditBreakStartTime(stringToTime(data.breakStartTime));
    setCellEditBreakEndTime(stringToTime(data.breakEndTime));
    setCellEditNotes(data.notes || '');
    setCellEditDialogNotice({ type: 'success', text: t('messages.pasted', 'Schedule updated successfully') });
    setTimeout(() => setCellEditDialogNotice(null), 2000);
  };

  // Copy/Paste for Change Request dialog
  const handleCopyRequest = () => {
    setScheduleClipboard({
      status: requestedStatus,
      startTime: timeToString(requestedStartTime),
      endTime: timeToString(requestedEndTime),
      breakStartTime: timeToString(requestedBreakStartTime),
      breakEndTime: timeToString(requestedBreakEndTime),
      notes: requestedNotes || ''
    });
    setRequestDialogNotice({ type: 'success', text: t('messages.copied', 'Schedule updated successfully') });
    setTimeout(() => setRequestDialogNotice(null), 2000);
  };

  const handlePasteRequest = () => {
    const data = getScheduleClipboard();
    if (!data) {
      setError('Nėra nukopijuotų duomenų');
      setRequestDialogNotice({ type: 'error', text: t('messages.noClipboard', 'No copied data found') });
      setTimeout(() => setRequestDialogNotice(null), 2000);
      return;
    }
    setRequestedStatus(data.status as any);
    setRequestedStartTime(stringToTime(data.startTime));
    setRequestedEndTime(stringToTime(data.endTime));
    setRequestedBreakStartTime(stringToTime(data.breakStartTime));
    setRequestedBreakEndTime(stringToTime(data.breakEndTime));
    setRequestedNotes(data.notes || '');
    setSuccess('Įklijuota');
    setRequestDialogNotice({ type: 'success', text: t('messages.pasted', 'Schedule updated successfully') });
    setTimeout(() => setRequestDialogNotice(null), 2000);
  };

  const handleCopyTemplateDay = (day: WeeklyTemplate) => {
    setScheduleClipboard({
      status: day.status,
      startTime: day.startTime ?? undefined,
      endTime: day.endTime ?? undefined,
      breakStartTime: day.breakStartTime ?? undefined,
      breakEndTime: day.breakEndTime ?? undefined,
      notes: day.notes ?? ''
    });
    setSuccess(t('messages.copied', 'Nukopijuota'));
  };

  const handlePasteTemplateDay = (dayOfWeek: number) => {
    const data = getScheduleClipboard();
    if (!data) {
      setError(t('messages.noClipboard', 'Nėra nukopijuotų duomenų'));
      return;
    }
    setWeeklyTemplate(prev => prev.map(day =>
      day.dayOfWeek === dayOfWeek
        ? {
            ...day,
            status: data.status,
            startTime: data.startTime ?? undefined,
            endTime: data.endTime ?? undefined,
            breakStartTime: data.breakStartTime ?? undefined,
            breakEndTime: data.breakEndTime ?? undefined,
            notes: data.notes ?? ''
          }
        : day
    ));
    setSuccess(t('messages.pasted', 'Įklijuota'));
  };

  const handleCopyDeptTemplateDay = (day: DepartmentWeekDay) => {
    setScheduleClipboard({
      status: day.status,
      startTime: day.startTime ?? undefined,
      endTime: day.endTime ?? undefined,
      breakStartTime: day.breakStartTime ?? undefined,
      breakEndTime: day.breakEndTime ?? undefined,
      notes: day.notes ?? ''
    });
    setSuccess(t('messages.copied', 'Nukopijuota'));
  };

  const handlePasteDeptTemplateDay = (templateIndex: number, dayOfWeek: number) => {
    const data = getScheduleClipboard();
    if (!data) {
      setError(t('messages.noClipboard', 'Nėra nukopijuotų duomenų'));
      return;
    }
    setDeptTemplates((prev) =>
      prev.map((tpl, idx) => {
        if (idx !== templateIndex) return tpl;
        return {
          ...tpl,
          week: tpl.week.map((day) =>
            day.dayOfWeek === dayOfWeek
              ? {
                  ...day,
                  status: data.status,
                  startTime: data.startTime ?? null,
                  endTime: data.endTime ?? null,
                  breakStartTime: data.breakStartTime ?? null,
                  breakEndTime: data.breakEndTime ?? null,
                  notes: data.notes ?? ''
                }
              : day
          )
        };
      })
    );
    setSuccess(t('messages.pasted', 'Įklijuota'));
  };

  const fetchChangeRequests = async () => {
    try {
      const response = await fetch(`${getApiUrl(API_ENDPOINTS.WORK_SCHEDULE)}/change-requests`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setChangeRequests(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching change requests:', error);
    }
  };

  const handleRefresh = async () => {
    setLoading(true);
    try {
      // Refresh all data
      await Promise.all([
        fetchSchedules(),
        fetchPendingApprovals(),
        fetchCompanyView(),
        fetchAllUsers()
      ]);
      setSuccess(t('schedule.refreshSuccess', 'Data refreshed successfully'));
    } catch (error) {
      setError(t('schedule.refreshError', 'Failed to refresh data'));
    } finally {
      setLoading(false);
    }
  };

  // Department filter functions (view-all or multi-department allowlist)
  const handleSelectAllDepartments = () => {
    if (!canToggleDepartments) return;
    setSelectedDepartments([...availableDepartments]);
  };

  const handleDeselectAllDepartments = () => {
    if (!canToggleDepartments) return;
    setSelectedDepartments([]);
  };

  const handleDepartmentToggle = (department: string) => {
    if (!canToggleDepartments) return;
    setSelectedDepartments(prev => 
      prev.includes(department)
        ? prev.filter(d => d !== department)
        : [...prev, department]
    );
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Keyboard shortcuts within dialogs for Copy/Paste
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && (e.key === 'c' || e.key === 'C')) {
        if (cellEditDialogOpen) {
          e.preventDefault();
          handleCopyCellEdit();
        } else if (requestDialogOpen) {
          e.preventDefault();
          handleCopyRequest();
        }
      }
      if (e.ctrlKey && (e.key === 'v' || e.key === 'V')) {
        if (cellEditDialogOpen) {
          e.preventDefault();
          handlePasteCellEdit();
        } else if (requestDialogOpen) {
          e.preventDefault();
          handlePasteRequest();
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [cellEditDialogOpen, requestDialogOpen, handleCopyCellEdit, handlePasteCellEdit, handleCopyRequest, handlePasteRequest]);

  const renderScheduleTable = (scheduleData: WorkScheduleEntry[]) => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow key="schedule-header">
            <TableCell>{t('schedule.date')}</TableCell>
            <TableCell>{t('schedule.status')}</TableCell>
            <TableCell>{t('schedule.time')}</TableCell>
            <TableCell>{t('schedule.notes')}</TableCell>
            <TableCell>{t('schedule.user')}</TableCell>
            <TableCell>{t('schedule.approved')}</TableCell>
            <TableCell>{t('common.actions')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {scheduleData.map((schedule) => (
            <TableRow key={schedule.id}>
              <TableCell>{new Date(schedule.date).toLocaleDateString('lt-LT')}</TableCell>
              <TableCell>
                <Chip
                  icon={getStatusIcon(schedule.status)}
                  label={statusOptions.find(s => s.value === schedule.status)?.label}
                  color={statusOptions.find(s => s.value === schedule.status)?.color as any}
                  size="small"
                />
              </TableCell>
              <TableCell>
                {(() => {
                  // For Atostogos / Ligos atostogos / Mamadienis, show total hours instead of a fake 09:00–17:00 range
                  if (isHoursOnlyStatus(schedule.status)) {
                    if (schedule.startTime && schedule.endTime) {
                      const start = new Date(`2000-01-01T${schedule.startTime}`);
                      const end = new Date(`2000-01-01T${schedule.endTime}`);
                      const hours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
                      return hours > 0 ? `${hours.toFixed(1)} val.` : '-';
                    }
                    return '-';
                  }

                  // For normal working / remote days, show the actual time range
                  return (schedule.startTime && schedule.endTime)
                    ? `${schedule.startTime} - ${schedule.endTime}`
                    : '-';
                })()}
              </TableCell>
              <TableCell>{schedule.notes || '-'}</TableCell>
              <TableCell>
                {schedule.user ? `${schedule.user.firstName} ${schedule.user.lastName}` : '-'}
              </TableCell>
              <TableCell>
                <Chip
                  label={schedule.isApproved ? t('common.approved') : t('common.pending')}
                  color={schedule.isApproved ? 'success' : 'warning'}
                  size="small"
                />
              </TableCell>
              <TableCell>
                <IconButton
                  size="small"
                  onClick={() => {
                    setEditingSchedule(schedule);
                    setSelectedDate(new Date(schedule.date));
                    setSelectedStatus(schedule.status);
                    setStartTime(schedule.startTime ? new Date(`2000-01-01T${schedule.startTime}`) : null);
                    setEndTime(schedule.endTime ? new Date(`2000-01-01T${schedule.endTime}`) : null);
                    setNotes(schedule.notes || '');
                    setDialogOpen(true);
                  }}
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleDelete(schedule.id)}
                >
                  <DeleteIcon />
                </IconButton>
                {!schedule.isApproved && canApproveSchedules() && (
                  <>
                    <IconButton
                      size="small"
                      color="success"
                      onClick={() => {
                        console.log('Approve button clicked for schedule ID:', schedule.id);
                        handleApproveIndividual(schedule.id);
                      }}
                      title={t('schedule.approve')}
                    >
                      <CheckIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => {
                        console.log('Reject button clicked for schedule ID:', schedule.id);
                        handleRejectIndividual(schedule.id);
                      }}
                      title={t('schedule.reject')}
                    >
                      <CloseIcon />
                    </IconButton>
                  </>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderWeeklyTemplate = () => (
    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>
      {templateDialogNotice && (
        <Alert severity={templateDialogNotice.type} onClose={() => setTemplateDialogNotice(null)}>
          {templateDialogNotice.text}
        </Alert>
      )}
      <Box>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {t('schedule.weeklyTemplate')}
        </Typography>
        
        {/* User Selection for Admins */}
        {canApproveSchedules() && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
              {t('schedule.template.selectUser')}
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel>{t('schedule.template.selectUser')}</InputLabel>
              <Select
                value={templateSelectedUser}
                onChange={(e) => setTemplateSelectedUser(e.target.value)}
                label={t('schedule.template.selectUser')}
                disabled={usersLoading}
              >
                <MenuItem value="">
                  <em>{t('schedule.template.applyToMyself')}</em>
                </MenuItem>
                {usersLoading ? (
                  <MenuItem disabled>
                    <em>{t('common.loading')}</em>
                  </MenuItem>
                ) : allUsers.length === 0 ? (
                  <MenuItem disabled>
                    <em>No users available</em>
                  </MenuItem>
                ) : (
                  allUsers
                    // All users shown - filtering by permissions instead of role
                    .map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.firstName} {user.lastName} ({user.department})
                      </MenuItem>
                    ))
                )}
              </Select>
            </FormControl>
            {usersLoading && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Loading users...
              </Typography>
            )}
          </Box>
        )}
        
        {/* Apply Mode Selection */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
            {t('schedule.applyMode')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant={templateApplyMode === 'single' ? 'contained' : 'outlined'}
              onClick={() => setTemplateApplyMode('single')}
              size="small"
            >
              {t('schedule.template.singleWeek')}
            </Button>
            <Button
              variant={templateApplyMode === 'multiple' ? 'contained' : 'outlined'}
              onClick={() => setTemplateApplyMode('multiple')}
              size="small"
            >
              {t('schedule.template.multipleWeeks')}
            </Button>
          </Box>
        </Box>

        {/* Date Selection */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
            {templateApplyMode === 'single' ? t('schedule.startDate') : t('schedule.template.dateRange')}
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: templateApplyMode === 'single' ? '1fr' : '1fr 1fr', gap: 2 }}>
            <DatePicker
              label={t('schedule.startDate')}
              value={templateStartDate}
              onChange={(newValue: Date | null) => setTemplateStartDate(newValue)}
              slotProps={{ textField: { fullWidth: true } }}
            />
            {templateApplyMode === 'multiple' && (
              <>
                <DatePicker
                  label={t('schedule.template.endDate')}
                  value={templateEndDate}
                  onChange={(newValue: Date | null) => setTemplateEndDate(newValue)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('schedule.or')}
                  </Typography>
                  <TextField
                    type="number"
                    label={t('schedule.template.weeksCount')}
                    value={templateWeeksCount}
                    onChange={(e) => setTemplateWeeksCount(Math.max(1, parseInt(e.target.value) || 1))}
                    size="small"
                    sx={{ width: 120 }}
                    inputProps={{ min: 1, max: 52 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {t('schedule.weeks')}
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </Box>

        {/* Reason for change requests when locked */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
            {t('schedule.requestReason')}
          </Typography>
          <TextField
            placeholder={t('schedule.requestReasonHelp', 'Pateikite priežastį, jei mėnuo užblokuotas')}
            value={templateReason}
            onChange={(e) => setTemplateReason(e.target.value)}
            multiline
            rows={3}
            fullWidth
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {t('schedule.reasonRequiredCaption', 'Jei mėnuo užblokuotas – priežastis privaloma')}
          </Typography>
        </Box>
        
        <Divider />
        
        <Box>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            {t('schedule.template.configureWeek')}
          </Typography>
          {weeklyTemplate.map((day, index) => (
            <Box key={day.dayOfWeek} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  {dayNames[day.dayOfWeek]}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button variant="outlined" size="small" onClick={() => handleCopyTemplateDay(day)}>
                    {t('common.copy', 'Kopijuoti')}
                  </Button>
                  <Button variant="outlined" size="small" onClick={() => handlePasteTemplateDay(day.dayOfWeek)}>
                    {t('common.paste', 'Įklijuoti')}
                  </Button>
                </Box>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
                <Box>
                  <FormControl fullWidth size="small">
                    <InputLabel>{t('schedule.status')}</InputLabel>
                    <Select
                      value={day.status}
                      onChange={(e) => {
                        const newStatus = e.target.value as string;
                        updateWeeklyTemplate(day.dayOfWeek, 'status', newStatus);
                        if (isHoursOnlyStatus(newStatus)) {
                          if (day.startTime && day.endTime) {
                            setWeeklyTemplateHoursStr(prev => ({
                              ...prev,
                              [day.dayOfWeek]: formatHoursOnlyAutofill(
                                day.startTime!,
                                day.endTime!,
                                day.breakStartTime,
                                day.breakEndTime
                              )
                            }));
                          } else {
                            setWeeklyTemplateHoursStr(prev => ({ ...prev, [day.dayOfWeek]: '' }));
                          }
                        } else {
                          setWeeklyTemplateHoursStr(prev => {
                            const next = { ...prev };
                            delete next[day.dayOfWeek];
                            return next;
                          });
                        }
                      }}
                      label={t('schedule.status')}
                    >
                      {weeklyTemplateStatusOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {option.icon}
                            {option.label}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                {isHoursOnlyStatus(day.status) ? (
                  <Box>
                    <TextField
                      label={
                        day.status === 'business_trip' ? t('schedule.businessTripHours') :
                        day.status === 'military_service' ? t('schedule.militaryServiceHours') :
                        day.status === 'day_off' ? t('schedule.vacationHours') :
                        day.status === 'sick_leave' ? t('schedule.sickHours') :
                        t('schedule.familyHours')
                      }
                      type="text"
                      inputMode="decimal"
                      value={
                        weeklyTemplateHoursStr[day.dayOfWeek] ??
                        (day.startTime && day.endTime
                          ? formatHoursOnlyAutofill(
                              day.startTime,
                              day.endTime,
                              day.breakStartTime,
                              day.breakEndTime
                            )
                          : '')
                      }
                      onChange={(e) => setWeeklyTemplateHoursStr(prev => ({ ...prev, [day.dayOfWeek]: e.target.value }))}
                      inputProps={{ placeholder: 'pvz. 7,5' }}
                      size="small"
                      fullWidth
                    />
                  </Box>
                ) : day.status !== 'state_holiday' && day.status !== 'rest_day' && (
                  <>
                <Box>
                  <TimePicker
                    label={t('schedule.startTime')}
                    value={day.startTime ? new Date(`2000-01-01T${day.startTime}`) : null}
                    onChange={(newValue: Date | null) => 
                      updateWeeklyTemplate(day.dayOfWeek, 'startTime', 
                        newValue ? newValue.toTimeString().slice(0, 5) : undefined)
                    }
                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                  />
                </Box>
                <Box>
                  <TimePicker
                    label={t('schedule.endTime')}
                    value={day.endTime ? new Date(`2000-01-01T${day.endTime}`) : null}
                    onChange={(newValue: Date | null) => 
                      updateWeeklyTemplate(day.dayOfWeek, 'endTime', 
                        newValue ? newValue.toTimeString().slice(0, 5) : undefined)
                    }
                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                  />
                </Box>
                  </>
                )}
                {(day.status === 'working' || day.status === 'remote' || day.status === 'praktika') && (
                  <>
                    <Box>
                      <TimePicker
                        label={t('schedule.breakStartTime', 'Break Start Time')}
                        value={day.breakStartTime ? new Date(`2000-01-01T${day.breakStartTime}`) : null}
                        onChange={(newValue: Date | null) => 
                          updateWeeklyTemplate(day.dayOfWeek, 'breakStartTime', 
                            newValue ? newValue.toTimeString().slice(0, 5) : undefined)
                        }
                        slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                      />
                    </Box>
                    <Box>
                      <TimePicker
                        label={t('schedule.breakEndTime', 'Break End Time')}
                        value={day.breakEndTime ? new Date(`2000-01-01T${day.breakEndTime}`) : null}
                        onChange={(newValue: Date | null) => 
                          updateWeeklyTemplate(day.dayOfWeek, 'breakEndTime', 
                            newValue ? newValue.toTimeString().slice(0, 5) : undefined)
                        }
                        slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                      />
                    </Box>
                  </>
                )}
                <Box>
                  <TextField
                    label={t('schedule.notes')}
                    value={day.notes || ''}
                    onChange={(e) => updateWeeklyTemplate(day.dayOfWeek, 'notes', e.target.value)}
                    size="small"
                    fullWidth
                    required={day.status === 'remote'}
                    error={day.status === 'remote' && !(day.notes || '').trim()}
                  />
                  {day.status === 'remote' && (
                    <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => updateWeeklyTemplate(day.dayOfWeek, 'notes', remoteAddressTemplate)}
                        disabled={!remoteAddressTemplate.trim() || remoteTemplateLoading}
                      >
                        {t('schedule.useMyRemoteAddress', 'Naudoti mano adresą')}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={async () => {
                          const ok = await saveRemoteAddressTemplate(day.notes || '');
                          if (ok) {
                            setTemplateDialogNotice({ type: 'success', text: t('schedule.remoteTemplateSaved', 'Mano nuotolinio darbo adresas išsaugotas.') });
                          } else {
                            setTemplateDialogNotice({ type: 'error', text: t('schedule.remoteTemplateSaveFailed', 'Nepavyko išsaugoti nuotolinio darbo adreso.') });
                          }
                        }}
                        disabled={!(day.notes || '').trim() || remoteTemplateSaving}
                      >
                        {t('schedule.saveAsMyRemoteAddress', 'Išsaugoti kaip mano adresą')}
                      </Button>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );

  // Calculate displayed hours (scheduled hours) for an employee for the current month
  const calculateDisplayedHours = (employee: CompanyEmployee, year: number, month: number): number => {
    let totalDisplayedHours = 0;
    
    // Get all schedules for this employee in the selected month
    const monthSchedules = employee.schedules.filter((schedule: WorkScheduleEntry) => {
      const scheduleDate = new Date(schedule.date);
      return scheduleDate.getFullYear() === year && scheduleDate.getMonth() === month;
    });
    
    monthSchedules.forEach((schedule: WorkScheduleEntry) => {
      // Count scheduled entries for statuses that contribute hours (exclude rest_day, state_holiday, praktika)
      // This shows what hours were "displayed" or scheduled for the user
      if (
        schedule.status !== 'rest_day' &&
        schedule.status !== 'state_holiday' &&
        schedule.status !== 'praktika' &&
        schedule.startTime &&
        schedule.endTime
      ) {
        totalDisplayedHours += getScheduleHours(schedule);
      }
    });
    
    return Math.round(totalDisplayedHours * 100) / 100; // Round to 2 decimal places
  };

  /** Hours of overlap between shift [workStart, workEnd] and break on the same reference day; 0 if break invalid or disjoint. */
  function getBreakOverlapHours(
    workStartTime: string,
    workEndTime: string,
    breakStartTime: string,
    breakEndTime: string
  ): number {
    const workStart = new Date(`2000-01-01T${workStartTime}`).getTime();
    const workEnd = new Date(`2000-01-01T${workEndTime}`).getTime();
    const breakStart = new Date(`2000-01-01T${breakStartTime}`).getTime();
    const breakEnd = new Date(`2000-01-01T${breakEndTime}`).getTime();
    if (breakEnd <= breakStart) return 0;
    const overlapStart = Math.max(workStart, breakStart);
    const overlapEnd = Math.min(workEnd, breakEnd);
    return Math.max(0, overlapEnd - overlapStart) / (1000 * 60 * 60);
  }

  function calculateHoursWorked(startTime: string, endTime: string, breakStartTime?: string, breakEndTime?: string): number {
    const start = new Date(`2000-01-01T${startTime}`);
    const end = new Date(`2000-01-01T${endTime}`);
    let totalHours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);

    if (breakStartTime && breakEndTime) {
      totalHours -= getBreakOverlapHours(startTime, endTime, breakStartTime, breakEndTime);
    }

    // Guard against inverted/legacy bad rows (e.g. 17:00–09:00)
    return Math.max(0, totalHours);
  }

  // Hours for a schedule entry — only subtract break for working/remote (Ligos atostogos, Atostogos, Mamadienis have no breaks)
  function getScheduleHours(schedule: WorkScheduleEntry): number {
    if (!schedule.startTime || !schedule.endTime) return 0;
    const shouldSubtractBreak =
      schedule.status === 'working' ||
      schedule.status === 'remote' ||
      schedule.status === 'praktika';
    return calculateHoursWorked(
      schedule.startTime,
      schedule.endTime,
      shouldSubtractBreak ? schedule.breakStartTime : undefined,
      shouldSubtractBreak ? schedule.breakEndTime : undefined
    );
  }

  // Helper function to get the previous day
  const getPreviousDay = (dateString: string): string => {
    const date = new Date(dateString + 'T00:00:00');
    date.setDate(date.getDate() - 1);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper function to get hours worked before a state holiday
  const getHoursWorkedBeforeStateHoliday = (employee: CompanyEmployee, stateHolidayDate: string): number => {
    const previousDay = getPreviousDay(stateHolidayDate);
    const schedule = employee.schedules.find((s: WorkScheduleEntry) => s.date === previousDay);
    
    if (schedule && (schedule.status === 'working' || schedule.status === 'remote')) {
      if (schedule.startTime && schedule.endTime) {
        let hours = calculateHoursWorked(
          schedule.startTime,
          schedule.endTime,
          schedule.breakStartTime,
          schedule.breakEndTime
        );
        return hours;
      }
    }
    return 0;
  };

  // Calculate required hours using summary accounting formula
  const calculateSummaryAccountingHours = (
    employee: CompanyEmployee,
    year: number,
    month: number,
    allEmployees: CompanyEmployee[]
  ): number => {
    // Get calendar days in month
    const calendarDays = new Date(year, month + 1, 0).getDate();
    
    // Get state holidays in month
    const stateHolidays = getStateHolidaysInMonth(year, month, allEmployees);
    
    // Calculate working days
    const workingDays = calendarDays - stateHolidays.length;
    
    // Calculate weeks
    const weeks = workingDays / 7;
    
    // Calculate weekly norm
    const weeklyNorm = 40 * (employee.etatas || 1);
    
    // Calculate total monthly hours (before subtracting hours worked before holidays)
    const totalMonthlyHours = weeks * weeklyNorm;
    
    // Sum hours worked on days before state holidays
    let sumOfHoursBeforeHolidays = 0;
    stateHolidays.forEach(holidayDate => {
      sumOfHoursBeforeHolidays += getHoursWorkedBeforeStateHoliday(employee, holidayDate);
    });
    
    // Calculate required hours: subtract hours worked before holidays from total monthly hours
    const requiredHours = totalMonthlyHours - sumOfHoursBeforeHolidays;
    
    // Round to nearest integer
    return Math.round(requiredHours);
  };

  const getEmployeeHoursSummary = (
    employee: CompanyEmployee,
    year: number,
    month: number,
    monthlyRequiredHours: number,
    companyEmployees: CompanyEmployee[]
  ): {
    actualHours: number;
    balanceText: string;
    balanceColor: string;
  } => {
    const actualHours = calculateDisplayedHours(employee, year, month);

    if (employee.workAccountingType === 'summary') {
      const required = calculateSummaryAccountingHours(employee, year, month, companyEmployees);
      return {
        actualHours,
        balanceText: required === 0 ? '-' : required.toFixed(2),
        balanceColor: required === 0 ? 'text.secondary' : 'inherit'
      };
    }

    const etatas = employee.etatas || 1;
    const required = monthlyRequiredHours > 0 ? monthlyRequiredHours * etatas : 0;
    const diff = Math.round((actualHours - required) * 100) / 100;
    const meets = required > 0 && (actualHours >= required || (required - actualHours) <= 0.25);
    return {
      actualHours,
      balanceText: required === 0 ? '-' : diff.toFixed(2),
      balanceColor: required === 0 ? 'text.secondary' : (meets ? '#2e7d32' : '#c62828')
    };
  };

  /** Praktika hours for hover — null when employee has no praktika days in loaded schedules. */
  const getEmployeePraktikaHoursBreakdown = (
    employee: CompanyEmployee,
    year: number,
    month: number
  ): { week: number; month: number; total: number } | null => {
    const praktikaSchedules = employee.schedules.filter(
      (s) => s.status === 'praktika' && s.startTime && s.endTime
    );
    if (praktikaSchedules.length === 0) return null;

    const now = new Date();
    const day = now.getDay(); // 0=Sun
    const mondayOffset = day === 0 ? -6 : 1 - day;
    const weekStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() + mondayOffset);
    weekStart.setHours(0, 0, 0, 0);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    weekEnd.setHours(23, 59, 59, 999);

    let week = 0;
    let monthHours = 0;
    let total = 0;

    praktikaSchedules.forEach((schedule) => {
      const hours = getScheduleHours(schedule);
      total += hours;
      const scheduleDate = new Date(schedule.date + 'T00:00:00');
      if (scheduleDate.getFullYear() === year && scheduleDate.getMonth() === month) {
        monthHours += hours;
      }
      if (scheduleDate >= weekStart && scheduleDate <= weekEnd) {
        week += hours;
      }
    });

    return {
      week: Math.round(week * 100) / 100,
      month: Math.round(monthHours * 100) / 100,
      total: Math.round(total * 100) / 100
    };
  };

  const renderEmployeeHoursTooltip = (
    employeeName: string,
    summary: ReturnType<typeof getEmployeeHoursSummary>,
    etatas?: number,
    praktikaHours?: { week: number; month: number; total: number } | null
  ) => {
    const balanceValueColor =
      summary.balanceColor === '#2e7d32' || summary.balanceColor === '#c62828'
        ? summary.balanceColor
        : 'text.secondary';

    const etatasText = etatas ? `${etatas} et.` : '-';

    const tooltipRow = (
      label: string,
      value: string | number,
      opts?: { color?: string; borderTop?: boolean }
    ) => (
      <Box
        key={label}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          gap: 2,
          py: 0.4,
          ...(opts?.borderTop
            ? { borderTop: '1px solid', borderColor: 'divider' }
            : {})
        }}
      >
        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
          {label}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 700,
            fontVariantNumeric: 'tabular-nums',
            color: opts?.color || 'text.primary'
          }}
        >
          {value}
        </Typography>
      </Box>
    );

    return (
      <Box sx={{ minWidth: 230, py: 0.25 }}>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 700, mb: 1, lineHeight: 1.3, color: 'text.primary' }}
        >
          {employeeName}
        </Typography>
        {tooltipRow(t('schedule.etatas', 'Etatas'), etatasText, { borderTop: true })}
        {tooltipRow(t('schedule.workHours'), summary.actualHours)}
        {tooltipRow(t('schedule.displayedHours'), summary.balanceText, {
          color: balanceValueColor
        })}
        {praktikaHours && (
          <>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                mt: 1,
                mb: 0.5,
                pt: 0.75,
                borderTop: '1px solid',
                borderColor: 'divider',
                color: PRAKTIKA_SOLID_FALLBACK,
                lineHeight: 1.3
              }}
            >
              {t('schedule.praktika')}
            </Typography>
            {tooltipRow(
              t('schedule.praktikaThisWeek', 'Ši savaitė'),
              `${praktikaHours.week} h`
            )}
            {tooltipRow(
              t('schedule.praktikaThisMonth', 'Šis mėnuo'),
              `${praktikaHours.month} h`
            )}
            {tooltipRow(
              t('schedule.praktikaTotal', 'Iš viso'),
              `${praktikaHours.total} h`
            )}
          </>
        )}
      </Box>
    );
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'working': return 'rgba(255, 193, 112, 0.85)'; // Darker orange — Dirba (working)
      case 'day_off': return 'rgba(70, 150, 70, 0.85)'; // Darker green — Atostogos (vacation), distinct from Ligos atostogos pale green
      case 'mothers_day': return 'rgba(173, 216, 230, 0.85)'; // Creamier light blue with transparency
      case 'remote': return 'rgba(255, 250, 205, 0.85)'; // Creamier light yellow with transparency
      case 'sick_leave': return 'rgba(152, 251, 152, 0.85)'; // Creamier pale green — Ligos atostogos
      case 'rest_day': return 'rgba(200, 170, 200, 0.85)'; // Creamier purple with transparency
      case 'state_holiday': return 'rgba(255, 182, 193, 0.85)'; // Creamier light pink with transparency
      case 'business_trip': return BUSINESS_TRIP_SOLID_FALLBACK;
      case 'military_service': return MILITARY_SERVICE_SOLID_FALLBACK;
      case 'praktika': return PRAKTIKA_SOLID_FALLBACK;
      default: return '#f5f5f5';
    }
  };

  const getScheduleCellBgProps = (
    status: string | undefined,
    isPending: boolean
  ): { backgroundColor: string } | { background: string; backgroundColor: string } => {
    if (isPending) return { backgroundColor: '#9e9e9e' };
    if (!status) return { backgroundColor: 'transparent' };
    if (status === 'business_trip') return { background: BUSINESS_TRIP_GRADIENT, backgroundColor: 'transparent' };
    if (status === 'military_service') return { background: MILITARY_SERVICE_GRADIENT, backgroundColor: 'transparent' };
    if (status === 'praktika') return { background: PRAKTIKA_GRADIENT, backgroundColor: 'transparent' };
    return { backgroundColor: getStatusColor(status) };
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'working': return t('schedule.working');
      case 'day_off': return t('schedule.dayOff');
      case 'mothers_day': return t('schedule.mothersDay');
      case 'remote': return t('schedule.remote');
      case 'sick_leave': return t('schedule.sickLeave');
      case 'rest_day': return t('schedule.restDay');
      case 'state_holiday': return t('schedule.stateHoliday');
      case 'business_trip': return t('schedule.businessTrip');
      case 'military_service': return t('schedule.militaryService');
      case 'praktika': return t('schedule.praktika');
      default: return status;
    }
  };

  // Get status abbreviation for Excel
  const getStatusAbbreviation = (status: string) => {
    switch (status) {
      case 'working': return 'D';
      case 'remote': return 'N';
      case 'sick_leave': return 'L';
      case 'day_off': return 'A';
      case 'mothers_day': return 'M';
      case 'rest_day': return 'P';
      case 'state_holiday': return 'Š';
      case 'business_trip': return 'K';
      case 'military_service': return 'KT';
      case 'praktika': return 'Pr';
      default: return '?';
    }
  };

  // Copy schedule to Excel with HTML format (preserves colors)
  const handleCopyScheduleToExcel = async () => {
    if (filteredCompanyView.length === 0 || selectedDepartments.length === 0) {
      setError('Nėra duomenų kopijuoti');
      return;
    }

    try {
      const year = selectedMonth.getFullYear();
      const month = selectedMonth.getMonth();
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      
      const monthDates = Array.from({ length: daysInMonth }, (_, i) => {
        const dayNumber = i + 1;
        const date = new Date(year, month, dayNumber);
        return {
          day: dayNumber,
          date: date
        };
      });

      // Format month title
      const monthTitle = selectedMonth.toLocaleDateString('lt-LT', { 
        month: 'long', 
        year: 'numeric' 
      });
      
      // Build HTML table - Excel requires specific format
      let htmlTable = '<html><head><meta charset="utf-8"></head><body><table border="1" cellpadding="3" cellspacing="0" style="border-collapse: collapse; font-family: Arial, sans-serif;">';
      
      // Title row with month and year
      const totalColumns = 6 + monthDates.length; // Employee, Department, Position, Required, Actual, Balance + date columns
      htmlTable += '<tr style="background-color: #4a90e2; color: white;">';
      htmlTable += `<td colspan="${totalColumns}" style="border: 1px solid #ddd; padding: 10px; text-align: center; font-weight: bold; font-size: 1.2em;">${monthTitle}</td>`;
      htmlTable += '</tr>';
      
      // Header row
      htmlTable += '<tr style="background-color: #f0f0f0; font-weight: bold;">';
      htmlTable += '<th style="border: 1px solid #ddd; padding: 5px; text-align: left;">' + t('schedule.employee') + '</th>';
      htmlTable += '<th style="border: 1px solid #ddd; padding: 5px; text-align: left;">' + t('schedule.department') + '</th>';
      htmlTable += '<th style="border: 1px solid #ddd; padding: 5px; text-align: left;">' + t('schedule.position') + '</th>';
      htmlTable += '<th style="border: 1px solid #ddd; padding: 5px; text-align: center;">' + t('schedule.requiredHoursLabel') + '</th>';
      htmlTable += '<th style="border: 1px solid #ddd; padding: 5px; text-align: center;">' + t('schedule.workHours') + '</th>';
      htmlTable += '<th style="border: 1px solid #ddd; padding: 5px; text-align: center;">' + t('schedule.displayedHours') + '</th>';
      monthDates.forEach(({ day, date }) => {
        const isWeekend = date.getDay() === 0 || date.getDay() === 6;
        const borderStyle = isWeekend ? '1px solid #5c6bc0' : '1px solid #ddd';
        htmlTable += `<th style="border: ${borderStyle}; padding: 5px; text-align: center; min-width: 50px;">${day}</th>`;
      });
      htmlTable += '</tr>';

      // Data rows
      filteredCompanyView.forEach((employee: CompanyEmployee) => {
        const isCurrentUser = user?.id === employee.userId;
        const isPinned = employee.isPinned || pinnedUsers.has(employee.userId);
        const rowBgColor = isCurrentUser ? '#e8f5e8' : (isPinned ? '#fff9e6' : '#ffffff');
        const rowBorderColor = isCurrentUser ? '#4caf50' : (isPinned ? '#ffc107' : '#ddd');
        
        // Use both bgcolor attribute and style for Excel compatibility
        htmlTable += `<tr bgcolor="${rowBgColor}" style="background-color: ${rowBgColor}; border-top: 2px solid ${rowBorderColor}; border-bottom: 2px solid ${rowBorderColor};">`;
        
        // Employee name
        htmlTable += `<td style="border: 1px solid #ddd; padding: 5px; font-weight: bold; color: ${isCurrentUser ? '#2e7d32' : 'inherit'};">${employee.name || ''}</td>`;
        
        // Department
        htmlTable += `<td style="border: 1px solid #ddd; padding: 5px;">${employee.department || ''}</td>`;
        
        // Position
        htmlTable += `<td style="border: 1px solid #ddd; padding: 5px;">${employee.position || ''}</td>`;
        
        // Required hours (per employee: monthly norm * etatas, or summary accounting)
        let employeeRequired = 0;
        if (employee.workAccountingType === 'summary') {
          employeeRequired = calculateSummaryAccountingHours(employee, year, month, filteredCompanyView);
        } else {
          const monthlyNorm = Number(monthlyRequirement?.requiredHours) || 0;
          const etatas = employee.etatas || 1;
          employeeRequired = monthlyNorm * etatas;
        }
        htmlTable += `<td style="border: 1px solid #ddd; padding: 5px; text-align: center;">${employeeRequired.toFixed(1)}</td>`;
        
        // Actual hours (displayed)
        const totalActualHours = calculateDisplayedHours(employee, year, month);
        htmlTable += `<td style="border: 1px solid #ddd; padding: 5px; text-align: center;">${totalActualHours.toFixed(1)}</td>`;
        
        // Balance (Valandų balansas)
        const balance = Math.round((totalActualHours - employeeRequired) * 100) / 100;
        htmlTable += `<td style="border: 1px solid #ddd; padding: 5px; text-align: center;">${employeeRequired === 0 ? '-' : balance.toFixed(2)}</td>`;
        
        // Date cells
        monthDates.forEach(({ day, date }) => {
          const dateYear = date.getFullYear();
          const dateMonth = String(date.getMonth() + 1).padStart(2, '0');
          const dateDay = String(date.getDate()).padStart(2, '0');
          const cellDate = `${dateYear}-${dateMonth}-${dateDay}`;
          const isWeekend = date.getDay() === 0 || date.getDay() === 6;
          
          const schedule = getPrimaryScheduleForDate(employee.schedules, cellDate, year, month, day);
          const praktikaSeg = getPraktikaScheduleForDate(employee.schedules, cellDate, year, month, day);
          const soleOrPrimary = schedule ?? praktikaSeg;
          
          // Check for pending approval
          const pendingApproval = pendingApprovals.find(req => 
            req.userId === employee.userId && req.date === cellDate
          );
          
          // Check if cell is highlighted
          const cellKey = `${employee.userId}-${cellDate}`;
          const hasExcursion = excursionsByUserDate.has(cellKey);
          const hasEdukacija = edukacijosByUserDate.has(cellKey);
          const hasManualHighlight = cellHighlights.has(cellKey);
          const hasBlueMark = cellBlueMarks.has(cellKey);
          const shouldHighlight = hasExcursion || hasEdukacija || hasManualHighlight;
          const highlightColor = hasExcursion ? '#7fff00' : hasEdukacija ? '#ff00de' : '#e34234'; // Ex=green, Ed=#ff00de light pink, manual=red
          const highlightColorSecondary = hasExcursion && hasEdukacija ? '#ff00de' : null; // For combined Ex+Ed borders
          const blueMarkColor = '#87CEEB'; // Sky blue
          
          // Determine cell background color
          let cellBgColor = '#ffffff';
          if (pendingApproval) {
            cellBgColor = '#9e9e9e'; // Grey for pending
          } else if (soleOrPrimary) {
            cellBgColor = getStatusColor(soleOrPrimary.status);
          }
          
          // Note: We keep the cell background color normal
          // Marks are indicated by colored borders (red left/top, blue right/bottom when both present)
          
          // Convert rgba to hex for better Excel compatibility (Excel prefers hex or rgb without alpha)
          // If it's rgba, convert to rgb (remove alpha channel)
          let excelBgColor = cellBgColor;
          if (cellBgColor.startsWith('rgba')) {
            // Extract RGB values and convert to hex
            const rgbaMatch = cellBgColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
            if (rgbaMatch) {
              const r = parseInt(rgbaMatch[1]);
              const g = parseInt(rgbaMatch[2]);
              const b = parseInt(rgbaMatch[3]);
              excelBgColor = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
            }
          }
          
          // Build cell content (primary may be absent on Praktika-only days)
          let cellContent = '';
          if (schedule) {
            if ((schedule.status === 'working' || schedule.status === 'remote' || schedule.status === 'sick_leave' || schedule.status === 'day_off' || schedule.status === 'business_trip' || schedule.status === 'military_service' || schedule.status === 'praktika') && schedule.startTime && schedule.endTime) {
              const hours = getScheduleHours(schedule).toFixed(1);
              cellContent = getStatusAbbreviation(schedule.status) + '<br>' +
                schedule.startTime.slice(0, 5) + '-' + schedule.endTime.slice(0, 5) + '<br>' +
                hours + 'h';
              if (schedule.status === 'working' || schedule.status === 'remote') {
                if (schedule.breakStartTime && schedule.breakEndTime) {
                  cellContent += '<br>' + schedule.breakStartTime.slice(0, 5) + '-' + schedule.breakEndTime.slice(0, 5);
                } else {
                  cellContent += '<br>⚠️';
                }
              } else if (schedule.status === 'praktika' && schedule.breakStartTime && schedule.breakEndTime) {
                cellContent += '<br>' + schedule.breakStartTime.slice(0, 5) + '-' + schedule.breakEndTime.slice(0, 5);
              }
              if (
                praktikaSeg &&
                PAIRABLE_PRIMARY_STATUSES.includes(schedule.status) &&
                praktikaSeg.startTime &&
                praktikaSeg.endTime
              ) {
                cellContent +=
                  '<br>Pr ' +
                  praktikaSeg.startTime.slice(0, 5) +
                  '-' +
                  praktikaSeg.endTime.slice(0, 5);
              }
            } else {
              cellContent = getStatusAbbreviation(schedule.status);
            }
          } else if (praktikaSeg && praktikaSeg.startTime && praktikaSeg.endTime) {
            const hours = getScheduleHours(praktikaSeg).toFixed(1);
            cellContent =
              getStatusAbbreviation('praktika') +
              '<br>' +
              praktikaSeg.startTime.slice(0, 5) +
              '-' +
              praktikaSeg.endTime.slice(0, 5) +
              '<br>' +
              hours +
              'h';
            if (praktikaSeg.breakStartTime && praktikaSeg.breakEndTime) {
              cellContent +=
                '<br>' +
                praktikaSeg.breakStartTime.slice(0, 5) +
                '-' +
                praktikaSeg.breakEndTime.slice(0, 5);
            }
          } else if (praktikaSeg) {
            cellContent = getStatusAbbreviation('praktika');
          }
          
          if (pendingApproval) {
            cellContent = (cellContent ? cellContent + '<br>' : '') + '<span style="background-color: #666; color: white; padding: 1px 3px; border-radius: 2px; font-size: 0.7em;">LAUKIA</span>';
          }
          
          // Add Ex/Ed indicators
          if (hasExcursion || hasEdukacija) {
            const indicators = [];
            if (hasExcursion) indicators.push('<span style="color: #000; font-weight: bold;">Ex</span>');
            if (hasEdukacija) indicators.push('<span style="color: #000; font-weight: bold;">Ed</span>');
            cellContent = (cellContent ? cellContent + '<br>' : '') + indicators.join(' ');
          }
          
          // Build border styles based on marks
          // When both marks exist: red left/top, blue right/bottom
          // When only red: red borders on all sides
          // When only blue: blue borders on all sides
          // Otherwise: normal borders
          let borderTop = '1px solid #ddd';
          let borderRight = '1px solid #ddd';
          let borderBottom = '1px solid #ddd';
          let borderLeft = '1px solid #ddd';
          
          if (isWeekend) {
            borderTop = '1px solid #5c6bc0';
            borderRight = '1px solid #5c6bc0';
            borderBottom = '1px solid #5c6bc0';
            borderLeft = '1px solid #5c6bc0';
          }
          
          if (!pendingApproval) {
            if (shouldHighlight && hasBlueMark) {
              // Both marks: highlight left/top, blue right/bottom
              borderLeft = `3px solid ${highlightColor}`;
              borderTop = `3px solid ${highlightColor}`;
              borderRight = `3px solid ${blueMarkColor}`;
              borderBottom = `3px solid ${blueMarkColor}`;
            } else if (shouldHighlight && highlightColorSecondary) {
              // Ex+Ed: green left/top, pink right/bottom
              borderLeft = `3px solid ${highlightColor}`;
              borderTop = `3px solid ${highlightColor}`;
              borderRight = `3px solid ${highlightColorSecondary}`;
              borderBottom = `3px solid ${highlightColorSecondary}`;
            } else if (shouldHighlight) {
              // Single highlight: borders on all sides
              borderTop = `3px solid ${highlightColor}`;
              borderRight = `3px solid ${highlightColor}`;
              borderBottom = `3px solid ${highlightColor}`;
              borderLeft = `3px solid ${highlightColor}`;
            } else if (hasBlueMark) {
              // Only blue mark: blue borders on all sides
              borderTop = `3px solid ${blueMarkColor}`;
              borderRight = `3px solid ${blueMarkColor}`;
              borderBottom = `3px solid ${blueMarkColor}`;
              borderLeft = `3px solid ${blueMarkColor}`;
            }
          }
          
          const borderStyle = `border-top: ${borderTop}; border-right: ${borderRight}; border-bottom: ${borderBottom}; border-left: ${borderLeft};`;
          
          // Use both bgcolor attribute and style for maximum Excel compatibility
          htmlTable += `<td bgcolor="${excelBgColor}" style="${borderStyle} padding: 3px; text-align: center; background-color: ${excelBgColor}; vertical-align: top; font-size: 0.85em;">${cellContent || ''}</td>`;
        });
        
        htmlTable += '</tr>';
      });
      
      // Worker count row
      htmlTable += '<tr style="background-color: #f5f5f5; font-weight: bold;">';
      // Span all 6 fixed columns (employee, department, position, required, actual, balance) before day columns
      htmlTable += '<td colspan="6" style="border: 1px solid #ddd; padding: 5px;">' + t('schedule.workersCount', 'Darbuotojų skaičius') + '</td>';
      monthDates.forEach(({ day, date }) => {
        const dateYear = date.getFullYear();
        const dateMonth = String(date.getMonth() + 1).padStart(2, '0');
        const dateDay = String(date.getDate()).padStart(2, '0');
        const cellDate = `${dateYear}-${dateMonth}-${dateDay}`;
        
        const workersCount = filteredCompanyView.filter(emp =>
          shouldCountEmployeeForWorkersCount(emp, cellDate, year, month, day)
        ).length;
        
        const isWeekend = date.getDay() === 0 || date.getDay() === 6;
        const borderStyle = isWeekend ? '1px solid #5c6bc0' : '1px solid #ddd';
        htmlTable += `<td style="border: ${borderStyle}; padding: 5px; text-align: center;">${workersCount}</td>`;
      });
      htmlTable += '</tr>';
      
      htmlTable += '</table></body></html>';

      // Create plain text fallback (TSV format)
      // Add title row
      const titleRow = monthTitle;
      const headers = [t('schedule.employee'), t('schedule.department'), t('schedule.position'), t('schedule.requiredHoursLabel'), t('schedule.workHours'), t('schedule.displayedHours'), ...monthDates.map(({ day }) => day.toString())];
      const headerRow = headers.join('\t');
      
      const dataRows = filteredCompanyView.map(employee => {
        const totalActualHours = calculateDisplayedHours(employee, year, month);
        let employeeRequired = 0;
        if (employee.workAccountingType === 'summary') {
          employeeRequired = calculateSummaryAccountingHours(employee, year, month, filteredCompanyView);
        } else {
          const monthlyNorm = Number(monthlyRequirement?.requiredHours) || 0;
          const etatas = employee.etatas || 1;
          employeeRequired = monthlyNorm * etatas;
        }
        const balance = Math.round((totalActualHours - employeeRequired) * 100) / 100;
        const dateCells = monthDates.map(({ day, date }) => {
          const dateYear = date.getFullYear();
          const dateMonth = String(date.getMonth() + 1).padStart(2, '0');
          const dateDay = String(date.getDate()).padStart(2, '0');
          const cellDate = `${dateYear}-${dateMonth}-${dateDay}`;
          let schedule = employee.schedules.find((s: WorkScheduleEntry) => s.date === cellDate);
          if (!schedule) {
            const alternativeFormats = [
              `${year}-${(month + 1).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`,
              `${year}/${(month + 1).toString().padStart(2, '0')}/${day.toString().padStart(2, '0')}`,
              `${day.toString().padStart(2, '0')}-${(month + 1).toString().padStart(2, '0')}-${year}`,
              `${day.toString().padStart(2, '0')}/${(month + 1).toString().padStart(2, '0')}/${year}`,
              date.toLocaleDateString('en-CA')
            ];
            for (const format of alternativeFormats) {
              schedule = employee.schedules.find((s: WorkScheduleEntry) => s.date === format);
              if (schedule) break;
            }
          }
          
          if (schedule) {
            const abbrev = getStatusAbbreviation(schedule.status);
            if (schedule.startTime && schedule.endTime) {
              const hours = getScheduleHours(schedule).toFixed(1);
              return `${abbrev} ${schedule.startTime.slice(0, 5)}-${schedule.endTime.slice(0, 5)} ${hours}h`;
            }
            return abbrev;
          }
          return '';
        });
        
        return [
          employee.name || '',
          employee.department || '',
          employee.position || '',
          employeeRequired.toFixed(1),
          totalActualHours.toFixed(1),
          employeeRequired === 0 ? '-' : balance.toFixed(2),
          ...dateCells
        ].join('\t');
      });
      
      const plainText = [titleRow, headerRow, ...dataRows].join('\n');

      // Copy to clipboard with both HTML and plain text formats
      const htmlBlob = new Blob([htmlTable], { type: 'text/html' });
      const textBlob = new Blob([plainText], { type: 'text/plain' });
      
      const clipboardItem = new ClipboardItem({
        'text/html': htmlBlob,
        'text/plain': textBlob
      });
      
      await navigator.clipboard.write([clipboardItem]);
      
      setSuccess('Grafikas nukopijuotas į iškarpinę! Galite įklijuoti į Excel.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error copying schedule:', err);
      setError('Nepavyko nukopijuoti grafiko');
      setTimeout(() => setError(''), 3000);
    }
  };

  // Copy schedule to Excel (simple: Dirba = color + net hours only; no time range or break)
  const handleCopyScheduleToExcelSimple = async () => {
    if (filteredCompanyView.length === 0 || selectedDepartments.length === 0) {
      setError('Nėra duomenų kopijuoti');
      return;
    }

    try {
      const year = selectedMonth.getFullYear();
      const month = selectedMonth.getMonth();
      const daysInMonth = new Date(year, month + 1, 0).getDate();

      const monthDates = Array.from({ length: daysInMonth }, (_, i) => {
        const dayNumber = i + 1;
        const date = new Date(year, month, dayNumber);
        return { day: dayNumber, date: date };
      });

      const monthTitle = selectedMonth.toLocaleDateString('lt-LT', { month: 'long', year: 'numeric' });

      let htmlTable = '<html><head><meta charset="utf-8"></head><body><table border="1" cellpadding="3" cellspacing="0" style="border-collapse: collapse; font-family: Arial, sans-serif;">';
      const totalColumns = 6 + monthDates.length;
      htmlTable += '<tr style="background-color: #4a90e2; color: white;">';
      htmlTable += `<td colspan="${totalColumns}" style="border: 1px solid #ddd; padding: 10px; text-align: center; font-weight: bold; font-size: 1.2em;">${monthTitle}</td>`;
      htmlTable += '</tr>';
      htmlTable += '<tr style="background-color: #f0f0f0; font-weight: bold;">';
      htmlTable += '<th style="border: 1px solid #ddd; padding: 5px; text-align: left;">' + t('schedule.employee') + '</th>';
      htmlTable += '<th style="border: 1px solid #ddd; padding: 5px; text-align: left;">' + t('schedule.department') + '</th>';
      htmlTable += '<th style="border: 1px solid #ddd; padding: 5px; text-align: left;">' + t('schedule.position') + '</th>';
      htmlTable += '<th style="border: 1px solid #ddd; padding: 5px; text-align: center;">' + t('schedule.requiredHoursLabel') + '</th>';
      htmlTable += '<th style="border: 1px solid #ddd; padding: 5px; text-align: center;">' + t('schedule.workHours') + '</th>';
      htmlTable += '<th style="border: 1px solid #ddd; padding: 5px; text-align: center;">' + t('schedule.displayedHours') + '</th>';
      monthDates.forEach(({ day, date }) => {
        const isWeekend = date.getDay() === 0 || date.getDay() === 6;
        const borderStyle = isWeekend ? '1px solid #5c6bc0' : '1px solid #ddd';
        htmlTable += `<th style="border: ${borderStyle}; padding: 5px; text-align: center; min-width: 50px;">${day}</th>`;
      });
      htmlTable += '</tr>';

      filteredCompanyView.forEach((employee: CompanyEmployee) => {
        const isCurrentUser = user?.id === employee.userId;
        const isPinned = employee.isPinned || pinnedUsers.has(employee.userId);
        const rowBgColor = isCurrentUser ? '#e8f5e8' : (isPinned ? '#fff9e6' : '#ffffff');
        const rowBorderColor = isCurrentUser ? '#4caf50' : (isPinned ? '#ffc107' : '#ddd');
        htmlTable += `<tr bgcolor="${rowBgColor}" style="background-color: ${rowBgColor}; border-top: 2px solid ${rowBorderColor}; border-bottom: 2px solid ${rowBorderColor};">`;
        htmlTable += `<td style="border: 1px solid #ddd; padding: 5px; font-weight: bold; color: ${isCurrentUser ? '#2e7d32' : 'inherit'};">${employee.name || ''}</td>`;
        htmlTable += `<td style="border: 1px solid #ddd; padding: 5px;">${employee.department || ''}</td>`;
        htmlTable += `<td style="border: 1px solid #ddd; padding: 5px;">${employee.position || ''}</td>`;
        let employeeRequired = 0;
        if (employee.workAccountingType === 'summary') {
          employeeRequired = calculateSummaryAccountingHours(employee, year, month, filteredCompanyView);
        } else {
          const monthlyNorm = Number(monthlyRequirement?.requiredHours) || 0;
          const etatas = employee.etatas || 1;
          employeeRequired = monthlyNorm * etatas;
        }
        htmlTable += `<td style="border: 1px solid #ddd; padding: 5px; text-align: center;">${employeeRequired.toFixed(1)}</td>`;
        const totalActualHours = calculateDisplayedHours(employee, year, month);
        htmlTable += `<td style="border: 1px solid #ddd; padding: 5px; text-align: center;">${totalActualHours.toFixed(1)}</td>`;
        const balance = Math.round((totalActualHours - employeeRequired) * 100) / 100;
        htmlTable += `<td style="border: 1px solid #ddd; padding: 5px; text-align: center;">${employeeRequired === 0 ? '-' : balance.toFixed(2)}</td>`;

        monthDates.forEach(({ day, date }) => {
          const dateYear = date.getFullYear();
          const dateMonth = String(date.getMonth() + 1).padStart(2, '0');
          const dateDay = String(date.getDate()).padStart(2, '0');
          const cellDate = `${dateYear}-${dateMonth}-${dateDay}`;
          const isWeekend = date.getDay() === 0 || date.getDay() === 6;
          let schedule = employee.schedules.find((s: WorkScheduleEntry) => s.date === cellDate);
          if (!schedule) {
            const alternativeFormats = [
              `${year}-${(month + 1).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`,
              `${year}/${(month + 1).toString().padStart(2, '0')}/${day.toString().padStart(2, '0')}`,
              `${day.toString().padStart(2, '0')}-${(month + 1).toString().padStart(2, '0')}-${year}`,
              `${day.toString().padStart(2, '0')}/${(month + 1).toString().padStart(2, '0')}/${year}`,
              date.toLocaleDateString('en-CA')
            ];
            for (const format of alternativeFormats) {
              schedule = employee.schedules.find((s: WorkScheduleEntry) => s.date === format);
              if (schedule) break;
            }
          }
          const pendingApproval = pendingApprovals.find(req => req.userId === employee.userId && req.date === cellDate);
          const cellKey = `${employee.userId}-${cellDate}`;
          const hasExcursion = excursionsByUserDate.has(cellKey);
          const hasEdukacija = edukacijosByUserDate.has(cellKey);
          const hasManualHighlight = cellHighlights.has(cellKey);
          const hasBlueMark = cellBlueMarks.has(cellKey);
          const shouldHighlight = hasExcursion || hasEdukacija || hasManualHighlight;
          const highlightColor = hasExcursion ? '#7fff00' : hasEdukacija ? '#ff00de' : '#e34234';
          const highlightColorSecondary = hasExcursion && hasEdukacija ? '#ff00de' : null;
          const blueMarkColor = '#87CEEB';
          let cellBgColor = '#ffffff';
          if (pendingApproval) cellBgColor = '#9e9e9e';
          else if (schedule) cellBgColor = getStatusColor(schedule.status);
          let excelBgColor = cellBgColor;
          if (cellBgColor.startsWith('rgba')) {
            const rgbaMatch = cellBgColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
            if (rgbaMatch) {
              const r = parseInt(rgbaMatch[1]);
              const g = parseInt(rgbaMatch[2]);
              const b = parseInt(rgbaMatch[3]);
              excelBgColor = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
            }
          }
          let cellContent = '';
          if (schedule) {
            if (schedule.status === 'working' || schedule.status === 'remote') {
              cellContent = getScheduleHours(schedule).toFixed(1) + 'h';
            } else {
              cellContent = getStatusAbbreviation(schedule.status);
            }
          }
          if (pendingApproval) {
            cellContent = (cellContent ? cellContent + '<br>' : '') + '<span style="background-color: #666; color: white; padding: 1px 3px; border-radius: 2px; font-size: 0.7em;">LAUKIA</span>';
          }
          if (hasExcursion || hasEdukacija) {
            const indicators = [];
            if (hasExcursion) indicators.push('<span style="color: #000; font-weight: bold;">Ex</span>');
            if (hasEdukacija) indicators.push('<span style="color: #000; font-weight: bold;">Ed</span>');
            cellContent = (cellContent ? cellContent + '<br>' : '') + indicators.join(' ');
          }
          let borderTop = '1px solid #ddd';
          let borderRight = '1px solid #ddd';
          let borderBottom = '1px solid #ddd';
          let borderLeft = '1px solid #ddd';
          if (isWeekend) {
            borderTop = borderRight = borderBottom = borderLeft = '1px solid #5c6bc0';
          }
          if (!pendingApproval) {
            if (shouldHighlight && hasBlueMark) {
              borderLeft = `3px solid ${highlightColor}`;
              borderTop = `3px solid ${highlightColor}`;
              borderRight = `3px solid ${blueMarkColor}`;
              borderBottom = `3px solid ${blueMarkColor}`;
            } else if (shouldHighlight && highlightColorSecondary) {
              borderLeft = `3px solid ${highlightColor}`;
              borderTop = `3px solid ${highlightColor}`;
              borderRight = `3px solid ${highlightColorSecondary}`;
              borderBottom = `3px solid ${highlightColorSecondary}`;
            } else if (shouldHighlight) {
              borderTop = borderRight = borderBottom = borderLeft = `3px solid ${highlightColor}`;
            } else if (hasBlueMark) {
              borderTop = borderRight = borderBottom = borderLeft = `3px solid ${blueMarkColor}`;
            }
          }
          const borderStyle = `border-top: ${borderTop}; border-right: ${borderRight}; border-bottom: ${borderBottom}; border-left: ${borderLeft};`;
          htmlTable += `<td bgcolor="${excelBgColor}" style="${borderStyle} padding: 3px; text-align: center; background-color: ${excelBgColor}; vertical-align: top; font-size: 0.85em;">${cellContent || ''}</td>`;
        });
        htmlTable += '</tr>';
      });

      htmlTable += '<tr style="background-color: #f5f5f5; font-weight: bold;">';
      htmlTable += '<td colspan="6" style="border: 1px solid #ddd; padding: 5px;">' + t('schedule.workersCount', 'Darbuotojų skaičius') + '</td>';
      monthDates.forEach(({ day, date }) => {
        const dateYear = date.getFullYear();
        const dateMonth = String(date.getMonth() + 1).padStart(2, '0');
        const dateDay = String(date.getDate()).padStart(2, '0');
        const cellDate = `${dateYear}-${dateMonth}-${dateDay}`;
        const workersCount = filteredCompanyView.filter(emp =>
          shouldCountEmployeeForWorkersCount(emp, cellDate, year, month, day)
        ).length;
        const isWeekend = date.getDay() === 0 || date.getDay() === 6;
        const borderStyle = isWeekend ? '1px solid #5c6bc0' : '1px solid #ddd';
        htmlTable += `<td style="border: ${borderStyle}; padding: 5px; text-align: center;">${workersCount}</td>`;
      });
      htmlTable += '</tr></table></body></html>';

      const titleRow = monthTitle;
      const headers = [t('schedule.employee'), t('schedule.department'), t('schedule.position'), t('schedule.requiredHoursLabel'), t('schedule.workHours'), t('schedule.displayedHours'), ...monthDates.map(({ day }) => day.toString())];
      const headerRow = headers.join('\t');
      const dataRows = filteredCompanyView.map(employee => {
        const totalActualHours = calculateDisplayedHours(employee, year, month);
        let employeeRequired = 0;
        if (employee.workAccountingType === 'summary') {
          employeeRequired = calculateSummaryAccountingHours(employee, year, month, filteredCompanyView);
        } else {
          const monthlyNorm = Number(monthlyRequirement?.requiredHours) || 0;
          const etatas = employee.etatas || 1;
          employeeRequired = monthlyNorm * etatas;
        }
        const balance = Math.round((totalActualHours - employeeRequired) * 100) / 100;
        const dateCells = monthDates.map(({ day, date }) => {
          const dateYear = date.getFullYear();
          const dateMonth = String(date.getMonth() + 1).padStart(2, '0');
          const dateDay = String(date.getDate()).padStart(2, '0');
          const cellDate = `${dateYear}-${dateMonth}-${dateDay}`;
          let schedule = employee.schedules.find((s: WorkScheduleEntry) => s.date === cellDate);
          if (!schedule) {
            const alternativeFormats = [
              `${year}-${(month + 1).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`,
              `${year}/${(month + 1).toString().padStart(2, '0')}/${day.toString().padStart(2, '0')}`,
              `${day.toString().padStart(2, '0')}-${(month + 1).toString().padStart(2, '0')}-${year}`,
              `${day.toString().padStart(2, '0')}/${(month + 1).toString().padStart(2, '0')}/${year}`,
              date.toLocaleDateString('en-CA')
            ];
            for (const format of alternativeFormats) {
              schedule = employee.schedules.find((s: WorkScheduleEntry) => s.date === format);
              if (schedule) break;
            }
          }
          if (schedule) {
            if (schedule.status === 'working' || schedule.status === 'remote') {
              return getScheduleHours(schedule).toFixed(1) + 'h';
            }
            return getStatusAbbreviation(schedule.status);
          }
          return '';
        });
        return [employee.name || '', employee.department || '', employee.position || '', employeeRequired.toFixed(1), totalActualHours.toFixed(1), employeeRequired === 0 ? '-' : balance.toFixed(2), ...dateCells].join('\t');
      });
      const plainText = [titleRow, headerRow, ...dataRows].join('\n');

      const htmlBlob = new Blob([htmlTable], { type: 'text/html' });
      const textBlob = new Blob([plainText], { type: 'text/plain' });
      await navigator.clipboard.write([new ClipboardItem({ 'text/html': htmlBlob, 'text/plain': textBlob })]);

      setSuccess('Grafikas (trumpa forma) nukopijuotas į iškarpinę! Galite įklijuoti į Excel.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error copying schedule (simple):', err);
      setError('Nepavyko nukopijuoti grafiko');
      setTimeout(() => setError(''), 3000);
    }
  };

  const getAuditActionLabel = (actionType: string) => {
    const map: Record<string, string> = {
      cell_create: t('schedule.logs.actions.cellCreate', 'Sukurtas langelio įrašas'),
      cell_update: t('schedule.logs.actions.cellUpdate', 'Atnaujintas langelio įrašas'),
      cell_delete: t('schedule.logs.actions.cellDelete', 'Ištrintas langelio įrašas'),
      cell_approve: t('schedule.logs.actions.cellApprove', 'Langelio įrašas patvirtintas'),
      change_request_submit: t('schedule.logs.actions.changeRequestSubmit', 'Pateiktas pakeitimo prašymas'),
      change_request_approve: t('schedule.logs.actions.changeRequestApprove', 'Pakeitimo prašymas patvirtintas'),
      change_request_reject: t('schedule.logs.actions.changeRequestReject', 'Pakeitimo prašymas atmestas'),
      change_request_cancel: t('schedule.logs.actions.changeRequestCancel', 'Pakeitimo prašymas atšauktas'),
      month_lock: t('schedule.logs.actions.monthLock', 'Mėnesių užšaldymas: užšaldyta'),
      month_unlock: t('schedule.logs.actions.monthUnlock', 'Mėnesių užšaldymas: atšildyta'),
      month_scheduling_open: t('schedule.logs.actions.monthSchedulingOpen', 'Mėnesių užšaldymas: autom. patvirtinimas atidarytas'),
      month_scheduling_close: t('schedule.logs.actions.monthSchedulingClose', 'Mėnesių užšaldymas: autom. patvirtinimas uždarytas'),
      highlight_create: t('schedule.logs.actions.highlightCreate', 'Pažymėtas langelis (highlight)'),
      highlight_delete: t('schedule.logs.actions.highlightDelete', 'Pašalintas highlight'),
      blue_mark_create: t('schedule.logs.actions.blueMarkCreate', 'Pažymėtas langelis (mėlyna)'),
      blue_mark_delete: t('schedule.logs.actions.blueMarkDelete', 'Pašalinta mėlyna žyma'),
      department_templates_update: t('schedule.logs.actions.departmentTemplatesUpdate', 'Atnaujinti skyrių šablonai'),
      remote_weekly_limit_set: t('schedule.logs.actions.remoteWeeklyLimitSet', 'Nuotolio dienų limitas pakeistas'),
      scheduling_auto_close_days_set: t('schedule.logs.actions.schedulingAutoCloseDaysSet', 'Autom. išjungimo dienų skaičius'),
      scheduling_close_announcement_publish: t('schedule.logs.actions.schedulingCloseAnnouncementPublish', 'Paskelbtas autom. išjungimo pranešimas'),
      scheduling_close_announcement_message_set: t('schedule.logs.actions.schedulingCloseAnnouncementMessageSet', 'Pakeistas autom. išjungimo pranešimo tekstas'),
      scheduling_auto_freeze_settings_set: t('schedule.logs.actions.schedulingAutoFreezeSettingsSet', 'Autom. užšaldymo nustatymai')
    };
    return map[actionType] || actionType;
  };

  const formatAuditDepartmentScope = (departments?: string[] | null) => {
    if (!departments || departments.length === 0 || departments.includes('*')) {
      return t('common.lockScopeAllDepartments', 'Visi skyriai');
    }
    return departments.join(', ');
  };

  const formatAuditActorName = (log: WorkScheduleAuditLogEntry) => {
    if (log.meta?.system || log.meta?.source === 'auto_days_before_month' || log.meta?.source === 'auto_days_before_month_end') {
      return t('schedule.logs.systemActor', 'Sistema');
    }
    if (log.actor) {
      return `${log.actor.firstName} ${log.actor.lastName}`;
    }
    return '-';
  };

  const formatAuditCellSourceHint = (source?: string | null) => {
    if (!source) return null;
    if (source === 'bulk') return t('schedule.logs.source.bulk', 'šablonas / bulk');
    if (source === 'department_autofill') return t('schedule.logs.source.departmentAutofill', 'skyriaus autofill');
    if (source === 'state_holiday_backfill') return t('schedule.logs.source.stateHolidayBackfill', 'valstybinė šventė');
    if (source === 'admin_shortcut_cancel_remote_work') return t('schedule.logs.shortcutSource', 'Admin shortcut');
    if (source === 'bulk_approve') return t('schedule.logs.source.bulkApprove', 'masinis patvirtinimas');
    return null;
  };

  const renderAuditSummary = (log: WorkScheduleAuditLogEntry) => {
    if (
      log.actionType === 'month_lock' ||
      log.actionType === 'month_unlock' ||
      log.actionType === 'month_scheduling_open' ||
      log.actionType === 'month_scheduling_close'
    ) {
      const scope = formatAuditDepartmentScope(
        log.meta?.departments ||
          log.afterState?.lockedDepartments ||
          log.afterState?.schedulingOpenDepartments
      );
      const reason = log.meta?.reason ? ` — ${log.meta.reason}` : '';
      if (log.actionType === 'month_lock') {
        if (log.meta?.source === 'auto_days_before_month_end') {
          return t(
            'schedule.logs.summary.monthLockAuto',
            'Automatinis užšaldymas ({{days}} d. iki mėnesio pabaigos): {{scope}}',
            { scope, days: log.meta?.days ?? '?' }
          );
        }
        return t('schedule.logs.summary.monthLock', 'Užšaldymas: {{scope}}{{reason}}', {
          scope,
          reason
        });
      }
      if (log.actionType === 'month_unlock') {
        return t('schedule.logs.summary.monthUnlock', 'Atšildymas: {{scope}}', { scope });
      }
      if (log.actionType === 'month_scheduling_open') {
        return t('schedule.logs.summary.monthSchedulingOpen', 'Autom. patvirtinimas atidarytas: {{scope}}', {
          scope
        });
      }
      if (log.meta?.source === 'auto_days_before_month') {
        return t(
          'schedule.logs.summary.monthSchedulingCloseAuto',
          'Automatinis išjungimas ({{days}} d. iki mėnesio): {{scope}}',
          { scope, days: log.meta?.days ?? '?' }
        );
      }
      return t('schedule.logs.summary.monthSchedulingClose', 'Autom. patvirtinimas uždarytas: {{scope}}', {
        scope
      });
    }

    if (log.actionType === 'cell_approve') {
      const hint = formatAuditCellSourceHint(log.meta?.source);
      const base = t('schedule.logs.summary.cellApprove', 'Patvirtinta');
      return hint ? `${base} (${hint})` : base;
    }

    if (log.actionType === 'highlight_create' || log.actionType === 'highlight_delete') {
      return log.actionType === 'highlight_create'
        ? t('schedule.logs.summary.highlightCreate', 'Highlight pridėtas')
        : t('schedule.logs.summary.highlightDelete', 'Highlight pašalintas');
    }

    if (log.actionType === 'blue_mark_create' || log.actionType === 'blue_mark_delete') {
      return log.actionType === 'blue_mark_create'
        ? t('schedule.logs.summary.blueMarkCreate', 'Mėlyna žyma pridėta')
        : t('schedule.logs.summary.blueMarkDelete', 'Mėlyna žyma pašalinta');
    }

    if (log.actionType === 'department_templates_update') {
      const beforeCount = log.beforeState?.templateCount ?? '-';
      const afterCount = log.afterState?.templateCount ?? '-';
      return t('schedule.logs.summary.departmentTemplatesUpdate', 'Šablonai: {{before}} → {{after}}', {
        before: beforeCount,
        after: afterCount
      });
    }

    if (log.actionType === 'remote_weekly_limit_set') {
      const before = log.beforeState?.limit ?? 2;
      const after = log.afterState?.limit ?? 2;
      return t('schedule.logs.summary.remoteWeeklyLimitSet', 'Nuotolio limitas: {{before}} → {{after}} / sav.', {
        before,
        after
      });
    }

    if (log.actionType === 'scheduling_auto_close_days_set') {
      const before = log.beforeState?.days ?? '-';
      const after = log.afterState?.days ?? '-';
      return t('schedule.logs.summary.schedulingAutoCloseDaysSet', 'Autom. išjungimas: {{before}} → {{after}} d.', {
        before,
        after
      });
    }

    if (log.actionType === 'scheduling_close_announcement_publish') {
      const campaignId = log.afterState?.campaignId || log.meta?.campaignId || '-';
      const src =
        log.meta?.source === 'manual_trigger'
          ? t('schedule.logs.source.manualTrigger', 'testas')
          : t('schedule.logs.source.auto', 'automatinis');
      return t('schedule.logs.summary.announcementPublish', 'Kampanija {{campaignId}} ({{source}})', {
        campaignId,
        source: src
      });
    }

    if (log.actionType === 'scheduling_close_announcement_message_set') {
      return t('schedule.logs.summary.announcementMessageSet', 'Pranešimo tekstas atnaujintas');
    }

    if (log.actionType === 'scheduling_auto_freeze_settings_set') {
      const beforeEnabled = log.beforeState?.enabled
        ? t('common.enabled', 'Įjungta')
        : t('common.disabled', 'Išjungta');
      const afterEnabled = log.afterState?.enabled
        ? t('common.enabled', 'Įjungta')
        : t('common.disabled', 'Išjungta');
      return t(
        'schedule.logs.summary.autoFreezeSettingsSet',
        'Autom. užšaldymas: {{beforeEnabled}} / {{beforeDays}} d. → {{afterEnabled}} / {{afterDays}} d.',
        {
          beforeEnabled,
          beforeDays: log.beforeState?.days ?? '-',
          afterEnabled,
          afterDays: log.afterState?.days ?? '-'
        }
      );
    }

    const beforeStatus = log.beforeState?.status || '-';
    const afterStatus = log.afterState?.status || '-';
    const sourceHint = formatAuditCellSourceHint(log.meta?.source);
    return sourceHint
      ? `${beforeStatus} -> ${afterStatus} (${sourceHint})`
      : `${beforeStatus} -> ${afterStatus}`;
  };

  const renderAuditLogsView = () => (
    <Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(5, minmax(0, 1fr))' }, gap: 2, mb: 2 }}>
        <DatePicker
          label={t('schedule.logs.filters.month', 'Mėnuo')}
          views={['year', 'month']}
          value={auditMonthFilter}
          onChange={(value: Date | null) => {
            if (value) setAuditMonthFilter(value);
          }}
          slotProps={{ textField: { fullWidth: true } }}
        />
        <FormControl fullWidth>
          <InputLabel>{t('schedule.logs.filters.department', 'Departamentas')}</InputLabel>
          <Select
            value={auditDepartmentFilter}
            onChange={(e) => setAuditDepartmentFilter(e.target.value)}
            label={t('schedule.logs.filters.department', 'Departamentas')}
          >
            <MenuItem value="">{t('common.all', 'Visi')}</MenuItem>
            {availableDepartments.map(dep => (
              <MenuItem key={dep} value={dep}>{dep}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth>
          <InputLabel>{t('schedule.logs.filters.actor', 'Kas pakeitė')}</InputLabel>
          <Select
            value={auditActorFilter}
            onChange={(e) => setAuditActorFilter(e.target.value)}
            label={t('schedule.logs.filters.actor', 'Kas pakeitė')}
          >
            <MenuItem value="">{t('common.all', 'Visi')}</MenuItem>
            {allUsers.map(u => (
              <MenuItem key={u.id} value={u.id}>{`${u.firstName} ${u.lastName}`}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth>
          <InputLabel>{t('schedule.logs.filters.employee', 'Darbuotojas')}</InputLabel>
          <Select
            value={auditEmployeeFilter}
            onChange={(e) => setAuditEmployeeFilter(e.target.value)}
            label={t('schedule.logs.filters.employee', 'Darbuotojas')}
          >
            <MenuItem value="">{t('common.all', 'Visi')}</MenuItem>
            {allUsers.map(u => (
              <MenuItem key={u.id} value={u.id}>{`${u.firstName} ${u.lastName}`}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth>
          <InputLabel>{t('schedule.logs.filters.action', 'Veiksmas')}</InputLabel>
          <Select
            value={auditActionFilter}
            onChange={(e) => setAuditActionFilter(e.target.value)}
            label={t('schedule.logs.filters.action', 'Veiksmas')}
          >
            <MenuItem value="">{t('common.all', 'Visi')}</MenuItem>
            {[
              'cell_create',
              'cell_update',
              'cell_delete',
              'cell_approve',
              'change_request_submit',
              'change_request_approve',
              'change_request_reject',
              'change_request_cancel',
              'month_lock',
              'month_unlock',
              'month_scheduling_open',
              'month_scheduling_close',
              'highlight_create',
              'highlight_delete',
              'blue_mark_create',
              'blue_mark_delete',
              'department_templates_update',
              'remote_weekly_limit_set',
              'scheduling_auto_close_days_set',
              'scheduling_close_announcement_publish',
              'scheduling_close_announcement_message_set',
              'scheduling_auto_freeze_settings_set'
            ].map((action) => (
              <MenuItem key={action} value={action}>{getAuditActionLabel(action)}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('schedule.logs.columns.time', 'Laikas')}</TableCell>
              <TableCell>{t('schedule.logs.columns.action', 'Veiksmas')}</TableCell>
              <TableCell>{t('schedule.logs.columns.employee', 'Darbuotojas')}</TableCell>
              <TableCell>{t('schedule.logs.columns.date', 'Data')}</TableCell>
              <TableCell>{t('schedule.logs.columns.department', 'Departamentas')}</TableCell>
              <TableCell>{t('schedule.logs.columns.actor', 'Pakeitė')}</TableCell>
              <TableCell>{t('schedule.logs.columns.summary', 'Santrauka')}</TableCell>
              <TableCell>{t('common.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {auditLogsLoading ? (
              <TableRow>
                <TableCell colSpan={8}>{t('common.loading', 'Kraunama...')}</TableCell>
              </TableRow>
            ) : auditLogs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8}>{t('schedule.logs.noData', 'Įrašų nerasta')}</TableCell>
              </TableRow>
            ) : (
              auditLogs.map((log) => (
                <TableRow key={log.id}>
                  <TableCell>{new Date(log.createdAt).toLocaleString('lt-LT')}</TableCell>
                  <TableCell>{getAuditActionLabel(log.actionType)}</TableCell>
                  <TableCell>{log.targetUser ? `${log.targetUser.firstName} ${log.targetUser.lastName}` : (log.actionType?.startsWith('month_') || log.actionType?.startsWith('scheduling_') || log.actionType === 'department_templates_update' ? t('common.monthLocks', 'Mėnesių užšaldymas') : '-')}</TableCell>
                  <TableCell>{log.targetDate || (log.actionType?.startsWith('month_') || log.actionType?.startsWith('scheduling_') || log.actionType === 'department_templates_update' ? log.targetMonth : '-')}</TableCell>
                  <TableCell>{
                    log.department ||
                    (log.actionType?.startsWith('month_')
                      ? formatAuditDepartmentScope(log.meta?.departments)
                      : '-')
                  }</TableCell>
                  <TableCell>{formatAuditActorName(log)}</TableCell>
                  <TableCell>{renderAuditSummary(log)}</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setSelectedAuditLog(log);
                        setAuditDetailsOpen(true);
                      }}
                    >
                      {t('schedule.logs.viewDetails', 'Detalės')}
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
        <Typography variant="body2">
          {t('schedule.logs.pagination.total', 'Iš viso: {{count}}', { count: auditLogTotal })}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button
            variant="outlined"
            size="small"
            disabled={auditLogPage <= 1 || auditLogsLoading}
            onClick={() => setAuditLogPage(prev => Math.max(prev - 1, 1))}
          >
            {t('common.previous', 'Ankstesnis')}
          </Button>
          <Typography variant="body2">{auditLogPage}</Typography>
          <Button
            variant="outlined"
            size="small"
            disabled={auditLogsLoading || auditLogPage * auditLogLimit >= auditLogTotal}
            onClick={() => setAuditLogPage(prev => prev + 1)}
          >
            {t('common.next', 'Kitas')}
          </Button>
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <Select
              value={String(auditLogLimit)}
              onChange={(e) => setAuditLogLimit(Number(e.target.value))}
            >
              {[25, 50, 100].map((value) => (
                <MenuItem key={value} value={String(value)}>{value}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>
    </Box>
  );

  // Detect break conflicts for a specific department on a specific date
  const detectBreakConflicts = (date: string, department: string) => {
    // Filter employees by department
    const departmentEmployees = filteredCompanyView.filter(emp => emp.department === department);
    
    // Get employees with working status and break times on this date
    const employeesWithBreaks = departmentEmployees.filter(employee => {
      const schedule = employee.schedules.find(s => s.date === date);
      return schedule && 
             (schedule.status === 'working' || schedule.status === 'remote') &&
             schedule.breakStartTime && 
             schedule.breakEndTime;
    });

    if (employeesWithBreaks.length < 2) {
      return { hasConflict: false, count: 0 };
    }

    // Check for overlapping breaks
    let maxOverlapCount = 0;
    
    for (let i = 0; i < employeesWithBreaks.length; i++) {
      let overlapCount = 1; // Start with current employee
      
      for (let j = 0; j < employeesWithBreaks.length; j++) {
        if (i === j) continue;
        
        const emp1 = employeesWithBreaks[i];
        const emp2 = employeesWithBreaks[j];
        const schedule1 = emp1.schedules.find(s => s.date === date);
        const schedule2 = emp2.schedules.find(s => s.date === date);
        
        if (schedule1 && schedule2) {
          // Check if breaks overlap
          const break1Start = new Date(`2000-01-01T${schedule1.breakStartTime}`);
          const break1End = new Date(`2000-01-01T${schedule1.breakEndTime}`);
          const break2Start = new Date(`2000-01-01T${schedule2.breakStartTime}`);
          const break2End = new Date(`2000-01-01T${schedule2.breakEndTime}`);
          
          // Check for overlap
          if (break1Start < break2End && break2Start < break1End) {
            overlapCount++;
          }
        }
      }
      
      maxOverlapCount = Math.max(maxOverlapCount, overlapCount);
    }

    // Apply department-specific thresholds
    let hasConflict = false;
    if (department === 'Ekspozicija') {
      hasConflict = maxOverlapCount > 2; // More than 2 (3 or more)
    } else if (department === 'Kasos') {
      hasConflict = maxOverlapCount >= 2; // 2 or more
    }

    return { hasConflict, count: maxOverlapCount };
  };

  const renderCompanyView = () => {
    if (departmentViewLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <Typography>Loading company view...</Typography>
        </Box>
      );
    }

    // Generate dates for the selected month
    const year = selectedMonth.getFullYear();
    const month = selectedMonth.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    const monthDates = Array.from({ length: daysInMonth }, (_, i) => {
      const dayNumber = i + 1;
      const date = new Date(year, month, dayNumber);
      
      return {
        day: dayNumber,
        date: date,
        dayOfWeek: date.getDay(),
        dayAcronym: t(`schedule.dayAcronyms.${['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][date.getDay()]}`)
      };
    });

    // Format month title
    const monthTitle = selectedMonth.toLocaleDateString('lt-LT', { 
      month: 'long', 
      year: 'numeric' 
    });

    const scheduleHeaderRowHeight = 56;
    const conflictHeaderCellBaseSx = {
      top: scheduleHeaderRowHeight,
      zIndex: 9
    };

    return (
      <Box>

        {/* Handle empty company state only; keep filters visible even with no departments selected */}
        {companyView.length === 0 ? (
          <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            {t('schedule.noCompanyData')}
          </Typography>
        ) : (
          <Box>

        {/* Month title and selector */}
        <Box sx={{ 
          mb: 3, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between', 
          flexWrap: 'wrap', 
          gap: 2,
          '@media (max-width: 600px)': {
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1
          }
        }}>
          {/* Centered title */}
          <Typography variant="h4" sx={{ 
            fontWeight: 'bold', 
            color: 'primary.main', 
            flex: '1 1 auto', 
            textAlign: 'center', 
            minWidth: 'fit-content',
            '@media (max-width: 600px)': {
              flex: 'none',
              textAlign: 'center'
            }
          }}>
            {monthTitle}
          </Typography>
          {/* Right-aligned selector */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2, 
            flexShrink: 0,
            '@media (max-width: 600px)': {
              flexShrink: 'unset',
              justifyContent: 'center'
            }
          }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              {t('schedule.selectMonth')}:
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton onClick={handlePreviousMonth} size="small" aria-label="Previous month">
                <ChevronLeft />
              </IconButton>
              <DatePicker
                value={selectedMonth}
                onChange={(newValue) => setSelectedMonth(newValue || new Date())}
                views={['month', 'year']}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { 
                      minWidth: 200,
                      '@media (max-width: 400px)': {
                        minWidth: 150
                      }
                    }
                  }
                }}
              />
              <IconButton onClick={handleNextMonth} size="small" aria-label="Next month">
                <ChevronRight />
              </IconButton>
            </Box>
          </Box>
        </Box>


        {/* Monthly work requirement + state holiday panels side by side */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1, minWidth: 280, p: 2, backgroundColor: '#e8f5e8', borderRadius: 1, border: '1px solid #4caf50' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                {t('schedule.requiredHours', 'Reikia išdirbt')}:
              </Typography>
              
              {monthlyRequirementLoading ? (
                <Typography variant="body2" color="text.secondary">
                  Kraunama...
                </Typography>
              ) : monthlyRequirement ? (
                <>
                  <TextField
                    size="small"
                    type="number"
                    inputProps={{ min: 0, step: 0.5 }}
                    placeholder={t('schedule.requiredHoursPlaceholder', 'Įveskite valandas')}
                    value={requiredHours}
                    onChange={handleRequiredHoursChange}
                    sx={{ width: 150 }}
                    disabled={!hasPermission('schedule.manage')}
                  />
                  <Typography variant="body2" color="text.secondary">
                    valandos per mėnesį
                  </Typography>
                  
                  {/* Show who set this requirement */}
                  <Box sx={{ ml: 'auto', textAlign: 'right' }}>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      Nustatė: {monthlyRequirement.setter.firstName} {monthlyRequirement.setter.lastName}{' '}
                      {new Date(monthlyRequirement.setAt).toLocaleDateString('lt-LT')}
                    </Typography>
                  </Box>
                </>
              ) : (
                <>
                  {canApproveSchedules() ? (
                    <>
                      <TextField
                        size="small"
                        type="number"
                        inputProps={{ min: 0, step: 0.5 }}
                        placeholder={t('schedule.requiredHoursPlaceholder', 'Įveskite valandas')}
                        value={requiredHours}
                        onChange={handleRequiredHoursChange}
                        sx={{ width: 150 }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        valandos per mėnesį
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        (Nustatykite mėnesio reikalavimą)
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      Mėnesio reikalavimas nenustatytas
                    </Typography>
                  )}
                </>
              )}
            </Box>
          </Box>

          {canApproveSchedules() && (
            <Box sx={{ flex: 1, minWidth: 280, p: 2, backgroundColor: '#fce4ec', borderRadius: 1, border: '1px solid #e91e63' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#c2185b' }}>
                  {t('schedule.stateHolidayQuickSet', 'Valstybinė šventė - Greitas nustatymas')}:
                </Typography>
                <DatePicker
                  value={stateHolidayDate}
                  onChange={(newValue) => setStateHolidayDate(newValue || new Date())}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { width: 150 }
                    }
                  }}
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSetStateHoliday}
                  sx={{ 
                    backgroundColor: '#e91e63', 
                    '&:hover': { backgroundColor: '#c2185b' },
                    color: 'white'
                  }}
                  startIcon={<EventIcon />}
                >
                  {t('schedule.setStateHoliday', 'Nustatyti šventę')}
                </Button>
              </Box>
            </Box>
          )}
        </Box>

        {/* Suminė darbo apskaita section */}
        {(() => {
          const employeesWithSummaryAccounting = filteredCompanyView.filter(
            emp => emp.workAccountingType === 'summary'
          );
          
          if (employeesWithSummaryAccounting.length === 0) {
            return null;
          }
          
          return (
            <Box sx={{ mb: 3, p: 2, backgroundColor: '#e3f2fd', borderRadius: 1, border: '1px solid #2196f3' }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1976d2', mb: 2 }}>
                {t('schedule.summaryAccounting', 'Suminė darbo apskaita')}:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {employeesWithSummaryAccounting.map((employee) => {
                  const calculatedHours = calculateSummaryAccountingHours(
                    employee,
                    selectedMonth.getFullYear(),
                    selectedMonth.getMonth(),
                    filteredCompanyView
                  );
                  return (
                    <Typography key={employee.userId} variant="body2">
                      <strong>{employee.name}</strong>: {calculatedHours.toFixed(2)} valandos
                    </Typography>
                  );
                })}
              </Box>
            </Box>
          );
        })()}

        {/* Department Filter with Month status and refresh button */}
        {availableDepartments.length > 0 && (
          <Box sx={{ mb: 2, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1, border: '1px solid #e0e0e0' }}>
            {/* Title row */}
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1.5 }}>
              {t('schedule.departmentFilter', 'Skyrių filtras')}
            </Typography>

            {/* Actions row */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<TemplateIcon />}
                onClick={() => setTemplateDialogOpen(true)}
              >
                {t('schedule.weeklyTemplate')}
              </Button>
              {canManageDepartmentTemplates() && (
                <>
                  <Button
                    size="small"
                    variant="outlined"
                    color="secondary"
                    startIcon={<ApplyIcon />}
                    onClick={openDeptAutofillDialog}
                    disabled={isCurrentMonthLocked}
                  >
                    {t('schedule.departmentAutofill')}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<SettingsIcon />}
                    onClick={openDeptTemplatesDialog}
                  >
                    {t('schedule.departmentTemplatesManage')}
                  </Button>
                </>
              )}
              <Button
                size="small"
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setDialogOpen(true)}
              >
                {t('schedule.addEntry')}
              </Button>
            </Box>

            {/* Department chips — multi-select with view_all or template allowlist (>1 dept) */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              {availableDepartments.map((department) => (
                <Chip
                  key={department}
                  label={department}
                  onClick={canToggleDepartments ? () => handleDepartmentToggle(department) : undefined}
                  color={selectedDepartments.includes(department) ? 'primary' : 'default'}
                  variant={selectedDepartments.includes(department) ? 'filled' : 'outlined'}
                  sx={{ cursor: canToggleDepartments ? 'pointer' : 'default' }}
                />
              ))}
            </Box>

            {/* Tools row */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                {canApproveSchedules() && (
                  <>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ContentCopyIcon />}
                      onClick={handleCopyScheduleToExcel}
                      disabled={loading || filteredCompanyView.length === 0 || selectedDepartments.length === 0}
                    >
                      Kopijuoti į Excel
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ContentCopyIcon />}
                      onClick={handleCopyScheduleToExcelSimple}
                      disabled={loading || filteredCompanyView.length === 0 || selectedDepartments.length === 0}
                    >
                      Kopijuoti į Excel (Supaprastinta)
                    </Button>
                  </>
                )}
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<PrintIcon />}
                  onClick={() => window.print()}
                  disabled={!currentUserEmployee || currentUserMonthDays.length === 0}
                >
                  {t('schedule.printMySchedule', 'Spausdinti mano grafiką')}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={handleRefresh}
                  disabled={loading}
                >
                  {t('common.refresh')}
                </Button>
                <Tooltip title={showDetailsColumns ? 'Slėpti Skyrius, Pareigos, Etatas, Faktinės valandos, Valandų balansas' : 'Rodyti Skyrius, Pareigos, Etatas, Faktinės valandos, Valandų balansas'}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setShowDetailsColumns(prev => !prev)}
                    startIcon={showDetailsColumns ? <ChevronLeft /> : <ChevronRight />}
                  >
                    {showDetailsColumns ? t('schedule.hideColumns', 'Slėpti stulpelius') : t('schedule.showColumns', 'Rodyti stulpelius')}
                  </Button>
                </Tooltip>
                {canToggleDepartments && (
                  <>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleSelectAllDepartments}
                      disabled={selectedDepartments.length === availableDepartments.length}
                    >
                      {t('schedule.selectAll', 'Pasirinkti visus')}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleDeselectAllDepartments}
                      disabled={selectedDepartments.length === 0}
                    >
                      {t('schedule.deselectAll', 'Atžymėti visus')}
                    </Button>
                  </>
                )}
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  {t('schedule.monthStatus', 'Mėnesio būsena')}:
                </Typography>
                <Chip
                  size="small"
                  icon={isCurrentMonthLocked ? <LockIcon /> : <UnlockIcon />}
                  label={isCurrentMonthLocked ? t('common.monthLockBadgeFrozen', 'Užšaldyta') : t('common.monthLockBadgeOpen', 'Atidarytas')}
                  color={isCurrentMonthLocked ? 'info' : 'success'}
                />
              </Box>
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {canToggleDepartments
                ? t('schedule.departmentFilterHelp', 'Showing schedules for {count} of {total} departments', {
                    count: selectedDepartments.length,
                    total: availableDepartments.length
                  })
                : t('schedule.departmentFilterOwnOnly', 'Rodomas tik jūsų skyrius')}
            </Typography>
          </Box>
        )}

        {/* Month status and refresh button - fallback when no departments */}
        {availableDepartments.length === 0 && (
          <Box sx={{ mb: 3, px: 2, py: 2, backgroundColor: '#f8f9fa', borderRadius: 1, border: '1px solid #e0e0e0' }}>
            {/* Actions row */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<TemplateIcon />}
                onClick={() => setTemplateDialogOpen(true)}
              >
                {t('schedule.weeklyTemplate')}
              </Button>
              {canManageDepartmentTemplates() && (
                <>
                  <Button
                    size="small"
                    variant="outlined"
                    color="secondary"
                    startIcon={<ApplyIcon />}
                    onClick={openDeptAutofillDialog}
                    disabled={isCurrentMonthLocked}
                  >
                    {t('schedule.departmentAutofill')}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<SettingsIcon />}
                    onClick={openDeptTemplatesDialog}
                  >
                    {t('schedule.departmentTemplatesManage')}
                  </Button>
                </>
              )}
              <Button
                size="small"
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setDialogOpen(true)}
              >
                {t('schedule.addEntry')}
              </Button>
            </Box>

            {/* Tools row */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={handleRefresh}
                  disabled={loading}
                >
                  {t('common.refresh')}
                </Button>
                <Tooltip title={showDetailsColumns ? 'Slėpti Skyrius, Pareigos, Etatas, Faktinės valandos, Valandų balansas' : 'Rodyti Skyrius, Pareigos, Etatas, Faktinės valandos, Valandų balansas'}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setShowDetailsColumns(prev => !prev)}
                    startIcon={showDetailsColumns ? <ChevronLeft /> : <ChevronRight />}
                  >
                    {showDetailsColumns ? t('schedule.hideColumns', 'Slėpti stulpelius') : t('schedule.showColumns', 'Rodyti stulpelius')}
                  </Button>
                </Tooltip>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  {t('schedule.monthStatus', 'Mėnesio būsena')}:
                </Typography>
                <Chip
                  size="small"
                  icon={isCurrentMonthLocked ? <LockIcon /> : <UnlockIcon />}
                  label={isCurrentMonthLocked ? t('common.monthLockBadgeFrozen', 'Užšaldyta') : t('common.monthLockBadgeOpen', 'Atidarytas')}
                  color={isCurrentMonthLocked ? 'info' : 'success'}
                />
              </Box>
            </Box>
          </Box>
        )}

        {/* Single table for all company employees */}
        <Card>
          <CardContent>
            <TableContainer
              component={Paper}
              sx={{
                maxHeight: 'calc(100vh - 320px)',
                '@media (max-width: 600px)': {
                  maxHeight: 'calc(100vh - 200px)'
                }
              }}
            >
              <Table size="small" stickyHeader sx={{ borderCollapse: 'collapse' }}>
                <TableHead>
                  <TableRow key="company-view-header">
                    <TableCell sx={{ 
                      minWidth: 150, 
                      fontWeight: 'bold',
                      position: 'sticky',
                      left: 0,
                      top: 0,
                      zIndex: 11,
                      backgroundColor: 'background.paper',
                      borderRight: '2px solid #ddd'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Tooltip title={showDetailsColumns ? 'Slėpti Skyrius, Pareigos, Etatas, Faktinės valandos, Valandų balansas' : 'Rodyti Skyrius, Pareigos, Etatas, Faktinės valandos, Valandų balansas'}>
                          <IconButton
                            size="small"
                            onClick={() => setShowDetailsColumns(prev => !prev)}
                            sx={{ p: 0.5, color: 'primary.main' }}
                            aria-label={showDetailsColumns ? 'Slėpti stulpelius' : 'Rodyti stulpelius'}
                          >
                            {showDetailsColumns ? <ChevronLeft /> : <ChevronRight />}
                          </IconButton>
                        </Tooltip>
                        {t('schedule.employee')}
                      </Box>
                    </TableCell>
                    {showDetailsColumns && (
                      <>
                        <TableCell sx={{ minWidth: 120, fontWeight: 'bold', top: 0, zIndex: 10, backgroundColor: 'background.paper' }}>
                          {t('schedule.department')}
                        </TableCell>
                        <TableCell sx={{ minWidth: 100, fontWeight: 'bold', top: 0, zIndex: 10, backgroundColor: 'background.paper' }}>
                          {t('schedule.position')}
                        </TableCell>
                        <TableCell sx={{ minWidth: 100, fontWeight: 'bold', top: 0, zIndex: 10, backgroundColor: 'background.paper' }}>
                          {t('schedule.etatas', 'Etatas')}
                        </TableCell>
                        <TableCell sx={{ minWidth: 100, fontWeight: 'bold', top: 0, zIndex: 10, backgroundColor: 'background.paper' }}>
                          Faktinės valandos
                        </TableCell>
                        <TableCell sx={{ minWidth: 130, fontWeight: 'bold', top: 0, zIndex: 10, backgroundColor: 'background.paper' }}>
                          Valandų balansas
                        </TableCell>
                      </>
                    )}
                    {monthDates.map((entry, dayIndex) => {
                      const { day, dayAcronym, date } = entry;
                      // Fix timezone issue: use local date formatting instead of UTC
                      const year = date.getFullYear();
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const dayNum = String(date.getDate()).padStart(2, '0');
                      const cellDate = `${year}-${month}-${dayNum}`;
                      
                      // Company-wide: show šventė column even when filtered dept all works that day
                      const hasStateHoliday = stateHolidayDateSet.has(cellDate);
                      
                      const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                      const nextDate = monthDates[dayIndex + 1]?.date;
                      const isFridayBeforeSaturday = nextDate != null && nextDate.getDay() === 6;
                      const weekendBorder = '1px solid #5c6bc0';
                      const defaultBorder = '1px solid #ddd';
                      
                      return (
                        <TableCell 
                          key={day} 
                          align="center" 
                          sx={{ 
                            minWidth: 50, 
                            fontWeight: 'bold',
                            top: 0,
                            zIndex: 10,
                            backgroundColor: hasStateHoliday ? '#fce4ec' : '#e3f2fd',
                            ...(hasStateHoliday
                              ? { border: '3px solid #e91e63', boxShadow: '0 0 10px 2px rgba(233, 30, 99, 0.45)' }
                              : {
                                  borderLeft: isWeekend ? weekendBorder : defaultBorder,
                                  borderRight: (isWeekend || isFridayBeforeSaturday) ? weekendBorder : defaultBorder,
                                  borderTop: isWeekend ? weekendBorder : defaultBorder,
                                  borderBottom: isWeekend ? weekendBorder : defaultBorder
                                })
                          }}
                        >
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Typography variant="caption" sx={{ 
                              fontSize: '0.7rem', 
                              color: hasStateHoliday ? '#c2185b' : 'text.secondary',
                              fontWeight: hasStateHoliday ? 'bold' : 'normal'
                            }}>
                              {dayAcronym}
                            </Typography>
                            <Typography variant="body2" sx={{ 
                              fontWeight: 'bold',
                              color: hasStateHoliday ? '#e91e63' : 'inherit'
                            }}>
                              {day}
                            </Typography>
                            {hasStateHoliday && (
                              <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#e91e63', fontWeight: 'bold' }}>
                                Š
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                  
                  {/* Break Conflict Warning Row */}
                  <TableRow key="break-conflict-warning">
                    <TableCell sx={{ 
                      backgroundColor: '#fff3e0', 
                      fontWeight: 'bold',
                      position: 'sticky',
                      left: 0,
                      ...conflictHeaderCellBaseSx,
                      zIndex: 11,
                      borderRight: '2px solid #ddd',
                      textAlign: 'center'
                    }}>
                      <Typography variant="caption" sx={{ 
                        fontSize: '1rem', 
                        color: '#e65100', 
                        fontWeight: 'bold',
                        backgroundColor: '#ffcc02',
                        px: 1,
                        py: 0.5,
                        borderRadius: 1
                      }}>
                        {t('schedule.breakConflictWarning', 'Pertraukų konfliktų įspėjimas')}
                      </Typography>
                    </TableCell>
                    {showDetailsColumns && (
                      <>
                        <TableCell sx={{ ...conflictHeaderCellBaseSx, backgroundColor: '#fff3e0' }} />
                        <TableCell sx={{ ...conflictHeaderCellBaseSx, backgroundColor: '#fff3e0' }} />
                        <TableCell sx={{ ...conflictHeaderCellBaseSx, backgroundColor: '#fff3e0' }} />
                        <TableCell sx={{ ...conflictHeaderCellBaseSx, backgroundColor: '#fff3e0' }} />
                        <TableCell sx={{ ...conflictHeaderCellBaseSx, backgroundColor: '#fff3e0' }} />
                      </>
                    )}
                    {monthDates.map((entry, dayIndex) => {
                      const { day, dayAcronym, date } = entry;
                      // Fix timezone issue: use local date formatting instead of UTC
                      const year = date.getFullYear();
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const dayNum = String(date.getDate()).padStart(2, '0');
                      const cellDate = `${year}-${month}-${dayNum}`;
                      
                      // Check for break conflicts in both departments
                      const ekspozicijaConflict = detectBreakConflicts(cellDate, 'Ekspozicija');
                      const kasosConflict = detectBreakConflicts(cellDate, 'Kasos');
                      const hasAnyConflict = ekspozicijaConflict.hasConflict || kasosConflict.hasConflict;
                      const isStateHolidayCol = stateHolidayDateSet.has(cellDate);
                      const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                      const nextDate = monthDates[dayIndex + 1]?.date;
                      const isFridayBeforeSaturday = nextDate != null && nextDate.getDay() === 6;
                      const weekendBorder = '1px solid #5c6bc0';
                      const defaultBorder = '1px solid #ddd';
                      
                      return (
                        <TableCell 
                          key={day} 
                          align="center" 
                          sx={{ 
                            minWidth: 50,
                            ...conflictHeaderCellBaseSx,
                            backgroundColor: hasAnyConflict ? '#fff3e0' : 'background.paper',
                            ...(isStateHolidayCol
                              ? { border: '3px solid #e91e63', boxShadow: '0 0 10px 2px rgba(233, 30, 99, 0.45)' }
                              : {
                                  borderLeft: isWeekend ? weekendBorder : defaultBorder,
                                  borderRight: (isWeekend || isFridayBeforeSaturday) ? weekendBorder : defaultBorder,
                                  borderTop: isWeekend ? weekendBorder : defaultBorder,
                                  borderBottom: isWeekend ? weekendBorder : defaultBorder
                                }),
                            py: 0.5
                          }}
                        >
                          {hasAnyConflict && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                              {ekspozicijaConflict.hasConflict && (
                                <Tooltip title="Konfliktas Ekspozicija" arrow>
                                  <Typography variant="caption" sx={{
                                    fontSize: '0.75rem',
                                    color: '#e65100',
                                    fontWeight: 'bold',
                                    backgroundColor: '#ffcc02',
                                    px: 0.5,
                                    py: 0.25,
                                    borderRadius: 1,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    maxWidth: '100%',
                                    display: 'block'
                                  }}>
                                    Eksp.
                                  </Typography>
                                </Tooltip>
                              )}
                              {kasosConflict.hasConflict && (
                                <Typography variant="caption" sx={{
                                  fontSize: '0.9rem',
                                  color: '#e65100',
                                  fontWeight: 'bold',
                                  backgroundColor: '#ffcc02',
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: 1
                                }}>
                                  Konfliktas Kasos
                                </Typography>
                              )}
                            </Box>
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedDepartments.length === 0 ? (
                    <TableRow key="no-departments">
                      <TableCell colSpan={monthDates.length + 1 + fixedDetailsColumnCount} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body1" color="text.secondary">
                          {t('schedule.noDepartmentsSelected', 'Please select at least one department to view schedules')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : filteredCompanyView.length === 0 ? (
                    <TableRow key="no-employees">
                      <TableCell colSpan={monthDates.length + 1 + fixedDetailsColumnCount} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body1" color="text.secondary">
                          {t('schedule.noDataForSelectedDepartments', 'No schedule data found for the selected departments')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredCompanyView.map((employee: CompanyEmployee, empIndex: number) => {
                      const isCurrentUser = user?.id === employee.userId;
                      const isPinned = employee.isPinned || pinnedUsers.has(employee.userId);
                      const selectedYear = selectedMonth.getFullYear();
                      const selectedMonthIndex = selectedMonth.getMonth();
                      const hoursSummary = getEmployeeHoursSummary(
                        employee,
                        selectedYear,
                        selectedMonthIndex,
                        Number(requiredHours),
                        filteredCompanyView
                      );
                      const praktikaHours = getEmployeePraktikaHoursBreakdown(
                        employee,
                        selectedYear,
                        selectedMonthIndex
                      );
                      const employeeNameElement = (
                        <Typography
                          variant="body2"
                          sx={{
                            cursor: canApproveSchedules() && hasPendingRequests(employee.userId) ? 'pointer' : 'default',
                            fontWeight: hasPendingRequests(employee.userId) ? 'bold' : 'normal',
                            color: hasPendingRequests(employee.userId) ? '#ff5722' : 'inherit',
                            textDecoration: hasPendingRequests(employee.userId) ? 'underline' : 'none',
                            '&:hover': canApproveSchedules() && hasPendingRequests(employee.userId) ? {
                              backgroundColor: 'rgba(255, 87, 34, 0.1)',
                              borderRadius: '4px',
                              padding: '2px 4px',
                              margin: '-2px -4px'
                            } : {}
                          }}
                          onClick={() => handleUserNameClick(employee.userId, employee.name)}
                        >
                          {employee.name}
                        </Typography>
                      );
                      return (
                    <TableRow 
                      key={empIndex}
                      sx={{
                        backgroundColor: isCurrentUser ? '#e8f5e8' : (isPinned ? '#fff9e6' : 'inherit'),
                        borderTop: isCurrentUser ? '2px solid #4caf50' : (isPinned ? '2px solid #ffc107' : 'inherit'),
                        borderBottom: isCurrentUser ? '2px solid #4caf50' : (isPinned ? '2px solid #ffc107' : 'inherit'),
                        '&:hover': {
                          backgroundColor: isCurrentUser ? '#d4edda' : (isPinned ? '#fff8e1' : 'action.hover')
                        }
                      }}
                    >
                      <TableCell sx={{ 
                        fontWeight: 'bold',
                        position: 'sticky',
                        left: 0,
                        // Above day cells (Ex/Ed overlays) when scrolling horizontally
                        zIndex: 6,
                        backgroundColor: isCurrentUser ? '#e8f5e8' : 'background.paper',
                        borderRight: '2px solid #ddd',
                        color: isCurrentUser ? '#2e7d32' : 'inherit'
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {hasPermission('users.edit') && (
                            <>
                              <Tooltip title={(employee.isPinned || pinnedUsers.has(employee.userId)) ? 'Atkabinti' : 'Užkabinti'}>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const isPinned = employee.isPinned || pinnedUsers.has(employee.userId);
                                    if (isPinned) {
                                      handleUnpinUser(employee.userId);
                                    } else {
                                      handlePinUser(employee.userId);
                                    }
                                  }}
                                  sx={{
                                    color: (employee.isPinned || pinnedUsers.has(employee.userId)) ? '#1976d2' : 'action.disabled',
                                    '&:hover': {
                                      color: '#1976d2',
                                      backgroundColor: 'rgba(25, 118, 210, 0.1)'
                                    }
                                  }}
                                >
                                  {(employee.isPinned || pinnedUsers.has(employee.userId)) ? (
                                    <PushPinIcon fontSize="small" />
                                  ) : (
                                    <PushPinOutlinedIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={isEmployeeExcludedFromWorkersCount(employee) ? 'Įtraukti į darbuotojų skaičių' : 'Neįtraukti į darbuotojų skaičių'}>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (isEmployeeExcludedFromWorkersCount(employee)) {
                                      handleIncludeInWorkersCount(employee.userId);
                                    } else {
                                      handleExcludeFromWorkersCount(employee.userId);
                                    }
                                  }}
                                  sx={{
                                    color: isEmployeeExcludedFromWorkersCount(employee) ? '#d32f2f' : 'action.disabled',
                                    '&:hover': {
                                      color: '#d32f2f',
                                      backgroundColor: 'rgba(211, 47, 47, 0.1)'
                                    }
                                  }}
                                >
                                  {isEmployeeExcludedFromWorkersCount(employee) ? (
                                    <PersonAddIcon fontSize="small" />
                                  ) : (
                                    <PersonRemoveIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                              {hasPermission('schedule.manage') && (
                                <Tooltip
                                  title={t(
                                    'schedule.remoteWeeklyLimitEdit',
                                    'Nuotolio dienos / savaitė (dabar: {{max}})',
                                    { max: remoteWeeklyLimits[employee.userId] || 2 }
                                  )}
                                >
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openRemoteLimitDialog(employee.userId, employee.name);
                                    }}
                                    sx={{
                                      color: remoteWeeklyLimits[employee.userId]
                                        ? '#f57c00'
                                        : 'action.disabled',
                                      '&:hover': {
                                        color: '#f57c00',
                                        backgroundColor: 'rgba(245, 124, 0, 0.1)'
                                      }
                                    }}
                                  >
                                    <HomeIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </>
                          )}
                          {hasPermission('schedule.manage') && remoteWeeklyLimits[employee.userId] && (
                            <Chip
                              size="small"
                              label={`N×${remoteWeeklyLimits[employee.userId]}`}
                              sx={{
                                height: 20,
                                fontSize: '0.7rem',
                                fontWeight: 700,
                                bgcolor: 'rgba(255, 250, 205, 0.95)',
                                border: '1px solid #f0e68c',
                                color: '#8d6e00'
                              }}
                            />
                          )}
                          {(!showDetailsColumns || praktikaHours) ? (
                            <Tooltip
                              title={renderEmployeeHoursTooltip(
                                employee.name,
                                hoursSummary,
                                employee.etatas,
                                praktikaHours
                              )}
                              placement="right"
                              arrow
                              enterDelay={200}
                              slotProps={{
                                tooltip: {
                                  sx: {
                                    bgcolor: 'background.paper',
                                    color: 'text.primary',
                                    boxShadow: 4,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1.5,
                                    p: 1.5,
                                    maxWidth: 320
                                  }
                                },
                                arrow: {
                                  sx: {
                                    color: 'background.paper',
                                    '&::before': {
                                      border: '1px solid',
                                      borderColor: 'divider',
                                      backgroundColor: 'background.paper',
                                      boxSizing: 'border-box'
                                    }
                                  }
                                },
                                popper: { sx: { zIndex: 1400 } }
                              }}
                            >
                              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                {employeeNameElement}
                              </Box>
                            </Tooltip>
                          ) : (
                            employeeNameElement
                          )}
                          {pendingApprovals.some(req => req.userId === employee.userId) && (
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                backgroundColor: '#ff9800',
                                animation: 'pulse 2s infinite'
                              }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      {showDetailsColumns && (
                        <>
                          <TableCell>
                            {employee.department || '-'}
                          </TableCell>
                          <TableCell>
                            {employee.position || '-'}
                          </TableCell>
                          <TableCell>
                            {employee.etatas ? `${employee.etatas} et.` : '-'}
                          </TableCell>
                          <TableCell 
                            sx={{
                              backgroundColor: '#f5f5f5',
                              fontWeight: 'bold'
                            }}
                          >
                            {hoursSummary.actualHours}
                          </TableCell>
                          <TableCell>
                            <Box sx={{
                              fontWeight: 'bold',
                              color: hoursSummary.balanceColor
                            }}>
                              {hoursSummary.balanceText}
                            </Box>
                          </TableCell>
                        </>
                      )}
                      {monthDates.map((entry, dayIndex) => {
                        const { day, date } = entry;
                        // Fix timezone issue: use local date formatting instead of UTC
                        const dateYear = date.getFullYear();
                        const dateMonth = String(date.getMonth() + 1).padStart(2, '0');
                        const dateDay = String(date.getDate()).padStart(2, '0');
                        const cellDate = `${dateYear}-${dateMonth}-${dateDay}`; // Always use YYYY-MM-DD format
                        const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                        const nextDate = monthDates[dayIndex + 1]?.date;
                        const isFridayBeforeSaturday = nextDate != null && nextDate.getDay() === 6;
                        const isStateHolidayColumn = stateHolidayDateSet.has(cellDate);
                        
                        const schedule = getPrimaryScheduleForDate(
                          employee.schedules,
                          cellDate,
                          year,
                          month,
                          day
                        );
                        const praktikaSchedule = getPraktikaScheduleForDate(
                          employee.schedules,
                          cellDate,
                          year,
                          month,
                          day
                        );
                        const hasDualSegments = Boolean(
                          schedule &&
                            praktikaSchedule &&
                            PAIRABLE_PRIMARY_STATUSES.includes(schedule.status)
                        );
                        // Praktika-only: primary is undefined; use praktika as the sole displayed segment
                        const soleSchedule = schedule ?? praktikaSchedule;
                        
                        // For simplicity, we'll mark cells as potentially locked based on being in the past
                        // In a real implementation, this should check the actual month lock status
                        const cellYear = date.getFullYear();
                        const cellMonth = date.getMonth() + 1;
                        const currentDate = new Date();
                        const currentYear = currentDate.getFullYear();
                        const currentMonth = currentDate.getMonth() + 1;
                        
                        // Consider a month as potentially locked if it's in the past (for visual cues)
                        // Only lock past months for Darbuotojai (employees), not Vadovai (managers)
                        const isPastMonth = cellYear < currentYear || (cellYear === currentYear && cellMonth < currentMonth);
                        // Check if month is locked based on permissions, not role
                        const isMonthLocked = !hasPermission('schedule.manage') && isPastMonth;
                        // Determine if this cell is clickable for the current user
                        const isClickable = canEditOtherUsersCells() || (employee.userId === user?.id && !isMonthLocked);
                        
                        // Check if there's a pending approval for this cell (from any month)
                        const pendingApproval = pendingApprovals.find(req => 
                          req.userId === employee.userId && req.date === cellDate
                        );
                        
                        // Also check if this user has any pending approvals (to show indicator)
                        const hasAnyPendingApproval = pendingApprovals.some(req => 
                          req.userId === employee.userId
                        );
                        
                        // Determine if detailed information should be shown based on department visibility
                        const shouldShowDetailedInfo = canApproveSchedules() || 
                          employee.department === user?.department || 
                          employee.userId === user?.id;
                        

                        
                        // Enhanced debugging for Marmantas specifically
                        if (employee.name === 'Marmantas Marmasevičius') {
                          console.log(`🔍 MARMANTAS DEBUG for ${cellDate}:`, {
                            employeeUserId: employee.userId,
                            pendingApprovals: pendingApprovals.map(req => ({
                              userId: req.userId,
                              date: req.date,
                              status: req.status,
                              isApproved: req.isApproved
                            })),
                            cellDate: cellDate,
                            found: !!pendingApproval,
                            pendingApproval: pendingApproval ? {
                              userId: pendingApproval.userId,
                              date: pendingApproval.date,
                              status: pendingApproval.status
                            } : null
                          });
                        }
                        
                        // Debug the entire pendingApprovals array for Marmantas
                        if (employee.name === 'Marmantas Marmasevičius' && cellDate === '2025-08-01') {
                          console.log('🔍 FULL PENDING APPROVALS DEBUG:', {
                            allPendingApprovals: pendingApprovals,
                            marmantasPending: pendingApprovals.filter(req => req.userId === employee.userId),
                            employeeUserId: employee.userId
                          });
                        }
                        
                        // Enhanced debugging for pending approval matching
                        if (employee.userId === user?.id && pendingApprovals.length > 0) {
                          console.log(`🔍 Enhanced Debug for ${employee.name} on ${cellDate}:`, {
                            employeeUserId: employee.userId,
                            cellDate: cellDate,
                            pendingApprovals: pendingApprovals.map(req => ({
                              reqUserId: req.userId,
                              reqDate: req.date,
                              reqStatus: req.status,
                              userIdMatch: req.userId === employee.userId,
                              dateMatch: req.date === cellDate,
                              fullMatch: req.userId === employee.userId && req.date === cellDate
                            })),
                            foundPendingApproval: !!pendingApproval,
                            pendingApprovalDetails: pendingApproval ? {
                              id: pendingApproval.id,
                              date: pendingApproval.date,
                              status: pendingApproval.status
                            } : null
                          });
                        }
                        
                        // Determine cell styling based on approval status
                        const cellBgProps = hasDualSegments
                          ? {
                              background: `linear-gradient(180deg, ${getStatusColor(schedule!.status)} 0%, ${getStatusColor(schedule!.status)} 48%, ${PRAKTIKA_SOLID_FALLBACK} 52%, ${PRAKTIKA_SOLID_FALLBACK} 100%)`,
                              backgroundColor: 'transparent' as const
                            }
                          : getScheduleCellBgProps(soleSchedule?.status, !!pendingApproval);
                        
                        // Debug logging for pending approvals (after cellBackgroundColor is defined)
                        if (employee.userId === user?.id) {
                          console.log(`🔍 Debug for ${employee.name} on ${cellDate}:`, {
                            pendingApprovalsCount: pendingApprovals.length,
                            pendingApprovals: pendingApprovals.map(req => ({ 
                              userId: req.userId, 
                              date: req.date, 
                              status: req.status,
                              isApproved: req.isApproved 
                            })),
                            employeeUserId: employee.userId,
                            cellDate: cellDate,
                            found: !!pendingApproval,
                            hasAnyPending: hasAnyPendingApproval,
                            cellBgProps
                          });
                        }
                        
                        // Create hover tooltip content
                        const getTooltipContent = () => {
                          let tooltipContent = '';
                          const remoteNotesLine = (notes: string | undefined | null, status: string | undefined | null) => {
                            if (!notes?.trim()) return '';
                            if (status === 'remote' && !canViewRemoteWorkAddressForEmployee(employee.userId)) {
                              return `\n${t('schedule.remoteAddressHidden')}`;
                            }
                            return `\nPastabos: ${notes}`;
                          };
                          
                          // Add excursion information if available (all same-day assignments)
                          if (hasExcursion) {
                            const excursions = excursionsByUserDate.get(cellKey);
                            if (excursions && excursions.length > 0) {
                              excursions.forEach((ex: { title: string; startTime?: string; endTime?: string }, idx: number) => {
                                const n = excursions.length > 1 ? ` (${idx + 1}/${excursions.length})` : '';
                                tooltipContent += `🎫 Ekskursija${n}: ${ex.title}\n`;
                                if (ex.startTime && ex.endTime) {
                                  tooltipContent += `Laikas: ${ex.startTime.slice(0, 5)}-${ex.endTime.slice(0, 5)}\n`;
                                }
                              });
                            }
                          }
                          // Add edukacija information if available (all same-day assignments)
                          if (edukacijosByUserDate.has(cellKey)) {
                            const edukacijos = edukacijosByUserDate.get(cellKey);
                            if (edukacijos && edukacijos.length > 0) {
                              edukacijos.forEach((ed: { title: string; startTime?: string; endTime?: string }, idx: number) => {
                                const n = edukacijos.length > 1 ? ` (${idx + 1}/${edukacijos.length})` : '';
                                tooltipContent += `📚 Edukacija${n}: ${ed.title}\n`;
                                if (ed.startTime && ed.endTime) {
                                  tooltipContent += `Laikas: ${ed.startTime.slice(0, 5)}-${ed.endTime.slice(0, 5)}\n`;
                                }
                              });
                            }
                          }
                          
                          if (pendingApproval) {
                            tooltipContent += `LAUKIA PATVIRTINIMO\n`;
                            
                            // Current schedule
                            if (schedule) {
                              tooltipContent += `Dabartinis: ${getStatusText(schedule.status)} ${schedule.startTime ? schedule.startTime.slice(0, 5) : ''} ${schedule.endTime ? schedule.endTime.slice(0, 5) : ''}`;
                              if ((schedule.status === 'working' || schedule.status === 'remote' || schedule.status === 'praktika') && schedule.breakStartTime && schedule.breakEndTime) {
                                tooltipContent += `\nPertrauka: ${schedule.breakStartTime.slice(0, 5)}-${schedule.breakEndTime.slice(0, 5)}`;
                              }
                              tooltipContent += remoteNotesLine(schedule.notes, schedule.status);
                            } else {
                              tooltipContent += `Dabartinis: Nėra`;
                            }
                            
                            tooltipContent += `\n`;
                            
                            // Requested changes
                            tooltipContent += `Prašomas: ${getStatusText(pendingApproval.status)} ${pendingApproval.startTime ? pendingApproval.startTime.slice(0, 5) : ''} ${pendingApproval.endTime ? pendingApproval.endTime.slice(0, 5) : ''}`;
                            if ((pendingApproval.status === 'working' || pendingApproval.status === 'remote' || pendingApproval.status === 'praktika') && pendingApproval.breakStartTime && pendingApproval.breakEndTime) {
                              tooltipContent += `\nPertrauka: ${pendingApproval.breakStartTime.slice(0, 5)}-${pendingApproval.breakEndTime.slice(0, 5)}`;
                            }
                            tooltipContent += remoteNotesLine(pendingApproval.notes, pendingApproval.status);
                            
                            tooltipContent += `\nSpustelėkite patvirtinti/atmesti`;
                            return tooltipContent;
                          }
                          
                          // Regular tooltip for editable cells
                          if (isClickable) {
                            if (soleSchedule) {
                              let content = `${getStatusText(soleSchedule.status)} - ${soleSchedule.startTime ? soleSchedule.startTime.slice(0, 5) : ''} - ${soleSchedule.endTime ? soleSchedule.endTime.slice(0, 5) : ''}`;
                              if ((soleSchedule.status === 'working' || soleSchedule.status === 'remote' || soleSchedule.status === 'praktika') && soleSchedule.breakStartTime && soleSchedule.breakEndTime) {
                                content += `\nPertrauka: ${soleSchedule.breakStartTime.slice(0, 5)}-${soleSchedule.breakEndTime.slice(0, 5)}`;
                              }
                              content += remoteNotesLine(soleSchedule.notes, soleSchedule.status);
                              if (hasDualSegments && praktikaSchedule) {
                                content += `\n${getStatusText('praktika')}: ${praktikaSchedule.startTime?.slice(0, 5) || ''}–${praktikaSchedule.endTime?.slice(0, 5) || ''}`;
                              }
                              return tooltipContent + content;
                            }
                            return tooltipContent + 'Spustelėkite redaguoti';
                          }
                          
                          // Limited tooltip for other departments
                          if (soleSchedule) {
                            if (shouldShowDetailedInfo) {
                              let content = `${getStatusText(soleSchedule.status)}`;
                              if (soleSchedule.startTime && soleSchedule.endTime) {
                                content += ` - ${soleSchedule.startTime.slice(0, 5)}-${soleSchedule.endTime.slice(0, 5)}`;
                              }
                              if ((soleSchedule.status === 'working' || soleSchedule.status === 'remote' || soleSchedule.status === 'praktika') && soleSchedule.breakStartTime && soleSchedule.breakEndTime) {
                                content += `\nPertrauka: ${soleSchedule.breakStartTime.slice(0, 5)}-${soleSchedule.breakEndTime.slice(0, 5)}`;
                              }
                              content += remoteNotesLine(soleSchedule.notes, soleSchedule.status);
                              if (hasDualSegments && praktikaSchedule) {
                                content += `\n${getStatusText('praktika')}: ${praktikaSchedule.startTime?.slice(0, 5) || ''}–${praktikaSchedule.endTime?.slice(0, 5) || ''}`;
                              }
                              return tooltipContent + content;
                            } else {
                              // Show only status for other departments
                              return tooltipContent + (hasDualSegments && schedule
                                ? `${getStatusText(schedule.status)} + ${getStatusText('praktika')}`
                                : `${getStatusText(soleSchedule.status)}`);
                            }
                          }
                          
                          return tooltipContent || 'Tik administratoriai gali keisti kitų vartotojų grafikus';
                        };
                        
                        // Check if cell should be highlighted
                        const cellKey = `${employee.userId}-${cellDate}`;
                        const hasExcursion = excursionsByUserDate.has(cellKey);
                        const hasEdukacija = edukacijosByUserDate.has(cellKey);
                        const hasManualHighlight = cellHighlights.has(cellKey);
                        const hasBlueMark = cellBlueMarks.has(cellKey);
                        const shouldHighlight = hasExcursion || hasEdukacija || hasManualHighlight;
                        
                        // Determine highlight: excursion=#7fff00, edukacija=#ff00de (light pink), manual=#e34234; both Ex+Ed use diagonal gradient
                        const highlightColor = hasExcursion && hasEdukacija
                          ? 'linear-gradient(135deg, #7fff00 0%, #7fff00 50%, #ff00de 50%, #ff00de 100%)'
                          : hasExcursion ? '#7fff00' : hasEdukacija ? '#ff00de' : '#e34234';
                        const blueMarkColor = '#87CEEB'; // Sky blue
                        
                        // Create highlight style - red/green/pink on top-left half (135deg)
                        // Blue mark on top-right half (315deg or -45deg)
                        const highlightStyle = (shouldHighlight || hasBlueMark) ? {
                          position: 'relative' as const,
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: shouldHighlight && hasBlueMark && typeof highlightColor === 'string' && !highlightColor.startsWith('linear-gradient')
                              ? `linear-gradient(135deg, ${highlightColor} 0%, ${highlightColor} 25%, transparent 25%, transparent 50%, ${blueMarkColor} 50%, ${blueMarkColor} 75%, transparent 75%, transparent 100%)`
                              : shouldHighlight
                              ? (typeof highlightColor === 'string' && highlightColor.startsWith('linear-gradient')
                                ? highlightColor
                                : `linear-gradient(135deg, ${highlightColor} 0%, ${highlightColor} 50%, transparent 50%, transparent 100%)`)
                              : `linear-gradient(315deg, ${blueMarkColor} 0%, ${blueMarkColor} 50%, transparent 50%, transparent 100%)`,
                            pointerEvents: 'none',
                            zIndex: 0  // Behind text content
                          },
                          // Add "Ex" / "Ed" indicators: "Ex" top-left, "Ed" bottom-right when both
                          ...((hasExcursion || hasEdukacija) && {
                            '&::after': {
                              content: hasExcursion ? '"Ex"' : '"Ed"',
                              position: 'absolute',
                              top: 2,
                              left: 2,
                              fontSize: hasExcursion && hasEdukacija ? '12px' : '20px',
                              fontWeight: 'bold',
                              color: '#000',
                              pointerEvents: 'none',
                              zIndex: 0  // Above highlight (::before); keep below sticky name column
                            }
                          }),
                          '& > *': {
                            position: 'relative',
                            zIndex: 1  // Ensure all children (text) are above highlight
                          }
                        } : {};
                        
                        return (
                          <Tooltip
                            key={`tooltip-${employee.userId}-${cellDate}`}
                            title={getTooltipContent()}
                            placement="top"
                            arrow
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  fontSize: '1.1rem', // Make text 1 size bigger
                                  backgroundColor: 'rgba(0, 0, 0, 0.9)',
                                  color: 'white',
                                  padding: '8px 12px',
                                  borderRadius: '6px',
                                  maxWidth: '300px',
                                  whiteSpace: 'pre-line'
                                }
                              }
                            }}
                          >
                          <TableCell 
                            key={`${employee.userId}-${cellDate}`} 
                            align="center"
                              onClick={() => {
                                if (pendingApproval && canApproveSchedules()) {
                                  handleApprovalClick(pendingApproval, soleSchedule);
                                } else if (isClickable) {
                                  handleCellClick(employee, cellDate, soleSchedule);
                                }
                              }}
                            sx={{ 
                              ...cellBgProps,
                              ...(isStateHolidayColumn
                                ? { border: '3px solid #e91e63', boxShadow: '0 0 10px 2px rgba(233, 30, 99, 0.45)' }
                                : (soleSchedule && (soleSchedule.status === 'working' || soleSchedule.status === 'remote') && 
                                  soleSchedule.startTime && soleSchedule.endTime && 
                                  (!soleSchedule.breakStartTime || !soleSchedule.breakEndTime) && !isMonthLocked)
                                ? { border: '2px solid #ff4444' }
                                : (pendingApproval && !isMonthLocked)
                                ? { border: '2px solid #9e9e9e' }
                                : {
                                    borderLeft: isWeekend ? '1px solid #5c6bc0' : '1px solid #ddd',
                                    borderRight: (isWeekend || isFridayBeforeSaturday) ? '1px solid #5c6bc0' : '1px solid #ddd',
                                    borderTop: isWeekend ? '1px solid #5c6bc0' : '1px solid #ddd',
                                    borderBottom: isWeekend ? '1px solid #5c6bc0' : '1px solid #ddd'
                                  }),
                              minWidth: hasDualSegments ? 58 : 50,
                              height: hasDualSegments ? 64 : 40,
                              cursor: isMonthLocked ? 'not-allowed' : ((pendingApproval && canApproveSchedules()) || isClickable ? 'pointer' : 'default'),
                              position: 'relative',
                              ...highlightStyle,
                              // Add striped overlay for locked months while preserving original colors
                              ...(isMonthLocked && {
                                '&::before': {
                                  content: '""',
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  bottom: 0,
                                  background: `repeating-linear-gradient(
                                    45deg,
                                    transparent,
                                    transparent 8px,
                                    rgba(0, 0, 0, 0.15) 8px,
                                    rgba(0, 0, 0, 0.15) 16px
                                  )`,
                                  pointerEvents: 'none',
                                  zIndex: 1
                                }
                              }),
                              ...(soleSchedule && (soleSchedule.status === 'working' || soleSchedule.status === 'remote') && 
                                soleSchedule.startTime && soleSchedule.endTime && 
                                (!soleSchedule.breakStartTime || !soleSchedule.breakEndTime) && !isMonthLocked && {
                                boxShadow: '0 0 8px 2px #ff4444'
                              }),
                              ...(pendingApproval && !isMonthLocked && {
                                boxShadow: '0 0 8px 2px #9e9e9e'
                              }),
                                '&:hover': isMonthLocked ? {} : ((pendingApproval && canApproveSchedules()) || isClickable ? {
                                  ...(pendingApproval
                                    ? { backgroundColor: '#757575' }
                                    : soleSchedule?.status === 'business_trip'
                                      ? { background: BUSINESS_TRIP_GRADIENT, backgroundColor: 'transparent' }
                                      : soleSchedule?.status === 'military_service'
                                        ? { background: MILITARY_SERVICE_GRADIENT, backgroundColor: 'transparent' }
                                      : soleSchedule?.status === 'praktika'
                                        ? { background: PRAKTIKA_GRADIENT, backgroundColor: 'transparent' }
                                      : { backgroundColor: soleSchedule ? getStatusColor(soleSchedule.status) : '#f5f5f5' }),
                                  opacity: 0.8,
                                  transform: 'scale(1.02)',
                                  transition: 'all 0.2s ease-in-out'
                              } : {}),
                                opacity: ((pendingApproval && canApproveSchedules()) || isClickable ? 1 : 0.7),
                                transition: 'all 0.2s ease-in-out'
                              }}
                            >
                              {hasExcursion && hasEdukacija && (
                                <Box sx={{ position: 'absolute', bottom: 2, right: 2, fontSize: '12px', fontWeight: 'bold', color: '#000', pointerEvents: 'none', zIndex: 0 }}>Ed</Box>
                              )}
                              {soleSchedule ? (
                                hasDualSegments && schedule && schedule.startTime && schedule.endTime && praktikaSchedule?.startTime && praktikaSchedule?.endTime ? (
                                  <Box
                                    sx={{
                                      fontSize: '0.8rem',
                                      lineHeight: 1.25,
                                      pointerEvents: 'none',
                                      py: 0.25,
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: 0.25,
                                      justifyContent: 'center'
                                    }}
                                  >
                                    {shouldShowDetailedInfo ? (
                                      <>
                                        <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, fontSize: '0.8rem', lineHeight: 1.25 }}>
                                          {schedule.status === 'remote' ? 'N' : 'D'}{' '}
                                          {schedule.startTime.slice(0, 5)}-{schedule.endTime.slice(0, 5)}
                                        </Typography>
                                        <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, fontSize: '0.8rem', lineHeight: 1.25 }}>
                                          Pr {praktikaSchedule.startTime.slice(0, 5)}-{praktikaSchedule.endTime.slice(0, 5)}
                                        </Typography>
                                      </>
                                    ) : (
                                      <Box sx={{ textAlign: 'center' }}>
                                        <Typography variant="caption" sx={{ fontSize: '0.8rem', fontWeight: 700 }}>
                                          {schedule.status === 'remote' ? 'N' : 'D'}+Pr
                                        </Typography>
                                      </Box>
                                    )}
                                    {pendingApproval && (
                                      <Typography variant="caption" sx={{ display: 'block', color: '#fff', fontSize: '0.6rem', fontWeight: 'bold', backgroundColor: '#666', borderRadius: '2px', padding: '1px 3px', marginTop: '1px' }}>
                                        LAUKIA
                                      </Typography>
                                    )}
                                  </Box>
                                ) : (soleSchedule.status === 'working' || soleSchedule.status === 'remote' || soleSchedule.status === 'sick_leave' || soleSchedule.status === 'day_off' || soleSchedule.status === 'business_trip' || soleSchedule.status === 'military_service' || soleSchedule.status === 'praktika') && soleSchedule.startTime && soleSchedule.endTime ? (
                                  <Box sx={{ fontSize: '0.7rem', lineHeight: 1.2, pointerEvents: 'none' }}>
                                    {shouldShowDetailedInfo ? (
                                      <>
                                        <Typography variant="caption" sx={{ display: 'block', fontWeight: 'bold' }}>
                                          {soleSchedule.startTime.slice(0, 5)}-{soleSchedule.endTime.slice(0, 5)}
                                        </Typography>
                                        <Typography variant="caption" sx={{ display: 'block', color: 'text.primary', fontSize: '1.1rem', fontWeight: 'bold' }}>
                                          {getScheduleHours(soleSchedule).toFixed(2)}h
                                        </Typography>
                                        {(soleSchedule.status === 'working' || soleSchedule.status === 'remote' || soleSchedule.status === 'praktika') && soleSchedule.breakStartTime && soleSchedule.breakEndTime ? (
                                          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.9rem', fontWeight: 'bold' }}>
                                            {soleSchedule.breakStartTime.slice(0, 5)}-{soleSchedule.breakEndTime.slice(0, 5)}
                                          </Typography>
                                        ) : (soleSchedule.status === 'working' || soleSchedule.status === 'remote') ? (
                                          <Typography variant="caption" sx={{ display: 'block', color: '#ff4444', fontSize: '1.2rem', fontWeight: 'bold' }}>
                                            ⚠️
                                          </Typography>
                                        ) : soleSchedule.status === 'praktika' && soleSchedule.breakStartTime && soleSchedule.breakEndTime ? (
                                          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.9rem', fontWeight: 'bold' }}>
                                            {soleSchedule.breakStartTime.slice(0, 5)}-{soleSchedule.breakEndTime.slice(0, 5)}
                                          </Typography>
                                        ) : null}
                                      </>
                                    ) : (
                                      // Limited view for other departments - show only status indicator
                                      <Box sx={{ textAlign: 'center' }}>
                                        <Typography variant="caption" sx={{ fontSize: '0.9rem', fontWeight: 'bold', pointerEvents: 'none' }}>
                                          {soleSchedule.status === 'working' ? 'D' : 
                                           soleSchedule.status === 'remote' ? 'N' : 
                                           soleSchedule.status === 'sick_leave' ? 'L' : 
                                           soleSchedule.status === 'day_off' ? 'A' : 
                                           soleSchedule.status === 'business_trip' ? 'K' :
                                           soleSchedule.status === 'military_service' ? 'KT' :
                                           soleSchedule.status === 'praktika' ? 'Pr' :
                                           soleSchedule.status === 'mothers_day' ? 'M' : 
                                           soleSchedule.status === 'rest_day' ? 'P' : 
                                           soleSchedule.status === 'state_holiday' ? 'Š' : '?'}
                                        </Typography>
                                        {/* Show warning for sick leave or day off without time information */}
                                        {(soleSchedule.status === 'sick_leave' || soleSchedule.status === 'day_off' || soleSchedule.status === 'business_trip' || soleSchedule.status === 'military_service') && (!soleSchedule.startTime || !soleSchedule.endTime) && (
                                          <Typography variant="caption" sx={{ display: 'block', color: '#ff4444', fontSize: '0.6rem', fontWeight: 'bold', marginTop: '2px' }}>
                                            ⚠️
                                          </Typography>
                                        )}
                                        {/* Show warning for mothers day without time information */}
                                        {(soleSchedule.status as any) === 'mothers_day' && (!soleSchedule.startTime || !soleSchedule.endTime) && (
                                          <Typography variant="caption" sx={{ display: 'block', color: '#ff4444', fontSize: '0.6rem', fontWeight: 'bold', marginTop: '2px' }}>
                                            ⚠️
                                          </Typography>
                                        )}
                                        {/* Show warning for working or remote status without break time */}
                                        {(soleSchedule.status === 'working' || soleSchedule.status === 'remote') && soleSchedule.startTime && soleSchedule.endTime && (!soleSchedule.breakStartTime || !soleSchedule.breakEndTime) && (
                                          <Typography variant="caption" sx={{ display: 'block', color: '#ff4444', fontSize: '1.2rem', fontWeight: 'bold', marginTop: '2px' }}>
                                            ⚠️
                                          </Typography>
                                        )}
                                      </Box>
                                    )}
                                    {/* Show pending approval indicator */}
                                    {pendingApproval && (
                                      <Typography variant="caption" sx={{ display: 'block', color: '#fff', fontSize: '0.6rem', fontWeight: 'bold', backgroundColor: '#666', borderRadius: '2px', padding: '1px 3px', marginTop: '2px' }}>
                                        LAUKIA
                                      </Typography>
                                    )}
                                    {/* Show optimistic update indicator */}
                                    {optimisticUpdates.has(`${employee.userId}-${date}`) && (
                                      <Typography variant="caption" sx={{ display: 'block', color: '#fff', fontSize: '0.6rem', fontWeight: 'bold', backgroundColor: '#2196f3', borderRadius: '2px', padding: '1px 3px', marginTop: '2px', animation: 'pulse 1.5s infinite' }}>
                                        ⚡
                                      </Typography>
                                    )}
                                  </Box>
                                ) : (
                                  <Box sx={{ textAlign: 'center' }}>
                                    <Typography variant="caption" sx={{ fontSize: '0.8rem', fontWeight: 'bold', pointerEvents: 'none' }}>
                                      {soleSchedule.status === 'working' ? 'D' : 
                                       soleSchedule.status === 'remote' ? 'N' : 
                                       soleSchedule.status === 'sick_leave' ? 'L' : 
                                       soleSchedule.status === 'day_off' ? 'A' : 
                                       soleSchedule.status === 'business_trip' ? 'K' :
                                       soleSchedule.status === 'military_service' ? 'KT' :
                                       soleSchedule.status === 'praktika' ? 'Pr' :
                                       soleSchedule.status === 'mothers_day' ? 'M' : 
                                       soleSchedule.status === 'rest_day' ? 'P' : 
                                       soleSchedule.status === 'state_holiday' ? 'Š' : 
                                       getStatusText(soleSchedule.status).charAt(0).toUpperCase()}
                                    </Typography>
                                    {/* Show warning for sick leave or day off without time information */}
                                    {(soleSchedule.status === 'sick_leave' || soleSchedule.status === 'day_off' || soleSchedule.status === 'business_trip' || soleSchedule.status === 'military_service') && (!soleSchedule.startTime || !soleSchedule.endTime) && (
                                      <Typography variant="caption" sx={{ display: 'block', color: '#ff4444', fontSize: '0.6rem', fontWeight: 'bold', marginTop: '2px' }}>
                                        ⚠️
                                      </Typography>
                                    )}
                                    {/* Show warning for mothers day without time information */}
                                    {(soleSchedule.status as any) === 'mothers_day' && (!soleSchedule.startTime || !soleSchedule.endTime) && (
                                      <Typography variant="caption" sx={{ display: 'block', color: '#ff4444', fontSize: '0.6rem', fontWeight: 'bold', marginTop: '2px' }}>
                                        ⚠️
                                      </Typography>
                                    )}
                                    {/* Show warning for working or remote status without break time */}
                                    {(soleSchedule.status === 'working' || soleSchedule.status === 'remote') && soleSchedule.startTime && soleSchedule.endTime && (!soleSchedule.breakStartTime || !soleSchedule.breakEndTime) && (
                                      <Typography variant="caption" sx={{ display: 'block', color: '#ff4444', fontSize: '1.2rem', fontWeight: 'bold', marginTop: '2px' }}>
                                        ⚠️
                                      </Typography>
                                    )}
                                    {/* Show pending approval indicator */}
                                    {pendingApproval && (
                                      <Typography variant="caption" sx={{ display: 'block', color: '#fff', fontSize: '0.6rem', fontWeight: 'bold', backgroundColor: '#666', borderRadius: '2px', padding: '1px 3px', marginTop: '2px' }}>
                                        LAUKIA
                                      </Typography>
                                    )}
                                    {/* Show optimistic update indicator */}
                                    {optimisticUpdates.has(`${employee.userId}-${date}`) && (
                                      <Typography variant="caption" sx={{ display: 'block', color: '#fff', fontSize: '0.6rem', fontWeight: 'bold', backgroundColor: '#2196f3', borderRadius: '2px', padding: '1px 3px', marginTop: '2px', animation: 'pulse 1.5s infinite' }}>
                                        ⚡
                                      </Typography>
                                    )}
                                  </Box>
                                )
                              ) : (
                                /* No schedule - show pending approval if exists */
                                pendingApproval ? (
                                  <Box sx={{ fontSize: '0.7rem', lineHeight: '1.2', pointerEvents: 'none', textAlign: 'center', position: 'relative' }}>
                                    <Typography variant="caption" sx={{ display: 'block', fontWeight: 'bold' }}>
                                      {pendingApproval.startTime ? pendingApproval.startTime.slice(0, 5) : ''}-{pendingApproval.endTime ? pendingApproval.endTime.slice(0, 5) : ''}
                                    </Typography>
                                    <Typography variant="caption" sx={{ display: 'block', color: '#fff', fontSize: '0.6rem', fontWeight: 'bold', backgroundColor: '#666', borderRadius: '2px', padding: '1px 3px', marginTop: '2px' }}>
                                      LAUKIA
                                    </Typography>
                                  </Box>
                                ) : ''
                              )}
                            </TableCell>
                          </Tooltip>
                        );
                      })}
                    </TableRow>
                      );
                    })
                  )}
                  
                  {/* Worker Count Row */}
                  <TableRow key="worker-count">
                    <TableCell sx={{ 
                      backgroundColor: '#f5f5f5', 
                      fontWeight: 'bold',
                      position: 'sticky',
                      left: 0,
                      zIndex: 6,
                      borderRight: '2px solid #ddd',
                      py: 0.5,
                      minWidth: 150
                    }}>
                      <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.8rem' }}>
                        {t('schedule.workersCount', 'Darbuotojų skaičius')}
                      </Typography>
                    </TableCell>
                    {/* Placeholder cells to align with toggleable details columns */}
                    {Array.from({ length: fixedDetailsColumnCount }).map((_, idx) => (
                      <TableCell key={`workers-count-placeholder-${idx}`} />
                    ))}
                    {Array.from({ length: daysInMonth }, (_, index) => {
                      const day = index + 1;
                      const date = new Date(year, month, day);
                      // Fix timezone issue: use local date formatting instead of UTC
                      const dateYear = date.getFullYear();
                      const dateMonth = String(date.getMonth() + 1).padStart(2, '0');
                      const dateDay = String(date.getDate()).padStart(2, '0');
                      const cellDate = `${dateYear}-${dateMonth}-${dateDay}`; // YYYY-MM-DD format
                      
                      // Company-wide: show šventė column even when filtered dept all works that day
                      const hasStateHoliday = stateHolidayDateSet.has(cellDate);
                      
                      // Simple counting: count workers with 'working' or 'remote' status (exclude sick_leave)
                      const workersCount = filteredCompanyView.filter(employee =>
                        shouldCountEmployeeForWorkersCount(employee, cellDate, year, month, day)
                      ).length;
                      
                      return (
                        <TableCell 
                          key={day} 
                          align="center" 
                          sx={{ 
                            minWidth: 50,
                            py: 0.25,
                            ...(hasStateHoliday
                              ? { border: '3px solid #e91e63', boxShadow: '0 0 10px 2px rgba(233, 30, 99, 0.45)' }
                              : { border: '1px solid #ddd' })
                          }}
                        >
                          <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                            {workersCount}
                          </Typography>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Color scheme legend - displayed under the schedule (Spalvų legenda) */}
        <Box sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            {t('schedule.colorLegend')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <Chip label={t('schedule.working')} sx={{ backgroundColor: getStatusColor('working'), color: 'black' }} />
            <Chip label={t('schedule.dayOff')} sx={{ backgroundColor: getStatusColor('day_off'), color: 'black' }} />
            <Chip label={t('schedule.mothersDay')} sx={{ backgroundColor: getStatusColor('mothers_day'), color: 'black' }} />
            <Chip label={t('schedule.remote')} sx={{ backgroundColor: getStatusColor('remote'), color: 'black' }} />
            <Chip
              label={t('schedule.businessTrip')}
              sx={{ background: BUSINESS_TRIP_GRADIENT, backgroundColor: 'transparent', color: 'black' }}
            />
            <Chip
              label={t('schedule.militaryService')}
              sx={{ background: MILITARY_SERVICE_GRADIENT, backgroundColor: 'transparent', color: '#f5f5dc' }}
            />
            <Chip
              label={t('schedule.praktika')}
              sx={{ background: PRAKTIKA_GRADIENT, backgroundColor: 'transparent', color: 'black' }}
            />
            <Chip label={t('schedule.sickLeave')} sx={{ backgroundColor: getStatusColor('sick_leave'), color: 'black' }} />
            <Chip label={t('schedule.restDay')} sx={{ backgroundColor: getStatusColor('rest_day'), color: 'black' }} />
            <Chip label={t('schedule.stateHoliday')} sx={{ backgroundColor: getStatusColor('state_holiday'), color: 'black' }} />
          </Box>
          
          {/* Data visibility legend (abbreviations match getStatusAbbreviation) */}
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: 'text.secondary' }}>
            {t('schedule.dataVisibilityLegend', 'Duomenų matomumas:')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', fontSize: '0.8rem' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>D</Typography>
              <Typography variant="caption">- {t('schedule.working')}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>N</Typography>
              <Typography variant="caption">- {t('schedule.remote')}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>L</Typography>
              <Typography variant="caption">- {t('schedule.sickLeave')}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>A</Typography>
              <Typography variant="caption">- {t('schedule.dayOff')}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>K</Typography>
              <Typography variant="caption">- {t('schedule.businessTrip')}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>KT</Typography>
              <Typography variant="caption">- {t('schedule.militaryService')}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>Pr</Typography>
              <Typography variant="caption">- {t('schedule.praktika')}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>M</Typography>
              <Typography variant="caption">- {t('schedule.mothersDay')}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>P</Typography>
              <Typography variant="caption">- {t('schedule.restDay')}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>Š</Typography>
              <Typography variant="caption">- {t('schedule.stateHoliday')}</Typography>
            </Box>
          </Box>
          <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary', fontStyle: 'italic' }}>
            {t('schedule.dataVisibilityNote', 'Pastaba: Darbuotojai mato tik savo departamento detalią informaciją. Kiti departamentai mato tik statuso indikatorius.')}
          </Typography>
        </Box>
          </Box>
        )}
      </Box>
    );
  };

  // Approval system functions
  const fetchPendingApprovals = async () => {
    // Allow admins to see all pending approvals, workers to see their own
    if (!user) return;
    
    try {
      const url = `${getApiUrl(API_ENDPOINTS.WORK_SCHEDULE)}/change-requests`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('📡 fetchPendingApprovals response:', {
          userId: user?.id,
          data: data.data || [],
          count: (data.data || []).length
        });
        const nextPendings = data.data || [];
        pendingApprovalsRef.current = nextPendings;
        setPendingApprovals(nextPendings);
      }
    } catch (error) {
      console.error('Failed to fetch pending approvals:', error);
    }
  };

  // State to store current schedule data for approval dialog
  const [currentScheduleForApproval, setCurrentScheduleForApproval] = useState<any>(null);

  // Function to get current schedule data from existing companyView data
  const getCurrentScheduleFromCompanyView = (userId: string, date: string) => {
    if (!companyView || companyView.length === 0) {
      console.log('DEBUG: No company view data available');
      return null;
    }

    // Find the employee in companyView
    const employee = companyView.find(emp => emp.userId === userId);
    if (!employee) {
      console.log('DEBUG: Employee not found in company view:', userId);
      return null;
    }

    // Find the schedule for the specific date
    const schedule = employee.schedules.find(s => s.date === date);
    if (!schedule) {
      console.log('DEBUG: No schedule found for date:', date);
      return null;
    }

    // Only return approved schedules as "current"
    if (schedule.isApproved) {
      console.log('DEBUG: Found approved current schedule:', schedule);
      return schedule;
    } else {
      console.log('DEBUG: Schedule exists but is not approved (pending change request)');
      return null;
    }
  };

  // Update handleApprovalClick to capture current schedule data
  const handleApprovalClick = async (approval: ScheduleChangeRequest, currentScheduleData?: any) => {
    setSelectedApproval(approval);
    setApprovalDialogOpen(true);
    
    // Store the current schedule data that was visible in the tooltip
    if (currentScheduleData) {
      setCurrentScheduleForApproval(currentScheduleData);
    }
    
    // Ensure we have the latest company view data
    await fetchCompanyView();
  };

  const handleApproveRequest = async () => {
    if (!selectedApproval) return;

    // Store original schedule for rollback
    const originalSchedule = companyView
      .find(emp => emp.userId === selectedApproval.userId)
      ?.schedules.find(s => s.date === selectedApproval.date);

    // ✅ OPTIMISTIC UPDATE: Mark as approved immediately
    updateCompanyViewOptimistically(
      selectedApproval.userId,
      selectedApproval.date,
      {
        ...selectedApproval,
        isApproved: true
      },
      'update'
    );

    try {
      const response = await fetch(`${getApiUrl(API_ENDPOINTS.WORK_SCHEDULE)}/approve-change-request/${selectedApproval.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          reason: approvalReason
        })
      });

      if (response.ok) {
        // ✅ Confirm optimistic update
        confirmOptimisticUpdate(selectedApproval.userId, selectedApproval.date);
        
        setSuccess('Schedule change approved successfully');
        setApprovalDialogOpen(false);
        setSelectedApproval(null);
        setApprovalReason(''); // Clear approval reason
        
        // Refresh company view so admin sees the approved schedule immediately
        await fetchCompanyView();
        fetchPendingApprovals();
      } else {
        // ❌ ROLLBACK: Revert optimistic update on error
        rollbackCompanyViewUpdate(selectedApproval.userId, selectedApproval.date, originalSchedule);
        setError('Failed to approve schedule change');
      }
    } catch (error) {
      // ❌ ROLLBACK: Revert optimistic update on error
      rollbackCompanyViewUpdate(selectedApproval.userId, selectedApproval.date, originalSchedule);
      
      console.error('Error approving request:', error);
      setError('Failed to approve schedule change');
    }
  };

  const handleRejectRequest = async () => {
    if (!selectedApproval) return;

    // Store original schedule for rollback
    const originalSchedule = companyView
      .find(emp => emp.userId === selectedApproval.userId)
      ?.schedules.find(s => s.date === selectedApproval.date);

    // ✅ OPTIMISTIC UPDATE: Remove the pending change request immediately
    updateCompanyViewOptimistically(
      selectedApproval.userId,
      selectedApproval.date,
      {},
      'delete'
    );

    try {
      const response = await fetch(`${getApiUrl(API_ENDPOINTS.WORK_SCHEDULE)}/reject-change-request/${selectedApproval.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          reason: approvalReason
        })
      });

      if (response.ok) {
        // ✅ Confirm optimistic update
        confirmOptimisticUpdate(selectedApproval.userId, selectedApproval.date);
        
        setSuccess('Schedule change rejected');
        setApprovalDialogOpen(false);
        setSelectedApproval(null);
        setApprovalReason(''); // Clear approval reason
        
        // Refresh company view so admin sees the updated schedule immediately
        await fetchCompanyView();
        fetchPendingApprovals();
      } else {
        // ❌ ROLLBACK: Revert optimistic update on error
        rollbackCompanyViewUpdate(selectedApproval.userId, selectedApproval.date, originalSchedule);
        setError('Failed to reject schedule change');
      }
    } catch (error) {
      // ❌ ROLLBACK: Revert optimistic update on error
      rollbackCompanyViewUpdate(selectedApproval.userId, selectedApproval.date, originalSchedule);
      
      console.error('Error rejecting request:', error);
      setError('Failed to reject schedule change');
    }
  };

  const handleSetStateHoliday = async () => {
    if (!stateHolidayDate) return;
    
    try {
      // Fix timezone issue: use local date formatting instead of UTC
      const year = stateHolidayDate.getFullYear();
      const month = String(stateHolidayDate.getMonth() + 1).padStart(2, '0');
      const day = String(stateHolidayDate.getDate()).padStart(2, '0');
      const dateString = `${year}-${month}-${day}`;

      const cellHasScheduleData = (employee: CompanyEmployee) =>
        employee.schedules.some((s) => s.date === dateString) ||
        pendingApprovals.some((pa) => pa.userId === employee.userId && pa.date === dateString);

      const employeesToUpdate = filteredCompanyView.filter((employee) => !cellHasScheduleData(employee));
      let skippedCount = filteredCompanyView.length - employeesToUpdate.length;
      let appliedCount = 0;

      const results = await Promise.all(
        employeesToUpdate.map(async (employee) => {
          const response = await fetch(`${getApiUrl(API_ENDPOINTS.WORK_SCHEDULE)}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({
              userId: employee.userId,
              date: dateString,
              status: 'state_holiday',
              startTime: null,
              endTime: null,
              breakStartTime: null,
              breakEndTime: null,
              notes: 'Valstybinė šventė'
            })
          });

          if (!response.ok) {
            throw new Error(`Failed to set state holiday for ${employee.name}`);
          }

          const result = await response.json();
          return result;
        })
      );

      for (const result of results) {
        if (result.skipped) {
          skippedCount += 1;
        } else {
          appliedCount += 1;
        }
      }

      await fetchCompanyView();

      alert(
        t('schedule.stateHolidayBulkResult', {
          date: dateString,
          applied: appliedCount,
          skipped: skippedCount
        })
      );
    } catch (error) {
      console.error('Failed to set state holiday:', error);
      alert(t('schedule.stateHolidayBulkError'));
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={lt}>
      <Box
        sx={{
          px: { xs: 1.5, sm: 2, md: 3 },
          py: { xs: 2, md: 3 },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
          <Typography
            variant="h4"
            component="h1"
            sx={isJustina ? { animation: 'rainbow 3s linear infinite' } : undefined}
          >
            {t('schedule.title')}
          </Typography>
            {pendingApprovals.length > 0 && (
              <Typography variant="h6" color="text.secondary" sx={{ mt: 1, fontSize: '1.5rem' }}>
                {t('schedule.pendingApprovalMessage', { count: pendingApprovals.length })}
              </Typography>
            )}
          </Box>
        </Box>

        {error && !dialogOpen && !cellEditDialogOpen && !requestDialogOpen && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

        <Card>
          <CardContent>
            {initialView === 'mobile' ? (
              <MobileSchedule
                days={currentUserDays}
                monthTitle={mobileMonthTitle}
                monthPlannedHours={currentUserMonthPlannedHours}
                monthHoursBalance={currentUserMonthHoursBalance}
                focusDate={mobileFocusDate}
                onPrevWeek={handleMobilePrevWeek}
                onNextWeek={handleMobileNextWeek}
                onToday={handleMobileToday}
              />
            ) : (
              <>
                <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
                  <Tab 
                    label={t('schedule.departmentView')} 
                    icon={<PersonIcon />} 
                    iconPosition="start"
                  />
                  {canSeeMonthLocksTab && (
                    <Tab 
                      label={t('common.monthLocks')} 
                      icon={<LockIcon />} 
                      iconPosition="start"
                    />
                  )}
                  {canSeeAuditLogsTab && (
                    <Tab
                      label={t('schedule.logs.tab', 'Pakeitimų žurnalas')}
                      icon={<ScheduleIcon />}
                      iconPosition="start"
                    />
                  )}
                </Tabs>

                {activeTab === 0 && (
                  <>
                    {/* Debug: Show pending approvals state */}
                    {console.log('🔍 Overall pending approvals state:', {
                      count: pendingApprovals.length,
                      data: pendingApprovals.map(req => ({ 
                        userId: req.userId, 
                        date: req.date, 
                        status: req.status,
                        isApproved: req.isApproved 
                      }))
                    })}
                    
                    
                    {renderCompanyView()}
                  </>
                )}

                {monthLocksTabIndex !== null && activeTab === monthLocksTabIndex && hasPermission('schedule.manage') && (
                  <MonthLockManager />
                )}

                {auditLogsTabIndex !== null && activeTab === auditLogsTabIndex && canSeeAuditLogsTab && (
                  renderAuditLogsView()
                )}
              </>
            )}
          </CardContent>
        </Card>

        <Dialog open={auditDetailsOpen} onClose={() => setAuditDetailsOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>{t('schedule.logs.detailsTitle', 'Audito detalės')}</DialogTitle>
          <DialogContent>
            {selectedAuditLog ? (
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2, mt: 1 }}>
                <Typography variant="body2">
                  <strong>{t('schedule.logs.columns.action', 'Veiksmas')}:</strong> {getAuditActionLabel(selectedAuditLog.actionType)}
                </Typography>
                <Typography variant="body2">
                  <strong>{t('schedule.logs.columns.actor', 'Pakeitė')}:</strong>{' '}
                  {formatAuditActorName(selectedAuditLog)}
                </Typography>
                <Typography variant="body2">
                  <strong>{t('schedule.logs.columns.employee', 'Darbuotojas')}:</strong>{' '}
                  {selectedAuditLog.targetUser ? `${selectedAuditLog.targetUser.firstName} ${selectedAuditLog.targetUser.lastName}` : '-'}
                </Typography>
                <TextField
                  label={t('schedule.logs.beforeState', 'Būsena prieš')}
                  value={JSON.stringify(selectedAuditLog.beforeState || {}, null, 2)}
                  multiline
                  minRows={6}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  label={t('schedule.logs.afterState', 'Būsena po')}
                  value={JSON.stringify(selectedAuditLog.afterState || {}, null, 2)}
                  multiline
                  minRows={6}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  label={t('schedule.logs.meta', 'Meta')}
                  value={JSON.stringify(selectedAuditLog.meta || {}, null, 2)}
                  multiline
                  minRows={4}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              </Box>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAuditDetailsOpen(false)}>{t('common.close', 'Uždaryti')}</Button>
          </DialogActions>
        </Dialog>

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editingSchedule ? t('schedule.editEntry') : t('schedule.addEntry')}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2, mt: 1 }}>
              {error && (
                <Alert severity="error" onClose={() => setError('')} sx={{ gridColumn: '1 / -1' }}>
                  {error}
                </Alert>
              )}
              <Box>
                <DatePicker
                  label={t('schedule.date')}
                  value={selectedDate}
                  onChange={(newValue: Date | null) => setSelectedDate(newValue)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Box>
              <Box>
                <FormControl fullWidth>
                  <InputLabel>{t('schedule.status')}</InputLabel>
                  <Select
                    value={selectedStatus}
                    onChange={(e) => {
                      const newStatus = e.target.value as WorkScheduleEntry['status'];
                      if (isHoursOnlyStatus(newStatus) && startTime && endTime) {
                        setAddDialogHoursStr(
                          formatHoursOnlyAutofill(
                            startTime.toTimeString().slice(0, 5),
                            endTime.toTimeString().slice(0, 5),
                            dateToHHMM(breakStartTime),
                            dateToHHMM(breakEndTime)
                          )
                        );
                      }
                      setSelectedStatus(newStatus);
                      clearBreakTimesForStatus(newStatus);
                    }}
                    label={t('schedule.status')}
                  >
                    {statusOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {option.icon}
                          {option.label}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              {isHoursOnlyStatus(selectedStatus) ? (
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>
                  <TextField
                    label={
                      selectedStatus === 'business_trip' ? t('schedule.businessTripHours') :
                      selectedStatus === 'military_service' ? t('schedule.militaryServiceHours') :
                      selectedStatus === 'day_off' ? "Atostogų valandos" :
                      selectedStatus === 'sick_leave' ? "Ligos valandos" :
                      "Mamadienio valandos"
                    }
                    type="text"
                    inputMode="decimal"
                    value={addDialogHoursStr}
                    onChange={(e) => setAddDialogHoursStr(e.target.value)}
                    inputProps={{ placeholder: 'pvz. 7,5' }}
                    fullWidth
                  />
                </Box>
              ) : selectedStatus !== 'state_holiday' && selectedStatus !== 'rest_day' && (
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <TimePicker
                  label={t('schedule.startTime')}
                  value={startTime}
                  onChange={(newValue: Date | null) => setStartTime(newValue)}
                  format="HH:mm"
                  slotProps={{ 
                    textField: { 
                      fullWidth: true,
                      inputProps: { placeholder: '00:00' } 
                    } 
                  }}
                />
                <TimePicker
                  label={t('schedule.endTime')}
                  value={endTime}
                  onChange={(newValue: Date | null) => setEndTime(newValue)}
                  format="HH:mm"
                  slotProps={{ 
                    textField: { 
                      fullWidth: true,
                      inputProps: { placeholder: '00:00' } 
                    } 
                  }}
                />
              </Box>
              )}
              {(selectedStatus === 'working' || selectedStatus === 'remote' || selectedStatus === 'praktika') && (
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <TimePicker
                    label={t('schedule.breakStartTime', 'Break Start Time')}
                    value={breakStartTime}
                    onChange={(newValue: Date | null) => setBreakStartTime(newValue)}
                    format="HH:mm"
                    slotProps={{ 
                      textField: { 
                        fullWidth: true,
                        inputProps: { placeholder: '00:00' } 
                      } 
                    }}
                  />
                  <TimePicker
                    label={t('schedule.breakEndTime', 'Break End Time')}
                    value={breakEndTime}
                    onChange={(newValue: Date | null) => setBreakEndTime(newValue)}
                    format="HH:mm"
                    slotProps={{ 
                      textField: { 
                        fullWidth: true,
                        inputProps: { placeholder: '00:00' } 
                      } 
                    }}
                  />
                </Box>
              )}
              <Box>
                {selectedStatus === 'remote' && (
                  <Typography
                    variant="body2"
                    sx={{ color: 'error.main', fontWeight: 600, mb: 1 }}
                  >
                    {t('schedule.remoteAddressRequired', 'Reikia įvesti darbo nuotoliu adresą.')}
                  </Typography>
                )}
                <TextField
                  label={t('schedule.notes')}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  multiline
                  rows={3}
                  fullWidth
                  required={selectedStatus === 'remote'}
                  error={selectedStatus === 'remote' && !notes.trim()}
                />
                {selectedStatus === 'remote' && (
                  <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setNotes(remoteAddressTemplate)}
                      disabled={!remoteAddressTemplate.trim() || remoteTemplateLoading}
                    >
                      {t('schedule.useMyRemoteAddress', 'Naudoti mano adresą')}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={async () => {
                        const ok = await saveRemoteAddressTemplate(notes);
                        if (ok) {
                          setSuccess(t('schedule.remoteTemplateSaved', 'Mano nuotolinio darbo adresas išsaugotas.'));
                        } else {
                          setError(t('schedule.remoteTemplateSaveFailed', 'Nepavyko išsaugoti nuotolinio darbo adreso.'));
                        }
                      }}
                      disabled={!notes.trim() || remoteTemplateSaving}
                    >
                      {t('schedule.saveAsMyRemoteAddress', 'Išsaugoti kaip mano adresą')}
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSubmit} variant="contained">
              {t('common.save')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Weekly Template Dialog */}
        <Dialog open={templateDialogOpen} onClose={() => setTemplateDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            {t('schedule.weeklyTemplate')}
          </DialogTitle>
          <DialogContent>
            {renderWeeklyTemplate()}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setTemplateDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={applyWeeklyTemplate} variant="contained" startIcon={<ApplyIcon />}>
              {t('schedule.applyTemplate')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Department Auto-fill Dialog */}
        <Dialog open={deptAutofillDialogOpen} onClose={() => !deptAutofillLoading && setDeptAutofillDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{t('schedule.departmentAutofillTitle')}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('schedule.departmentAutofillHelp')}
            </Typography>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {selectedMonth.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
            </Typography>
            {deptTemplatesLoading ? (
              <Typography>{t('common.loading', 'Kraunama...')}</Typography>
            ) : deptTemplates.filter((tpl) => tpl.enabled).length === 0 ? (
              <Alert severity="warning">{t('schedule.departmentAutofillNoTemplates')}</Alert>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {t('schedule.departmentAutofillSelect')}
                </Typography>
                {deptTemplates.filter((tpl) => tpl.enabled).map((tpl) => (
                  <FormControlLabel
                    key={tpl.department}
                    control={
                      <Checkbox
                        checked={deptAutofillSelected.includes(tpl.department)}
                        onChange={(e) => {
                          setDeptAutofillSelected((prev) =>
                            e.target.checked
                              ? [...prev, tpl.department]
                              : prev.filter((d) => d !== tpl.department)
                          );
                        }}
                      />
                    }
                    label={tpl.department}
                  />
                ))}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeptAutofillDialogOpen(false)} disabled={deptAutofillLoading}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="contained"
              startIcon={<ApplyIcon />}
              onClick={runDepartmentAutofill}
              disabled={deptAutofillLoading || deptAutofillSelected.length === 0 || isCurrentMonthLocked}
            >
              {deptAutofillLoading ? t('schedule.departmentAutofillRunning') : t('schedule.departmentAutofillRun')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Manage Department Templates Dialog */}
        <Dialog
          open={deptTemplatesDialogOpen}
          onClose={() => !deptTemplatesSaving && setDeptTemplatesDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>{t('schedule.departmentTemplates')}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('schedule.departmentTemplatesHelp')}
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                size="small"
                label={t('schedule.departmentTemplatesDepartmentName')}
                value={newDeptTemplateName}
                onChange={(e) => setNewDeptTemplateName(e.target.value)}
                sx={{ minWidth: 200 }}
              />
              {availableDepartments.length > 0 && (
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>{t('schedule.departmentTemplatesSelectExisting')}</InputLabel>
                  <Select
                    label={t('schedule.departmentTemplatesSelectExisting')}
                    value=""
                    onChange={(e) => setNewDeptTemplateName(String(e.target.value))}
                  >
                    {availableDepartments.map((dept) => (
                      <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <Button variant="outlined" startIcon={<AddIcon />} onClick={addDepartmentTemplate} disabled={!newDeptTemplateName.trim()}>
                {t('schedule.departmentTemplatesAdd')}
              </Button>
            </Box>

            {deptTemplates.length === 0 ? (
              <Alert severity="info">{t('schedule.departmentAutofillNoTemplates')}</Alert>
            ) : (
              <>
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel>{t('schedule.department')}</InputLabel>
                  <Select
                    label={t('schedule.department')}
                    value={editingDeptTemplateIndex >= 0 ? editingDeptTemplateIndex : ''}
                    onChange={(e) => setEditingDeptTemplateIndex(Number(e.target.value))}
                  >
                    {deptTemplates.map((tpl, idx) => (
                      <MenuItem key={`${tpl.department}-${idx}`} value={idx}>
                        {tpl.department}{tpl.enabled ? '' : ` (${t('common.disabled', 'išjungta')})`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {editingDeptTemplateIndex >= 0 && deptTemplates[editingDeptTemplateIndex] && (
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={deptTemplates[editingDeptTemplateIndex].enabled}
                            onChange={(e) => {
                              const enabled = e.target.checked;
                              setDeptTemplates((prev) =>
                                prev.map((tpl, idx) =>
                                  idx === editingDeptTemplateIndex ? { ...tpl, enabled } : tpl
                                )
                              );
                            }}
                          />
                        }
                        label={t('schedule.departmentTemplatesEnabled')}
                      />
                      <Button
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => removeDepartmentTemplate(editingDeptTemplateIndex)}
                      >
                        {t('schedule.departmentTemplatesRemove')}
                      </Button>
                    </Box>

                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      {t('schedule.departmentTemplatesEmptyWeek')}
                    </Typography>
                    {deptTemplates[editingDeptTemplateIndex].week
                      .slice()
                      .sort((a, b) => {
                        const order = (d: number) => (d === 0 ? 7 : d);
                        return order(a.dayOfWeek) - order(b.dayOfWeek);
                      })
                      .map((day) => (
                        <Box key={day.dayOfWeek} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                              {dayNames[day.dayOfWeek]}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button variant="outlined" size="small" onClick={() => handleCopyDeptTemplateDay(day)}>
                                {t('common.copy', 'Kopijuoti')}
                              </Button>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handlePasteDeptTemplateDay(editingDeptTemplateIndex, day.dayOfWeek)}
                              >
                                {t('common.paste', 'Įklijuoti')}
                              </Button>
                            </Box>
                          </Box>
                          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
                            <FormControl fullWidth size="small">
                              <InputLabel>{t('schedule.status')}</InputLabel>
                              <Select
                                label={t('schedule.status')}
                                value={day.status}
                                onChange={(e) =>
                                  updateDeptTemplateWeekDay(
                                    editingDeptTemplateIndex,
                                    day.dayOfWeek,
                                    'status',
                                    e.target.value
                                  )
                                }
                              >
                                {weeklyTemplateStatusOptions.map((option) => (
                                  <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            {(day.status === 'working' || day.status === 'remote' || day.status === 'sick_leave' || day.status === 'business_trip' || day.status === 'military_service' || day.status === 'praktika') && (
                              <>
                                <TextField
                                  size="small"
                                  type="time"
                                  label={t('schedule.startTime')}
                                  value={day.startTime || ''}
                                  onChange={(e) =>
                                    updateDeptTemplateWeekDay(
                                      editingDeptTemplateIndex,
                                      day.dayOfWeek,
                                      'startTime',
                                      e.target.value || null
                                    )
                                  }
                                  InputLabelProps={{ shrink: true }}
                                />
                                <TextField
                                  size="small"
                                  type="time"
                                  label={t('schedule.endTime')}
                                  value={day.endTime || ''}
                                  onChange={(e) =>
                                    updateDeptTemplateWeekDay(
                                      editingDeptTemplateIndex,
                                      day.dayOfWeek,
                                      'endTime',
                                      e.target.value || null
                                    )
                                  }
                                  InputLabelProps={{ shrink: true }}
                                />
                                {(day.status === 'working' || day.status === 'remote' || day.status === 'praktika') && (
                                  <>
                                    <TextField
                                      size="small"
                                      type="time"
                                      label={t('schedule.breakStartTime')}
                                      value={day.breakStartTime || ''}
                                      onChange={(e) =>
                                        updateDeptTemplateWeekDay(
                                          editingDeptTemplateIndex,
                                          day.dayOfWeek,
                                          'breakStartTime',
                                          e.target.value || null
                                        )
                                      }
                                      InputLabelProps={{ shrink: true }}
                                    />
                                    <TextField
                                      size="small"
                                      type="time"
                                      label={t('schedule.breakEndTime')}
                                      value={day.breakEndTime || ''}
                                      onChange={(e) =>
                                        updateDeptTemplateWeekDay(
                                          editingDeptTemplateIndex,
                                          day.dayOfWeek,
                                          'breakEndTime',
                                          e.target.value || null
                                        )
                                      }
                                      InputLabelProps={{ shrink: true }}
                                    />
                                  </>
                                )}
                              </>
                            )}
                          </Box>
                        </Box>
                      ))}
                  </Box>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeptTemplatesDialogOpen(false)} disabled={deptTemplatesSaving}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={saveDepartmentTemplates}
              disabled={deptTemplatesSaving || deptTemplatesLoading}
            >
              {t('schedule.departmentTemplatesSave')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
          <DialogTitle>{t('common.delete')}</DialogTitle>
          <DialogContent>
            <Typography>
              {t('messages.confirmDelete')}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteConfirmOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={confirmDelete} color="error" variant="contained">
              {t('common.delete')}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={remoteLimitDialogOpen}
          onClose={() => !remoteLimitSaving && setRemoteLimitDialogOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>
            {t('schedule.remoteWeeklyLimitTitle', 'Nuotolio dienos / savaitė')}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t(
                'schedule.remoteWeeklyLimitHelp',
                'Numatytasis limitas — 2 dienos. Išimtis taikoma tik šiam darbuotojui; kiekviena nuotolio diena vis tiek laukia vadovo patvirtinimo.'
              )}
            </Typography>
            {remoteLimitDialogUser && (
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                {remoteLimitDialogUser.name}
              </Typography>
            )}
            <FormControl fullWidth size="small">
              <InputLabel id="remote-weekly-limit-label">
                {t('schedule.remoteWeeklyLimitLabel', 'Maks. dienų per savaitę')}
              </InputLabel>
              <Select
                labelId="remote-weekly-limit-label"
                label={t('schedule.remoteWeeklyLimitLabel', 'Maks. dienų per savaitę')}
                value={remoteLimitDialogValue}
                onChange={(e) => setRemoteLimitDialogValue(Number(e.target.value))}
              >
                {[2, 3, 4, 5].map((n) => (
                  <MenuItem key={n} value={n}>
                    {n === 2
                      ? t('schedule.remoteWeeklyLimitDefaultOption', '{{n}} (numatyta)', { n })
                      : String(n)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRemoteLimitDialogOpen(false)} disabled={remoteLimitSaving}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={saveRemoteWeeklyLimit}
              variant="contained"
              disabled={remoteLimitSaving}
            >
              {t('common.save', 'Išsaugoti')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Cell Edit Dialog */}
        <Dialog open={cellEditDialogOpen} onClose={handleCellEditCancel} maxWidth="sm" fullWidth>
          <DialogTitle>
            {t('schedule.editEntry')} - {editingCellData?.userName} - {editingCellData?.date ? new Date(editingCellData.date).toLocaleDateString('lt-LT') : ''}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2, mt: 1 }}>
              {cellEditDialogNotice && (
                <Alert
                  severity={cellEditDialogNotice.type}
                  onClose={() => setCellEditDialogNotice(null)}
                  sx={{ gridColumn: '1 / -1' }}
                >
                  {cellEditDialogNotice.text}
                </Alert>
              )}
              <Box>
                <FormControl fullWidth>
                  <InputLabel>{t('schedule.status')}</InputLabel>
                  <Select
                    value={cellEditStatus}
                    onChange={(e) => {
                      const newStatus = e.target.value as WorkScheduleEntry['status'];
                      if (isHoursOnlyStatus(newStatus) && cellEditStartTime && cellEditEndTime) {
                        setCellEditHoursStr(
                          formatHoursOnlyAutofill(
                            cellEditStartTime.toTimeString().slice(0, 5),
                            cellEditEndTime.toTimeString().slice(0, 5),
                            dateToHHMM(cellEditBreakStartTime),
                            dateToHHMM(cellEditBreakEndTime)
                          )
                        );
                      }
                      setCellEditStatus(newStatus);
                      clearCellEditBreakTimesForStatus(newStatus);
                      if (!PAIRABLE_PRIMARY_STATUSES.includes(newStatus)) {
                        setCellEditPraktikaEnabled(false);
                        setCellEditPraktikaStartTime(null);
                        setCellEditPraktikaEndTime(null);
                        setCellEditPraktikaBreakStartTime(null);
                        setCellEditPraktikaBreakEndTime(null);
                      }
                    }}
                    label={t('schedule.status')}
                  >
                    {cellEditStatusOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {option.icon}
                          {option.label}
                        </Box>
                      </MenuItem>
                    ))}
                    {cellEditStatus === 'state_holiday' && (
                      <MenuItem value="state_holiday" disabled>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <EventIcon />
                          {t('schedule.stateHoliday')}
                        </Box>
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Box>
              {isHoursOnlyStatus(cellEditStatus) ? (
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>
                  <TextField
                    label={
                      cellEditStatus === 'business_trip' ? t('schedule.businessTripHours') :
                      cellEditStatus === 'military_service' ? t('schedule.militaryServiceHours') :
                      cellEditStatus === 'day_off' ? "Atostogų valandos" :
                      cellEditStatus === 'sick_leave' ? "Ligos valandos" :
                      "Mamadienio valandos"
                    }
                    type="text"
                    inputMode="decimal"
                    value={cellEditHoursStr}
                    onChange={(e) => setCellEditHoursStr(e.target.value)}
                    inputProps={{ placeholder: 'pvz. 7,5' }}
                    fullWidth
                  />
                </Box>
              ) : cellEditStatus !== 'rest_day' && (
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <TimePicker
                    label={t('schedule.startTime')}
                    value={cellEditStartTime}
                    onChange={(newValue: Date | null) => setCellEditStartTime(newValue)}
                    format="HH:mm"
                    slotProps={{ 
                      textField: { 
                        fullWidth: true,
                        inputProps: { placeholder: '00:00' }
                      } 
                    }}
                  />
                  <TimePicker
                    label={t('schedule.endTime')}
                    value={cellEditEndTime}
                    onChange={(newValue: Date | null) => setCellEditEndTime(newValue)}
                    format="HH:mm"
                    slotProps={{ 
                      textField: { 
                        fullWidth: true,
                        inputProps: { placeholder: '00:00' }
                      } 
                    }}
                  />
                </Box>
              )}
              {(cellEditStatus === 'working' || cellEditStatus === 'remote' || cellEditStatus === 'praktika') && (
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <TimePicker
                    label={t('schedule.breakStartTime', 'Break Start Time')}
                    value={cellEditBreakStartTime}
                    onChange={(newValue: Date | null) => setCellEditBreakStartTime(newValue)}
                    format="HH:mm"
                    slotProps={{ 
                      textField: { 
                        fullWidth: true,
                        inputProps: { placeholder: '00:00' }
                      } 
                    }}
                  />
                  <TimePicker
                    label={t('schedule.breakEndTime', 'Break End Time')}
                    value={cellEditBreakEndTime}
                    onChange={(newValue: Date | null) => setCellEditBreakEndTime(newValue)}
                    format="HH:mm"
                    slotProps={{ 
                      textField: { 
                        fullWidth: true,
                        inputProps: { placeholder: '00:00' }
                      } 
                    }}
                  />
                </Box>
              )}
              {(cellEditStatus === 'working' || cellEditStatus === 'remote') && (
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    background: 'rgba(147, 112, 219, 0.08)'
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={cellEditPraktikaEnabled}
                        onChange={(e) => {
                          const on = e.target.checked;
                          setCellEditPraktikaEnabled(on);
                          if (on && !cellEditPraktikaStartTime && cellEditEndTime) {
                            setCellEditPraktikaStartTime(cellEditEndTime);
                            const endPlus = new Date(cellEditEndTime.getTime() + 3 * 60 * 60 * 1000);
                            setCellEditPraktikaEndTime(endPlus);
                          }
                          if (!on) {
                            setCellEditPraktikaStartTime(null);
                            setCellEditPraktikaEndTime(null);
                            setCellEditPraktikaBreakStartTime(null);
                            setCellEditPraktikaBreakEndTime(null);
                          }
                        }}
                      />
                    }
                    label={t(
                      'schedule.praktikaSameDay',
                      'Praktika tą pačią dieną (kitos valandos)'
                    )}
                  />
                  {cellEditPraktikaEnabled && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                        <TimePicker
                          label={t('schedule.praktikaStartTime', 'Praktikos pradžia')}
                          value={cellEditPraktikaStartTime}
                          onChange={(newValue: Date | null) => setCellEditPraktikaStartTime(newValue)}
                          format="HH:mm"
                          slotProps={{ textField: { fullWidth: true } }}
                        />
                        <TimePicker
                          label={t('schedule.praktikaEndTime', 'Praktikos pabaiga')}
                          value={cellEditPraktikaEndTime}
                          onChange={(newValue: Date | null) => setCellEditPraktikaEndTime(newValue)}
                          format="HH:mm"
                          slotProps={{ textField: { fullWidth: true } }}
                        />
                      </Box>
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                        <TimePicker
                          label={t('schedule.breakStartTime', 'Break Start Time')}
                          value={cellEditPraktikaBreakStartTime}
                          onChange={(newValue: Date | null) =>
                            setCellEditPraktikaBreakStartTime(newValue)
                          }
                          format="HH:mm"
                          slotProps={{ textField: { fullWidth: true } }}
                        />
                        <TimePicker
                          label={t('schedule.breakEndTime', 'Break End Time')}
                          value={cellEditPraktikaBreakEndTime}
                          onChange={(newValue: Date | null) =>
                            setCellEditPraktikaBreakEndTime(newValue)
                          }
                          format="HH:mm"
                          slotProps={{ textField: { fullWidth: true } }}
                        />
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
              <Box>
                {cellEditStatus === 'remote' && (
                  <Typography
                    variant="body2"
                    sx={{ color: 'error.main', fontWeight: 600, mb: 1 }}
                  >
                    {t('schedule.remoteAddressRequired', 'Reikia įvesti darbo nuotoliu adresą.')}
                  </Typography>
                )}
                <TextField
                  label={t('schedule.notes')}
                  value={cellEditNotes}
                  onChange={(e) => setCellEditNotes(e.target.value)}
                  multiline
                  rows={3}
                  fullWidth
                  required={cellEditStatus === 'remote'}
                  error={cellEditStatus === 'remote' && !cellEditNotes.trim()}
                />
                {cellEditStatus === 'remote' && (
                  <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setCellEditNotes(remoteAddressTemplate)}
                      disabled={!remoteAddressTemplate.trim() || remoteTemplateLoading}
                    >
                      {t('schedule.useMyRemoteAddress', 'Naudoti mano adresą')}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={async () => {
                        const ok = await saveRemoteAddressTemplate(cellEditNotes);
                        if (ok) {
                          setCellEditDialogNotice({ type: 'success', text: t('schedule.remoteTemplateSaved', 'Mano nuotolinio darbo adresas išsaugotas.') });
                        } else {
                          setCellEditDialogNotice({ type: 'error', text: t('schedule.remoteTemplateSaveFailed', 'Nepavyko išsaugoti nuotolinio darbo adreso.') });
                        }
                      }}
                      disabled={!cellEditNotes.trim() || remoteTemplateSaving}
                    >
                      {t('schedule.saveAsMyRemoteAddress', 'Išsaugoti kaip mano adresą')}
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ flexWrap: 'wrap', rowGap: 1, columnGap: 1 }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button onClick={handleCellEditCancel} size="small" sx={{ whiteSpace: 'nowrap' }}>
                {t('common.cancel')}
              </Button>
              <Button 
                onClick={handleCellClear} 
                variant="outlined" 
                color="error"
                size="small"
                sx={{ whiteSpace: 'nowrap' }}
              >
                {t('common.clear')}
              </Button>
            </Box>
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                flexWrap: 'wrap',
                flex: 1,
                minWidth: 0,
                justifyContent: { xs: 'flex-start', md: 'center' }
              }}
            >
              <Tooltip title="Ctrl+C">
                <span>
                  <Button variant="outlined" size="small" onClick={handleCopyCellEdit}>
                    Kopijuoti
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Ctrl+V">
                <span>
                  <Button variant="outlined" size="small" onClick={handlePasteCellEdit}>
                    Įklijuoti
                  </Button>
                </span>
              </Tooltip>
              {hasPermission('schedule.manage') && cellEditStatus === 'remote' && (
                <Button
                  variant="outlined"
                  size="small"
                  color="warning"
                  onClick={handleCancelRemoteWork}
                >
                  {t('schedule.cancelRemoteWork')}
                </Button>
              )}
              {(canApproveSchedules() || hasPermission('schedule.manage')) && editingCellData && (
                <>
                  <Tooltip title={cellEditMonthLocked ? t('schedule.monthLockedNoChanges') : ''}>
                    <span>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleToggleHighlight(editingCellData.userId, editingCellData.date)}
                        startIcon={<HighlightIcon />}
                        disabled={cellEditMonthLocked}
                        sx={{ 
                          backgroundColor: cellHighlights.has(`${editingCellData.userId}-${editingCellData.date}`) ? '#e34234' : 'transparent',
                          color: cellHighlights.has(`${editingCellData.userId}-${editingCellData.date}`) ? 'white' : 'inherit'
                        }}
                      >
                        {cellHighlights.has(`${editingCellData.userId}-${editingCellData.date}`) ? 'Pašalinti žymeklį' : 'Pažymėti'}
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip title={cellEditMonthLocked ? t('schedule.monthLockedNoChanges') : ''}>
                    <span>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleToggleBlueMark(editingCellData.userId, editingCellData.date)}
                        startIcon={<HighlightIcon />}
                        disabled={cellEditMonthLocked}
                        sx={{ 
                          backgroundColor: cellBlueMarks.has(`${editingCellData.userId}-${editingCellData.date}`) ? '#87CEEB' : 'transparent',
                          color: cellBlueMarks.has(`${editingCellData.userId}-${editingCellData.date}`) ? 'white' : '#87CEEB',
                          borderColor: '#87CEEB'
                        }}
                      >
                        {cellBlueMarks.has(`${editingCellData.userId}-${editingCellData.date}`) ? 'Pašalinti mėlyną žymeklį' : 'Pažymėti mėlynai'}
                      </Button>
                    </span>
                  </Tooltip>
                </>
              )}
              {/* Cancel button for user's own pending requests */}
              {editingCellData && pendingApprovals.find(pa => 
                pa.userId === editingCellData.userId && 
                pa.date === editingCellData.date && 
                pa.userId === user?.id &&
                !hasPermission('schedule.manage')
              ) && (
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={() => {
                    const pendingRequest = pendingApprovals.find(pa => 
                      pa.userId === editingCellData.userId && 
                      pa.date === editingCellData.date
                    );
                    if (pendingRequest && window.confirm('Ar tikrai norite atšaukti šį pakeitimo prašymą?')) {
                      void handleCancelOwnRequest(pendingRequest.id);
                      setCellEditDialogOpen(false);
                      setEditingCellData(null);
                      setCellEditDialogNotice(null);
                    }
                  }}
                >
                  Atšaukti prašymą
                </Button>
              )}
            </Box>
            <Button
              onClick={() => { void handleCellEditSubmit(); }}
              variant="contained"
              size="small"
              sx={{ whiteSpace: 'nowrap', ml: { xs: 0, sm: 'auto' } }}
            >
              {t('common.save')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Change Request Dialog */}
        <Dialog open={requestDialogOpen} onClose={handleChangeRequestCancel} maxWidth="sm" fullWidth>
          <DialogTitle>
            {t('schedule.requestChange')} - {requestingForDate ? new Date(requestingForDate).toLocaleDateString('lt-LT') : ''}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2, mt: 1 }}>
              {requestDialogNotice && (
                <Alert
                  severity={requestDialogNotice.type}
                  onClose={() => setRequestDialogNotice(null)}
                  sx={{ gridColumn: '1 / -1' }}
                >
                  {requestDialogNotice.text}
                </Alert>
              )}
              <Box>
                <FormControl fullWidth>
                  <InputLabel>{t('schedule.status')}</InputLabel>
                  <Select
                    value={requestedStatus}
                    onChange={(e) => {
                      const newStatus = e.target.value as any;
                      if (isHoursOnlyStatus(newStatus) && requestedStartTime && requestedEndTime) {
                        setRequestedHoursStr(
                          formatHoursOnlyAutofill(
                            requestedStartTime.toTimeString().slice(0, 5),
                            requestedEndTime.toTimeString().slice(0, 5),
                            dateToHHMM(requestedBreakStartTime),
                            dateToHHMM(requestedBreakEndTime)
                          )
                        );
                      }
                      setRequestedStatus(newStatus);
                      if (isHoursOnlyStatus(newStatus)) {
                        setRequestedBreakStartTime(null);
                        setRequestedBreakEndTime(null);
                      }
                    }}
                    label={t('schedule.status')}
                  >
                    {changeRequestStatusOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {option.icon}
                          {option.label}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              {isHoursOnlyStatus(requestedStatus) ? (
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>
                  <TextField
                    label={
                      requestedStatus === 'business_trip' ? t('schedule.businessTripHours') :
                      requestedStatus === 'military_service' ? t('schedule.militaryServiceHours') :
                      requestedStatus === 'day_off' ? "Atostogų valandos" :
                      requestedStatus === 'sick_leave' ? "Ligos valandos" :
                      "Mamadienio valandos"
                    }
                    type="text"
                    inputMode="decimal"
                    value={requestedHoursStr}
                    onChange={(e) => setRequestedHoursStr(e.target.value)}
                    inputProps={{ placeholder: 'pvz. 7,5' }}
                    fullWidth
                  />
                </Box>
              ) : requestedStatus !== 'rest_day' && (
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <TimePicker
                    label={t('schedule.startTime')}
                    value={requestedStartTime}
                    onChange={(newValue: Date | null) => setRequestedStartTime(newValue)}
                    format="HH:mm"
                    slotProps={{ 
                      textField: { 
                        fullWidth: true,
                        inputProps: { placeholder: '00:00' }
                      } 
                    }}
                  />
                  <TimePicker
                    label={t('schedule.endTime')}
                    value={requestedEndTime}
                    onChange={(newValue: Date | null) => setRequestedEndTime(newValue)}
                    format="HH:mm"
                    slotProps={{ 
                      textField: { 
                        fullWidth: true,
                        inputProps: { placeholder: '00:00' }
                      } 
                    }}
                  />
                </Box>
              )}
              {(requestedStatus === 'working' || requestedStatus === 'remote' || requestedStatus === 'praktika') && (
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <TimePicker
                    label={t('schedule.breakStartTime', 'Break Start Time')}
                    value={requestedBreakStartTime}
                    onChange={(newValue: Date | null) => setRequestedBreakStartTime(newValue)}
                    format="HH:mm"
                    slotProps={{ 
                      textField: { 
                        fullWidth: true,
                        inputProps: { placeholder: '00:00' }
                      } 
                    }}
                  />
                  <TimePicker
                    label={t('schedule.breakEndTime', 'Break End Time')}
                    value={requestedBreakEndTime}
                    onChange={(newValue: Date | null) => setRequestedBreakEndTime(newValue)}
                    format="HH:mm"
                    slotProps={{ 
                      textField: { 
                        fullWidth: true,
                        inputProps: { placeholder: '00:00' }
                      } 
                    }}
                  />
                </Box>
              )}
              <Box>
                <TextField
                  label={t('schedule.notes')}
                  value={requestedNotes}
                  onChange={(e) => setRequestedNotes(e.target.value)}
                  multiline
                  rows={2}
                  fullWidth
                />
              </Box>
              <Box>
                <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={isCurrentMonthLocked ? t('schedule.monthLocked') : t('schedule.monthUnlocked')}
                    color={isCurrentMonthLocked ? 'error' : 'success'}
                    size="small"
                    icon={isCurrentMonthLocked ? <LockIcon /> : <UnlockIcon />}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {isCurrentMonthLocked 
                      ? t('schedule.reasonRequiredCaption')
                      : t('schedule.reasonOptionalCaption')
                    }
                  </Typography>
                </Box>
                <TextField
                  label={t('schedule.requestReason')}
                  value={requestedReason}
                  onChange={(e) => setRequestedReason(e.target.value)}
                  multiline
                  rows={3}
                  fullWidth
                  required={false} // We'll handle validation in the submit function
                  helperText={isCurrentMonthLocked 
                    ? `${t('schedule.requestReasonHelp')} ${t('schedule.reasonRequiredTag')}`
                    : `${t('schedule.requestReasonHelp')} ${t('schedule.reasonOptionalTag')}`
                  }
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            {/* Check if there's a pending request for this date */}
            {(() => {
              const hasPendingRequest = pendingApprovals.find(pa => 
                pa.userId === requestingForUser && 
                pa.date === requestingForDate
              );
              return hasPendingRequest;
            })() && (
              <Button
                variant="outlined"
                color="error"
                size="small"
                sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                onClick={() => {
                  const hasPendingRequest = pendingApprovals.find(pa => 
                    pa.userId === requestingForUser && 
                    pa.date === requestingForDate
                  );
                  if (hasPendingRequest) {
                    handleCancelOwnRequest(hasPendingRequest.id);
                    handleChangeRequestCancel(); // Close dialog
                  }
                }}
              >
                Atšaukti prašymą
              </Button>
            )}
            <Button onClick={handleChangeRequestCancel}>
              {t('common.cancel')}
            </Button>
            <Box sx={{ display: 'flex', gap: 1, ml: 'auto', mr: 2 }}>
              <Tooltip title="Ctrl+C">
                <span>
                  <Button variant="outlined" size="small" onClick={handleCopyRequest}>
                    Kopijuoti
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Ctrl+V">
                <span>
                  <Button variant="outlined" size="small" onClick={handlePasteRequest}>
                    Įklijuoti
                  </Button>
                </span>
              </Tooltip>
            </Box>
            <Button onClick={handleChangeRequestSubmit} variant="contained">
              {t('schedule.submitRequest')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Approval Dialog */}
        <Dialog open={approvalDialogOpen} onClose={() => {
          setApprovalDialogOpen(false);
          setCurrentScheduleForApproval(null); // Clear stored schedule data
          setApprovalReason(''); // Clear approval reason
        }} maxWidth="md" fullWidth>
          <DialogTitle>
            {t('schedule.approveScheduleChange', 'Approve Schedule Change')}
          </DialogTitle>
          <DialogContent>
            {selectedApproval && (
              <Box sx={{ mt: 2 }}>
                {/* Simplified UI: removed raw debug dump */}

                <Typography variant="h6" gutterBottom>
                  {t('schedule.employee')}: {companyView.find(e => e.userId === selectedApproval.userId)?.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('schedule.date')}: {new Date(selectedApproval.date).toLocaleDateString('lt-LT')}
                </Typography>
                
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3, mt: 3 }}>
                                    {/* Current Schedule */}
                  <Box sx={{ p: 2, border: '1px solid #ddd', borderRadius: 1, backgroundColor: '#f5f5f5' }}>
                    <Typography variant="h6" gutterBottom color="primary">
                      {t('schedule.currentSchedule', 'Current Schedule')}
                    </Typography>
                    {(() => {
                      // Use the stored schedule data that was captured from the tooltip
                      if (currentScheduleForApproval) {
                        let currentLine = `Dabartinis: ${getStatusText(currentScheduleForApproval.status)} ${currentScheduleForApproval.startTime ? currentScheduleForApproval.startTime.slice(0, 5) : ''} ${currentScheduleForApproval.endTime ? currentScheduleForApproval.endTime.slice(0, 5) : ''}`;
                        
                        // Add break time only for working/remote (Ligos atostogos, Atostogos have no breaks)
                        if ((currentScheduleForApproval.status === 'working' || currentScheduleForApproval.status === 'remote' || currentScheduleForApproval.status === 'praktika') && currentScheduleForApproval.breakStartTime && currentScheduleForApproval.breakEndTime) {
                          currentLine += `\nPertrauka: ${currentScheduleForApproval.breakStartTime.slice(0, 5)}-${currentScheduleForApproval.breakEndTime.slice(0, 5)}`;
                        }
                        
                        return (
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{currentLine}</Typography>
                        );
                      } else {
                        return (
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>Dabartinis: Nėra</Typography>
                        );
                      }
                    })()}
                  </Box>
                  
                  {/* Requested Changes */}
                  <Box sx={{ p: 2, border: '1px solid #ddd', borderRadius: 1, backgroundColor: '#e3f2fd' }}>
                    <Typography variant="h6" gutterBottom color="secondary">
                      {t('schedule.requestedChanges', 'Requested Changes')}
                    </Typography>
                    {(() => {
                      let requestedLine = `Prašomas: ${getStatusText(selectedApproval.status)} ${selectedApproval.startTime ? selectedApproval.startTime.slice(0, 5) : ''} ${selectedApproval.endTime ? selectedApproval.endTime.slice(0, 5) : ''}`;
                      
                      // Add break time only for working/remote (Ligos atostogos, Atostogos have no breaks)
                      if ((selectedApproval.status === 'working' || selectedApproval.status === 'remote' || selectedApproval.status === 'praktika') && selectedApproval.breakStartTime && selectedApproval.breakEndTime) {
                        requestedLine += `\nPertrauka: ${selectedApproval.breakStartTime.slice(0, 5)}-${selectedApproval.breakEndTime.slice(0, 5)}`;
                      }
                      
                      return (
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{requestedLine}</Typography>
                      );
                    })()}
                  </Box>
                </Box>
                
                {/* Reason (compact, no debug) */}
                {selectedApproval.notes && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      {t('schedule.requestReason', 'Reason for Change')}
                    </Typography>
                    <Typography variant="body2">
                      {selectedApproval.notes.replace('Change Request Reason: ', '')}
                    </Typography>
                  </Box>
                )}

                {/* Admin reason for approval/rejection */}
                <Box sx={{ mt: 3 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label={t('schedule.adminReason', 'Admin Feedback (Optional)')}
                    value={approvalReason}
                    onChange={(e) => setApprovalReason(e.target.value)}
                    placeholder={t('schedule.adminReasonPlaceholder', 'Enter reason for approval/rejection...')}
                    variant="outlined"
                    size="small"
                  />
                </Box>

                {/* End simplified UI */}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setApprovalDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleRejectRequest} color="error" variant="outlined">
              {t('common.reject')}
            </Button>
            <Button onClick={handleApproveRequest} variant="contained" color="success">
              {t('common.approve')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* User Bulk Approval Dialog */}
        <Dialog open={userBulkApprovalDialogOpen} onClose={() => {
          setUserBulkApprovalDialogOpen(false);
          setSelectedUserForBulkApproval(null);
          setSelectedRequestsForBulk(new Set());
        }} maxWidth="md" fullWidth>
          <DialogTitle>
            {t('schedule.bulkApproval.title')} - {selectedUserForBulkApproval?.userName} - {selectedRequestsForBulk.size} iš {selectedUserForBulkApproval?.pendingRequests.length} pasirinkta
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Button 
                  size="small" 
                  variant="outlined" 
                  onClick={selectAllRequests}
                  disabled={selectedRequestsForBulk.size === selectedUserForBulkApproval?.pendingRequests.length}
                >
                  {t('schedule.bulkApproval.selectAll')}
                </Button>
                <Button 
                  size="small" 
                  variant="outlined" 
                  onClick={selectNoRequests}
                  disabled={selectedRequestsForBulk.size === 0}
                >
                  {t('schedule.bulkApproval.selectNone')}
                </Button>
                <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center', ml: 2 }}>
                  {t('schedule.bulkApproval.requestsSelected', { 
                    selected: selectedRequestsForBulk.size, 
                    total: selectedUserForBulkApproval?.pendingRequests.length 
                  })}
                </Typography>
              </Box>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('schedule.bulkApproval.selectRequests')}
              </Typography>
              
              {(selectedUserForBulkApproval ? [...selectedUserForBulkApproval.pendingRequests].sort((a, b) => {
                const aTime = new Date(a.date).getTime();
                const bTime = new Date(b.date).getTime();
                return aTime - bTime;
              }) : []).map((request, index) => (
                <Box key={request.id} sx={{ 
                  p: 2, 
                  mb: 1, 
                  border: '1px solid #ddd', 
                  borderRadius: 1,
                  backgroundColor: isRequestSelected(request.id) ? '#f0f8ff' : '#f9f9f9',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1
                }}>
                  <Box sx={{ mt: 0.5 }}>
                    <input
                      type="checkbox"
                      checked={isRequestSelected(request.id)}
                      onChange={() => toggleRequestSelection(request.id)}
                      style={{ transform: 'scale(1.2)' }}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2">
                      {new Date(request.date).toLocaleDateString('lt-LT')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('schedule.bulkApproval.requested')} {getStatusText(request.status)} 
                      {request.startTime && request.endTime && 
                        ` (${request.startTime} - ${request.endTime})`
                      }
                    </Typography>
                    {request.notes && (
                      <Typography variant="caption" color="text.secondary">
                        {t('schedule.bulkApproval.notes')} {request.notes}
                      </Typography>
                    )}
                    {request.reason && (
                      <Typography variant="caption" color="text.secondary">
                        {t('schedule.bulkApproval.reason')} {request.reason}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setUserBulkApprovalDialogOpen(false);
              setSelectedUserForBulkApproval(null);
              setSelectedRequestsForBulk(new Set());
            }}>
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={async () => {
                // Handle selective rejection
                try {
                  if (!selectedUserForBulkApproval || selectedRequestsForBulk.size === 0) return;
                  
                  const selectedRequests = selectedUserForBulkApproval.pendingRequests.filter(
                    request => selectedRequestsForBulk.has(request.id)
                  );
                  
                  const promises = selectedRequests.map(request =>
                    fetch(`${getApiUrl(API_ENDPOINTS.WORK_SCHEDULE)}/reject-change-request/${request.id}`, {
                      method: 'POST',
                      headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`,
                        'Content-Type': 'application/json'
                      }
                    })
                  );
                  
                  await Promise.all(promises);
                  setUserBulkApprovalDialogOpen(false);
                  setSelectedUserForBulkApproval(null);
                  setSelectedRequestsForBulk(new Set());
                  fetchPendingApprovals();
                  fetchCompanyView();
                } catch (error) {
                  console.error('Error in selective rejection:', error);
                }
              }}
              color="error"
              variant="outlined"
              disabled={selectedRequestsForBulk.size === 0}
            >
              {t('schedule.bulkApproval.rejectSelected', { count: selectedRequestsForBulk.size })}
            </Button>
            <Button 
              onClick={async () => {
                // Handle selective approval
                try {
                  if (!selectedUserForBulkApproval || selectedRequestsForBulk.size === 0) return;
                  
                  console.log('=== Bulk Approval Debug ===');
                  console.log('Selected user:', selectedUserForBulkApproval);
                  console.log('Selected requests:', selectedRequestsForBulk);
                  
                  const selectedRequests = selectedUserForBulkApproval.pendingRequests.filter(
                    request => selectedRequestsForBulk.has(request.id)
                  );
                  
                  console.log('🔍 DEBUG: Selected request IDs:', Array.from(selectedRequestsForBulk));
                  console.log('🔍 DEBUG: All pending requests:', selectedUserForBulkApproval.pendingRequests.map(req => ({ id: req.id, date: req.date })));
                  console.log('🔍 DEBUG: Filtered selected requests:', selectedRequests.map(req => ({ id: req.id, date: req.date })));
                  
                  const promises = selectedRequests.map(async (request, index) => {
                    console.log(`Approving request ${index + 1}/${selectedRequests.length}:`, request);
                    const url = `${getApiUrl(API_ENDPOINTS.WORK_SCHEDULE)}/approve-change-request/${request.id}`;
                    console.log('API URL:', url);
                    
                    const response = await fetch(url, {
                      method: 'POST',
                      headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`,
                        'Content-Type': 'application/json'
                      }
                    });
                    
                    console.log(`Response for request ${request.id}:`, response.status, response.statusText);
                    const responseData = await response.json();
                    console.log('Response data:', responseData);
                    
                    if (!response.ok) {
                      throw new Error(`Failed to approve request ${request.id}: ${responseData.message || response.statusText}`);
                    }
                    
                    return responseData;
                  });
                  
                  console.log('Executing all promises...');
                  const results = await Promise.all(promises);
                  console.log('All approvals completed:', results);
                  
                  setUserBulkApprovalDialogOpen(false);
                  setSelectedUserForBulkApproval(null);
                  setSelectedRequestsForBulk(new Set());
                  
                  console.log('Refreshing data...');
                  fetchPendingApprovals();
                  fetchCompanyView();
                  
                  console.log('Bulk approval completed successfully');
                } catch (error) {
                  console.error('Error in selective approval:', error);
                  const errorMessage = error instanceof Error ? error.message : 'Nežinoma klaida';
                  alert(`Klaida patvirtinant prašymus: ${errorMessage}`);
                }
              }}
              color="success"
              variant="contained"
              disabled={selectedRequestsForBulk.size === 0}
            >
              {t('schedule.bulkApproval.approveSelected', { count: selectedRequestsForBulk.size })}
            </Button>
          </DialogActions>
        </Dialog>

        {typeof document !== 'undefined' &&
          createPortal(
            <div id="schedule-print-root">
              {currentUserEmployee && currentUserMonthDays.length > 0 && (
                <ScheduleMonthPrint
                  days={currentUserMonthDays}
                  employeeName={currentUserEmployee.name}
                  department={currentUserEmployee.department}
                  monthTitle={mobileMonthTitle}
                  plannedHours={currentUserMonthPlannedHours}
                  hoursBalance={currentUserMonthHoursBalance}
                />
              )}
            </div>,
            document.body
          )}
      </Box>
    </LocalizationProvider>
  );
};

export default WorkSchedule; 